/*! @license lucide-react 0.508.0
 * ISC License
 *
 * Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2022 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2022.
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 */
import type { XuwoIconData } from './data';

/*
 * 线宽:Lucide 原稿是 24 画布线宽 2,缩到菜单与按钮的 20px 约 1.67,比自有
 * 图标(20 画布线宽 1.35)粗一圈,两套并排时一眼能看出。这里统一改成 1.6
 * (20px 下约 1.33),与自有图标同粗。再从 lucide 拷新图标进来时照此改。
 * 尺寸不改:Lucide 图形约占画布 75%,自有图标多在 70%–80%,同放 20 的槽里一样大
 * (2026-10-10 试过整体缩到 0.84 / 0.92,菜单里显得又小又淡,撤回)。
 */

/** Vendored Lucide primitives for document icon selection. */
export const lucidePickerIconData = {
  'lucide-a-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 13h6"></path><path d="m2 16 4.5-9 4.5 9"></path><path d="M18 7v9"></path><path d="m14 12 4 4 4-4"></path></g>',
  },
  'lucide-a-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 13h6"></path><path d="m2 16 4.5-9 4.5 9"></path><path d="M18 16V7"></path><path d="m14 11 4-4 4 4"></path></g>',
  },
  'lucide-a-large-small': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 14h-5"></path><path d="M16 16v-3.5a2.5 2.5 0 0 1 5 0V16"></path><path d="M4.5 13h6"></path><path d="m3 16 4.5-9 4.5 9"></path></g>',
  },
  'lucide-accessibility': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="16" cy="4" r="1"></circle><path d="m18 19 1-7-6 1"></path><path d="m5 8 3-3 5.5 3-2.36 3.5"></path><path d="M4.24 14.5a5 5 0 0 0 6.88 6"></path><path d="M13.76 17.5a5 5 0 0 0-6.88-6"></path></g>',
  },
  'lucide-activity': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"></path></g>',
  },
  'lucide-air-vent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 17.5a2.5 2.5 0 1 1-4 2.03V12"></path><path d="M6 12H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><path d="M6 8h12"></path><path d="M6.6 15.572A2 2 0 1 0 10 17v-5"></path></g>',
  },
  'lucide-airplay': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"></path><path d="m12 15 5 6H7Z"></path></g>',
  },
  'lucide-alarm-clock-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M5 3 2 6"></path><path d="m22 6-3-3"></path><path d="M6.38 18.7 4 21"></path><path d="M17.64 18.67 20 21"></path><path d="m9 13 2 2 4-4"></path></g>',
  },
  'lucide-alarm-clock-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M5 3 2 6"></path><path d="m22 6-3-3"></path><path d="M6.38 18.7 4 21"></path><path d="M17.64 18.67 20 21"></path><path d="M9 13h6"></path></g>',
  },
  'lucide-alarm-clock-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.87 6.87a8 8 0 1 0 11.26 11.26"></path><path d="M19.9 14.25a8 8 0 0 0-9.15-9.15"></path><path d="m22 6-3-3"></path><path d="M6.26 18.67 4 21"></path><path d="m2 2 20 20"></path><path d="M4 4 2 6"></path></g>',
  },
  'lucide-alarm-clock-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M5 3 2 6"></path><path d="m22 6-3-3"></path><path d="M6.38 18.7 4 21"></path><path d="M17.64 18.67 20 21"></path><path d="M12 10v6"></path><path d="M9 13h6"></path></g>',
  },
  'lucide-alarm-clock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M12 9v4l2 2"></path><path d="M5 3 2 6"></path><path d="m22 6-3-3"></path><path d="M6.38 18.7 4 21"></path><path d="M17.64 18.67 20 21"></path></g>',
  },
  'lucide-alarm-smoke': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21c0-2.5 2-2.5 2-5"></path><path d="M16 21c0-2.5 2-2.5 2-5"></path><path d="m19 8-.8 3a1.25 1.25 0 0 1-1.2 1H7a1.25 1.25 0 0 1-1.2-1L5 8"></path><path d="M21 3a1 1 0 0 1 1 1v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a1 1 0 0 1 1-1z"></path><path d="M6 21c0-2.5 2-2.5 2-5"></path></g>',
  },
  'lucide-album': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><polyline points="11 3 11 11 14 8 17 11 17 3"></polyline></g>',
  },
  'lucide-align-center-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h20"></path><path d="M10 16v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4"></path><path d="M10 8V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v4"></path><path d="M20 16v1a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1"></path><path d="M14 8V7c0-1.1.9-2 2-2h2a2 2 0 0 1 2 2v1"></path></g>',
  },
  'lucide-align-center-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20"></path><path d="M8 10H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h4"></path><path d="M16 10h4a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-4"></path><path d="M8 20H7a2 2 0 0 1-2-2v-2c0-1.1.9-2 2-2h1"></path><path d="M16 14h1a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-1"></path></g>',
  },
  'lucide-align-center': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 12H7"></path><path d="M19 18H5"></path><path d="M21 6H3"></path></g>',
  },
  'lucide-align-end-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="16" x="4" y="2" rx="2"></rect><rect width="6" height="9" x="14" y="9" rx="2"></rect><path d="M22 22H2"></path></g>',
  },
  'lucide-align-end-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="6" x="2" y="4" rx="2"></rect><rect width="9" height="6" x="9" y="14" rx="2"></rect><path d="M22 22V2"></path></g>',
  },
  'lucide-align-horizontal-distribute-center': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="4" y="5" rx="2"></rect><rect width="6" height="10" x="14" y="7" rx="2"></rect><path d="M17 22v-5"></path><path d="M17 7V2"></path><path d="M7 22v-3"></path><path d="M7 5V2"></path></g>',
  },
  'lucide-align-horizontal-distribute-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="4" y="5" rx="2"></rect><rect width="6" height="10" x="14" y="7" rx="2"></rect><path d="M10 2v20"></path><path d="M20 2v20"></path></g>',
  },
  'lucide-align-horizontal-distribute-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="4" y="5" rx="2"></rect><rect width="6" height="10" x="14" y="7" rx="2"></rect><path d="M4 2v20"></path><path d="M14 2v20"></path></g>',
  },
  'lucide-align-horizontal-justify-center': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="2" y="5" rx="2"></rect><rect width="6" height="10" x="16" y="7" rx="2"></rect><path d="M12 2v20"></path></g>',
  },
  'lucide-align-horizontal-justify-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="2" y="5" rx="2"></rect><rect width="6" height="10" x="12" y="7" rx="2"></rect><path d="M22 2v20"></path></g>',
  },
  'lucide-align-horizontal-justify-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="6" y="5" rx="2"></rect><rect width="6" height="10" x="16" y="7" rx="2"></rect><path d="M2 2v20"></path></g>',
  },
  'lucide-align-horizontal-space-around': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="10" x="9" y="7" rx="2"></rect><path d="M4 22V2"></path><path d="M20 22V2"></path></g>',
  },
  'lucide-align-horizontal-space-between': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="14" x="3" y="5" rx="2"></rect><rect width="6" height="10" x="15" y="7" rx="2"></rect><path d="M3 2v20"></path><path d="M21 2v20"></path></g>',
  },
  'lucide-align-justify': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18"></path><path d="M3 18h18"></path><path d="M3 6h18"></path></g>',
  },
  'lucide-align-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12H3"></path><path d="M17 18H3"></path><path d="M21 6H3"></path></g>',
  },
  'lucide-align-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12H9"></path><path d="M21 18H7"></path><path d="M21 6H3"></path></g>',
  },
  'lucide-align-start-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="16" x="4" y="6" rx="2"></rect><rect width="6" height="9" x="14" y="6" rx="2"></rect><path d="M22 2H2"></path></g>',
  },
  'lucide-align-start-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="9" height="6" x="6" y="14" rx="2"></rect><rect width="16" height="6" x="6" y="4" rx="2"></rect><path d="M2 2v20"></path></g>',
  },
  'lucide-align-vertical-distribute-center': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 17h-3"></path><path d="M22 7h-5"></path><path d="M5 17H2"></path><path d="M7 7H2"></path><rect x="5" y="14" width="14" height="6" rx="2"></rect><rect x="7" y="4" width="10" height="6" rx="2"></rect></g>',
  },
  'lucide-align-vertical-distribute-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="14" rx="2"></rect><rect width="10" height="6" x="7" y="4" rx="2"></rect><path d="M2 20h20"></path><path d="M2 10h20"></path></g>',
  },
  'lucide-align-vertical-distribute-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="14" rx="2"></rect><rect width="10" height="6" x="7" y="4" rx="2"></rect><path d="M2 14h20"></path><path d="M2 4h20"></path></g>',
  },
  'lucide-align-vertical-justify-center': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="16" rx="2"></rect><rect width="10" height="6" x="7" y="2" rx="2"></rect><path d="M2 12h20"></path></g>',
  },
  'lucide-align-vertical-justify-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="12" rx="2"></rect><rect width="10" height="6" x="7" y="2" rx="2"></rect><path d="M2 22h20"></path></g>',
  },
  'lucide-align-vertical-justify-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="16" rx="2"></rect><rect width="10" height="6" x="7" y="6" rx="2"></rect><path d="M2 2h20"></path></g>',
  },
  'lucide-align-vertical-space-around': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="10" height="6" x="7" y="9" rx="2"></rect><path d="M22 20H2"></path><path d="M22 4H2"></path></g>',
  },
  'lucide-align-vertical-space-between': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="6" x="5" y="15" rx="2"></rect><rect width="10" height="6" x="7" y="3" rx="2"></rect><path d="M2 21h20"></path><path d="M2 3h20"></path></g>',
  },
  'lucide-ambulance': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10H6"></path><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path><path d="M19 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.578-.502l-1.539-3.076A1 1 0 0 0 16.382 8H14"></path><path d="M8 8v4"></path><path d="M9 18h6"></path><circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle></g>',
  },
  'lucide-ampersand': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 12c0 4.4-3.6 8-8 8A4.5 4.5 0 0 1 5 15.5c0-6 8-4 8-8.5a3 3 0 1 0-6 0c0 3 2.5 8.5 12 13"></path><path d="M16 12h3"></path></g>',
  },
  'lucide-ampersands': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17c-5-3-7-7-7-9a2 2 0 0 1 4 0c0 2.5-5 2.5-5 6 0 1.7 1.3 3 3 3 2.8 0 5-2.2 5-5"></path><path d="M22 17c-5-3-7-7-7-9a2 2 0 0 1 4 0c0 2.5-5 2.5-5 6 0 1.7 1.3 3 3 3 2.8 0 5-2.2 5-5"></path></g>',
  },
  'lucide-amphora': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v5.632c0 .424-.272.795-.653.982A6 6 0 0 0 6 14c.006 4 3 7 5 8"></path><path d="M10 5H8a2 2 0 0 0 0 4h.68"></path><path d="M14 2v5.632c0 .424.272.795.652.982A6 6 0 0 1 18 14c0 4-3 7-5 8"></path><path d="M14 5h2a2 2 0 0 1 0 4h-.68"></path><path d="M18 22H6"></path><path d="M9 2h6"></path></g>',
  },
  'lucide-anchor': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22V8"></path><path d="M5 12H2a10 10 0 0 0 20 0h-3"></path><circle cx="12" cy="5" r="3"></circle></g>',
  },
  'lucide-angry': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 16s-1.5-2-4-2-4 2-4 2"></path><path d="M7.5 8 10 9"></path><path d="m14 9 2.5-1"></path><path d="M9 10h.01"></path><path d="M15 10h.01"></path></g>',
  },
  'lucide-annoyed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 15h8"></path><path d="M8 9h2"></path><path d="M14 9h2"></path></g>',
  },
  'lucide-antenna': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12 7 2"></path><path d="m7 12 5-10"></path><path d="m12 12 5-10"></path><path d="m17 12 5-10"></path><path d="M4.5 7h15"></path><path d="M12 16v6"></path></g>',
  },
  'lucide-anvil': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10H6a4 4 0 0 1-4-4 1 1 0 0 1 1-1h4"></path><path d="M7 5a1 1 0 0 1 1-1h13a1 1 0 0 1 1 1 7 7 0 0 1-7 7H8a1 1 0 0 1-1-1z"></path><path d="M9 12v5"></path><path d="M15 12v5"></path><path d="M5 20a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3 1 1 0 0 1-1 1H6a1 1 0 0 1-1-1"></path></g>',
  },
  'lucide-aperture': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m14.31 8 5.74 9.94"></path><path d="M9.69 8h11.48"></path><path d="m7.38 12 5.74-9.94"></path><path d="M9.69 16 3.95 6.06"></path><path d="M14.31 16H2.83"></path><path d="m16.62 12-5.74 9.94"></path></g>',
  },
  'lucide-app-window-mac': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M6 8h.01"></path><path d="M10 8h.01"></path><path d="M14 8h.01"></path></g>',
  },
  'lucide-app-window': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="M10 4v4"></path><path d="M2 8h20"></path><path d="M6 4v4"></path></g>',
  },
  'lucide-apple': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z"></path><path d="M10 2c1 .5 2 2 2 5"></path></g>',
  },
  'lucide-archive-restore': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="5" x="2" y="3" rx="1"></rect><path d="M4 8v11a2 2 0 0 0 2 2h2"></path><path d="M20 8v11a2 2 0 0 1-2 2h-2"></path><path d="m9 15 3-3 3 3"></path><path d="M12 12v9"></path></g>',
  },
  'lucide-archive-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="5" x="2" y="3" rx="1"></rect><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path><path d="m9.5 17 5-5"></path><path d="m9.5 12 5 5"></path></g>',
  },
  'lucide-archive': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="5" x="2" y="3" rx="1"></rect><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path><path d="M10 12h4"></path></g>',
  },
  'lucide-armchair': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3"></path><path d="M3 16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z"></path><path d="M5 18v2"></path><path d="M19 18v2"></path></g>',
  },
  'lucide-arrow-big-down-dash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5H9"></path><path d="M15 9v3h4l-7 7-7-7h4V9z"></path></g>',
  },
  'lucide-arrow-big-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6v6h4l-7 7-7-7h4V6h6z"></path></g>',
  },
  'lucide-arrow-big-left-dash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 15V9"></path><path d="M15 15h-3v4l-7-7 7-7v4h3v6z"></path></g>',
  },
  'lucide-arrow-big-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 15h-6v4l-7-7 7-7v4h6v6z"></path></g>',
  },
  'lucide-arrow-big-right-dash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9v6"></path><path d="M9 9h3V5l7 7-7 7v-4H9V9z"></path></g>',
  },
  'lucide-arrow-big-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9h6V5l7 7-7 7v-4H6V9z"></path></g>',
  },
  'lucide-arrow-big-up-dash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 19h6"></path><path d="M9 15v-3H5l7-7 7 7h-4v3H9z"></path></g>',
  },
  'lucide-arrow-big-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18v-6H5l7-7 7 7h-4v6H9z"></path></g>',
  },
  'lucide-arrow-down-0-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><rect x="15" y="4" width="4" height="6" ry="2"></rect><path d="M17 20v-6h-2"></path><path d="M15 20h4"></path></g>',
  },
  'lucide-arrow-down-1-0': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><path d="M17 10V4h-2"></path><path d="M15 10h4"></path><rect x="15" y="14" width="4" height="6" ry="2"></rect></g>',
  },
  'lucide-arrow-down-a-z': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><path d="M20 8h-5"></path><path d="M15 10V6.5a2.5 2.5 0 0 1 5 0V10"></path><path d="M15 14h5l-5 6h5"></path></g>',
  },
  'lucide-arrow-down-from-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 3H5"></path><path d="M12 21V7"></path><path d="m6 15 6 6 6-6"></path></g>',
  },
  'lucide-arrow-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 7 7 17"></path><path d="M17 17H7V7"></path></g>',
  },
  'lucide-arrow-down-narrow-wide': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><path d="M11 4h4"></path><path d="M11 8h7"></path><path d="M11 12h10"></path></g>',
  },
  'lucide-arrow-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10"></path><path d="M17 7v10H7"></path></g>',
  },
  'lucide-arrow-down-to-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v14"></path><path d="m19 9-7 7-7-7"></path><circle cx="12" cy="21" r="1"></circle></g>',
  },
  'lucide-arrow-down-to-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17V3"></path><path d="m6 11 6 6 6-6"></path><path d="M19 21H5"></path></g>',
  },
  'lucide-arrow-down-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><path d="m21 8-4-4-4 4"></path><path d="M17 4v16"></path></g>',
  },
  'lucide-arrow-down-wide-narrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 20V4"></path><path d="M11 4h10"></path><path d="M11 8h7"></path><path d="M11 12h4"></path></g>',
  },
  'lucide-arrow-down-z-a': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 16 4 4 4-4"></path><path d="M7 4v16"></path><path d="M15 4h5l-5 6h5"></path><path d="M15 20v-3.5a2.5 2.5 0 0 1 5 0V20"></path><path d="M20 18h-5"></path></g>',
  },
  'lucide-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"></path><path d="m19 12-7 7-7-7"></path></g>',
  },
  'lucide-arrow-left-from-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6-6 6 6 6"></path><path d="M3 12h14"></path><path d="M21 19V5"></path></g>',
  },
  'lucide-arrow-left-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3 4 7l4 4"></path><path d="M4 7h16"></path><path d="m16 21 4-4-4-4"></path><path d="M20 17H4"></path></g>',
  },
  'lucide-arrow-left-to-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 19V5"></path><path d="m13 6-6 6 6 6"></path><path d="M7 12h14"></path></g>',
  },
  'lucide-arrow-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></g>',
  },
  'lucide-arrow-right-from-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5v14"></path><path d="M21 12H7"></path><path d="m15 18 6-6-6-6"></path></g>',
  },
  'lucide-arrow-right-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 3 4 4-4 4"></path><path d="M20 7H4"></path><path d="m8 21-4-4 4-4"></path><path d="M4 17h16"></path></g>',
  },
  'lucide-arrow-right-to-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 12H3"></path><path d="m11 18 6-6-6-6"></path><path d="M21 5v14"></path></g>',
  },
  'lucide-arrow-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></g>',
  },
  'lucide-arrow-up-0-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><rect x="15" y="4" width="4" height="6" ry="2"></rect><path d="M17 20v-6h-2"></path><path d="M15 20h4"></path></g>',
  },
  'lucide-arrow-up-1-0': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><path d="M17 10V4h-2"></path><path d="M15 10h4"></path><rect x="15" y="14" width="4" height="6" ry="2"></rect></g>',
  },
  'lucide-arrow-up-a-z': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><path d="M20 8h-5"></path><path d="M15 10V6.5a2.5 2.5 0 0 1 5 0V10"></path><path d="M15 14h5l-5 6h5"></path></g>',
  },
  'lucide-arrow-up-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21 16-4 4-4-4"></path><path d="M17 20V4"></path><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path></g>',
  },
  'lucide-arrow-up-from-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 9 7-7 7 7"></path><path d="M12 16V2"></path><circle cx="12" cy="21" r="1"></circle></g>',
  },
  'lucide-arrow-up-from-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 9-6-6-6 6"></path><path d="M12 3v14"></path><path d="M5 21h14"></path></g>',
  },
  'lucide-arrow-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17V7h10"></path><path d="M17 17 7 7"></path></g>',
  },
  'lucide-arrow-up-narrow-wide': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><path d="M11 12h4"></path><path d="M11 16h7"></path><path d="M11 20h10"></path></g>',
  },
  'lucide-arrow-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"></path><path d="M7 17 17 7"></path></g>',
  },
  'lucide-arrow-up-to-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3h14"></path><path d="m18 13-6-6-6 6"></path><path d="M12 7v14"></path></g>',
  },
  'lucide-arrow-up-wide-narrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><path d="M11 12h10"></path><path d="M11 16h7"></path><path d="M11 20h4"></path></g>',
  },
  'lucide-arrow-up-z-a': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 8 4-4 4 4"></path><path d="M7 4v16"></path><path d="M15 4h5l-5 6h5"></path><path d="M15 20v-3.5a2.5 2.5 0 0 1 5 0V20"></path><path d="M20 18h-5"></path></g>',
  },
  'lucide-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 7-7 7 7"></path><path d="M12 19V5"></path></g>',
  },
  'lucide-arrows-up-from-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4 6 3-3 3 3"></path><path d="M7 17V3"></path><path d="m14 6 3-3 3 3"></path><path d="M17 17V3"></path><path d="M4 21h16"></path></g>',
  },
  'lucide-asterisk': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v12"></path><path d="M17.196 9 6.804 15"></path><path d="m6.804 9 10.392 6"></path></g>',
  },
  'lucide-at-sign': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"></path></g>',
  },
  'lucide-atom': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"></circle><path d="M20.2 20.2c2.04-2.03.02-7.36-4.5-11.9-4.54-4.52-9.87-6.54-11.9-4.5-2.04 2.03-.02 7.36 4.5 11.9 4.54 4.52 9.87 6.54 11.9 4.5Z"></path><path d="M15.7 15.7c4.52-4.54 6.54-9.87 4.5-11.9-2.03-2.04-7.36-.02-11.9 4.5-4.52 4.54-6.54 9.87-4.5 11.9 2.03 2.04 7.36.02 11.9-4.5Z"></path></g>',
  },
  'lucide-audio-lines': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 10v3"></path><path d="M6 6v11"></path><path d="M10 3v18"></path><path d="M14 8v7"></path><path d="M18 5v13"></path><path d="M22 10v3"></path></g>',
  },
  'lucide-audio-waveform': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 13a2 2 0 0 0 2-2V7a2 2 0 0 1 4 0v13a2 2 0 0 0 4 0V4a2 2 0 0 1 4 0v13a2 2 0 0 0 4 0v-4a2 2 0 0 1 2-2"></path></g>',
  },
  'lucide-award': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path><circle cx="12" cy="8" r="6"></circle></g>',
  },
  'lucide-axe': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14 12-8.5 8.5a2.12 2.12 0 1 1-3-3L11 9"></path><path d="M15 13 9 7l4-4 6 6h3a8 8 0 0 1-7 7z"></path></g>',
  },
  'lucide-axis-3d': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16h16"></path><path d="m4 20 7-7"></path></g>',
  },
  'lucide-baby': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5"></path><path d="M15 12h.01"></path><path d="M19.38 6.813A9 9 0 0 1 20.8 10.2a2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1"></path><path d="M9 12h.01"></path></g>',
  },
  'lucide-backpack': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"></path><path d="M8 10h8"></path><path d="M8 18h8"></path><path d="M8 22v-6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></g>',
  },
  'lucide-badge-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></g>',
  },
  'lucide-badge-cent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M12 7v10"></path><path d="M15.4 10a4 4 0 1 0 0 4"></path></g>',
  },
  'lucide-badge-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="m9 12 2 2 4-4"></path></g>',
  },
  'lucide-badge-dollar-sign': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path><path d="M12 18V6"></path></g>',
  },
  'lucide-badge-euro': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M7 12h5"></path><path d="M15 9.4a4 4 0 1 0 0 5.2"></path></g>',
  },
  'lucide-badge-help': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" x2="12.01" y1="17" y2="17"></line></g>',
  },
  'lucide-badge-indian-rupee': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M8 8h8"></path><path d="M8 12h8"></path><path d="m13 17-5-1h1a4 4 0 0 0 0-8"></path></g>',
  },
  'lucide-badge-info': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><line x1="12" x2="12" y1="16" y2="12"></line><line x1="12" x2="12.01" y1="8" y2="8"></line></g>',
  },
  'lucide-badge-japanese-yen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="m9 8 3 3v7"></path><path d="m12 11 3-3"></path><path d="M9 12h6"></path><path d="M9 16h6"></path></g>',
  },
  'lucide-badge-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><line x1="8" x2="16" y1="12" y2="12"></line></g>',
  },
  'lucide-badge-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="m15 9-6 6"></path><path d="M9 9h.01"></path><path d="M15 15h.01"></path></g>',
  },
  'lucide-badge-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><line x1="12" x2="12" y1="8" y2="16"></line><line x1="8" x2="16" y1="12" y2="12"></line></g>',
  },
  'lucide-badge-pound-sterling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M8 12h4"></path><path d="M10 16V9.5a2.5 2.5 0 0 1 5 0"></path><path d="M8 16h7"></path></g>',
  },
  'lucide-badge-russian-ruble': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M9 16h5"></path><path d="M9 12h5a2 2 0 1 0 0-4h-3v9"></path></g>',
  },
  'lucide-badge-swiss-franc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><path d="M11 17V8h4"></path><path d="M11 12h3"></path><path d="M9 16h4"></path></g>',
  },
  'lucide-badge-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path><line x1="15" x2="9" y1="9" y2="15"></line><line x1="9" x2="15" y1="9" y2="15"></line></g>',
  },
  'lucide-badge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"></path></g>',
  },
  'lucide-baggage-claim': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 18H6a2 2 0 0 1-2-2V7a2 2 0 0 0-2-2"></path><path d="M17 14V4a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v10"></path><rect width="13" height="8" x="8" y="6" rx="1"></rect><circle cx="18" cy="20" r="2"></circle><circle cx="9" cy="20" r="2"></circle></g>',
  },
  'lucide-ban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m4.9 4.9 14.2 14.2"></path></g>',
  },
  'lucide-banana': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 13c3.5-2 8-2 10 2a5.5 5.5 0 0 1 8 5"></path><path d="M5.15 17.89c5.52-1.52 8.65-6.89 7-12C11.55 4 11.5 2 13 2c3.22 0 5 5.5 5 8 0 6.5-4.2 12-10.49 12C5.11 22 2 22 2 20c0-1.5 1.14-1.55 3.15-2.11Z"></path></g>',
  },
  'lucide-bandage': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10.01h.01"></path><path d="M10 14.01h.01"></path><path d="M14 10.01h.01"></path><path d="M14 14.01h.01"></path><path d="M18 6v11.5"></path><path d="M6 6v12"></path><rect x="2" y="6" width="20" height="12" rx="2"></rect></g>',
  },
  'lucide-banknote-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5"></path><path d="m16 19 3 3 3-3"></path><path d="M18 12h.01"></path><path d="M19 16v6"></path><path d="M6 12h.01"></path><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-banknote-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5"></path><path d="M18 12h.01"></path><path d="M19 22v-6"></path><path d="m22 19-3-3-3 3"></path><path d="M6 12h.01"></path><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-banknote-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5"></path><path d="m17 17 5 5"></path><path d="M18 12h.01"></path><path d="m22 17-5 5"></path><path d="M6 12h.01"></path><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-banknote': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"></rect><circle cx="12" cy="12" r="2"></circle><path d="M6 12h.01M18 12h.01"></path></g>',
  },
  'lucide-barcode': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5v14"></path><path d="M8 5v14"></path><path d="M12 5v14"></path><path d="M17 5v14"></path><path d="M21 5v14"></path></g>',
  },
  'lucide-baseline': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16"></path><path d="m6 16 6-12 6 12"></path><path d="M8 12h8"></path></g>',
  },
  'lucide-bath': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 4 8 6"></path><path d="M17 19v2"></path><path d="M2 12h20"></path><path d="M7 19v2"></path><path d="M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"></path></g>',
  },
  'lucide-battery-charging': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 7h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2"></path><path d="M6 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h1"></path><path d="m11 7-3 5h4l-3 5"></path><line x1="22" x2="22" y1="11" y2="13"></line></g>',
  },
  'lucide-battery-full': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="10" x="2" y="7" rx="2" ry="2"></rect><line x1="22" x2="22" y1="11" y2="13"></line><line x1="6" x2="6" y1="11" y2="13"></line><line x1="10" x2="10" y1="11" y2="13"></line><line x1="14" x2="14" y1="11" y2="13"></line></g>',
  },
  'lucide-battery-low': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="10" x="2" y="7" rx="2" ry="2"></rect><line x1="22" x2="22" y1="11" y2="13"></line><line x1="6" x2="6" y1="11" y2="13"></line></g>',
  },
  'lucide-battery-medium': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="10" x="2" y="7" rx="2" ry="2"></rect><line x1="22" x2="22" y1="11" y2="13"></line><line x1="6" x2="6" y1="11" y2="13"></line><line x1="10" x2="10" y1="11" y2="13"></line></g>',
  },
  'lucide-battery-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9v6"></path><path d="M13.5 7H16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2.5"></path><path d="M22 11v2"></path><path d="M6.5 17H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2.5"></path><path d="M7 12h6"></path></g>',
  },
  'lucide-battery-warning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17h.01"></path><path d="M10 7v6"></path><path d="M14 7h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2"></path><path d="M22 11v2"></path><path d="M6 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path></g>',
  },
  'lucide-battery': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="10" x="2" y="7" rx="2" ry="2"></rect><line x1="22" x2="22" y1="11" y2="13"></line></g>',
  },
  'lucide-beaker': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 3h15"></path><path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3"></path><path d="M6 14h12"></path></g>',
  },
  'lucide-bean-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 9c-.64.64-1.521.954-2.402 1.165A6 6 0 0 0 8 22a13.96 13.96 0 0 0 9.9-4.1"></path><path d="M10.75 5.093A6 6 0 0 1 22 8c0 2.411-.61 4.68-1.683 6.66"></path><path d="M5.341 10.62a4 4 0 0 0 6.487 1.208M10.62 5.341a4.015 4.015 0 0 1 2.039 2.04"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-bean': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.165 6.598C9.954 7.478 9.64 8.36 9 9c-.64.64-1.521.954-2.402 1.165A6 6 0 0 0 8 22c7.732 0 14-6.268 14-14a6 6 0 0 0-11.835-1.402Z"></path><path d="M5.341 10.62a4 4 0 1 0 5.279-5.28"></path></g>',
  },
  'lucide-bed-double': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"></path><path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"></path><path d="M12 4v6"></path><path d="M2 18h20"></path></g>',
  },
  'lucide-bed-single': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20v-8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8"></path><path d="M5 10V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4"></path><path d="M3 18h18"></path></g>',
  },
  'lucide-bed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4v16"></path><path d="M2 8h18a2 2 0 0 1 2 2v10"></path><path d="M2 17h20"></path><path d="M6 8v9"></path></g>',
  },
  'lucide-beef': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.4 13.7A6.5 6.5 0 1 0 6.28 6.6c-1.1 3.13-.78 3.9-3.18 6.08A3 3 0 0 0 5 18c4 0 8.4-1.8 11.4-4.3"></path><path d="m18.5 6 2.19 4.5a6.48 6.48 0 0 1-2.29 7.2C15.4 20.2 11 22 7 22a3 3 0 0 1-2.68-1.66L2.4 16.5"></path><circle cx="12.5" cy="8.5" r="2.5"></circle></g>',
  },
  'lucide-beer-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 13v5"></path><path d="M17 11.47V8"></path><path d="M17 11h1a3 3 0 0 1 2.745 4.211"></path><path d="m2 2 20 20"></path><path d="M5 8v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3"></path><path d="M7.536 7.535C6.766 7.649 6.154 8 5.5 8a2.5 2.5 0 0 1-1.768-4.268"></path><path d="M8.727 3.204C9.306 2.767 9.885 2 11 2c1.56 0 2 1.5 3 1.5s1.72-.5 2.5-.5a1 1 0 1 1 0 5c-.78 0-1.5-.5-2.5-.5a3.149 3.149 0 0 0-.842.12"></path><path d="M9 14.6V18"></path></g>',
  },
  'lucide-beer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 11h1a3 3 0 0 1 0 6h-1"></path><path d="M9 12v6"></path><path d="M13 12v6"></path><path d="M14 7.5c-1 0-1.44.5-3 .5s-2-.5-3-.5-1.72.5-2.5.5a2.5 2.5 0 0 1 0-5c.78 0 1.57.5 2.5.5S9.44 2 11 2s2 1.5 3 1.5 1.72-.5 2.5-.5a2.5 2.5 0 0 1 0 5c-.78 0-1.5-.5-2.5-.5Z"></path><path d="M5 8v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8"></path></g>',
  },
  'lucide-bell-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M13.916 2.314A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.74 7.327A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673 9 9 0 0 1-.585-.665"></path><circle cx="18" cy="8" r="3"></circle></g>',
  },
  'lucide-bell-electric': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.518 17.347A7 7 0 0 1 14 19"></path><path d="M18.8 4A11 11 0 0 1 20 9"></path><path d="M9 9h.01"></path><circle cx="20" cy="16" r="2"></circle><circle cx="9" cy="9" r="7"></circle><rect x="4" y="16" width="10" height="6" rx="2"></rect></g>',
  },
  'lucide-bell-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M15 8h6"></path><path d="M16.243 3.757A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673A9.4 9.4 0 0 1 18.667 12"></path></g>',
  },
  'lucide-bell-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M17 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 .258-1.742"></path><path d="m2 2 20 20"></path><path d="M8.668 3.01A6 6 0 0 1 18 8c0 2.687.77 4.653 1.707 6.05"></path></g>',
  },
  'lucide-bell-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M15 8h6"></path><path d="M18 5v6"></path><path d="M20.002 14.464a9 9 0 0 0 .738.863A1 1 0 0 1 20 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 8.75-5.332"></path></g>',
  },
  'lucide-bell-ring': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M22 8c0-2.3-.8-4.3-2-6"></path><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"></path><path d="M4 2C2.8 3.7 2 5.7 2 8"></path></g>',
  },
  'lucide-bell': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.268 21a2 2 0 0 0 3.464 0"></path><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"></path></g>',
  },
  'lucide-between-horizontal-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="13" height="7" x="3" y="3" rx="1"></rect><path d="m22 15-3-3 3-3"></path><rect width="13" height="7" x="3" y="14" rx="1"></rect></g>',
  },
  'lucide-between-horizontal-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="13" height="7" x="8" y="3" rx="1"></rect><path d="m2 9 3 3-3 3"></path><rect width="13" height="7" x="8" y="14" rx="1"></rect></g>',
  },
  'lucide-between-vertical-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="13" x="3" y="3" rx="1"></rect><path d="m9 22 3-3 3 3"></path><rect width="7" height="13" x="14" y="3" rx="1"></rect></g>',
  },
  'lucide-between-vertical-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="13" x="3" y="8" rx="1"></rect><path d="m15 2-3 3-3-3"></path><rect width="7" height="13" x="14" y="8" rx="1"></rect></g>',
  },
  'lucide-biceps-flexed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.409 13.017A5 5 0 0 1 22 15c0 3.866-4 7-9 7-4.077 0-8.153-.82-10.371-2.462-.426-.316-.631-.832-.62-1.362C2.118 12.723 2.627 2 10 2a3 3 0 0 1 3 3 2 2 0 0 1-2 2c-1.105 0-1.64-.444-2-1"></path><path d="M15 14a5 5 0 0 0-7.584 2"></path><path d="M9.964 6.825C8.019 7.977 9.5 13 8 15"></path></g>',
  },
  'lucide-bike': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18.5" cy="17.5" r="3.5"></circle><circle cx="5.5" cy="17.5" r="3.5"></circle><circle cx="15" cy="5" r="1"></circle><path d="M12 17.5V14l-3-3 4-3 2 3h2"></path></g>',
  },
  'lucide-binary': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="14" y="14" width="4" height="6" rx="2"></rect><rect x="6" y="4" width="4" height="6" rx="2"></rect><path d="M6 20h4"></path><path d="M14 10h4"></path><path d="M6 14h2v6"></path><path d="M14 4h2v6"></path></g>',
  },
  'lucide-binoculars': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10h4"></path><path d="M19 7V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v3"></path><path d="M20 21a2 2 0 0 0 2-2v-3.851c0-1.39-2-2.962-2-4.829V8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2z"></path><path d="M 22 16 L 2 16"></path><path d="M4 21a2 2 0 0 1-2-2v-3.851c0-1.39 2-2.962 2-4.829V8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11a2 2 0 0 1-2 2z"></path><path d="M9 7V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v3"></path></g>',
  },
  'lucide-biohazard': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="11.9" r="2"></circle><path d="M6.7 3.4c-.9 2.5 0 5.2 2.2 6.7C6.5 9 3.7 9.6 2 11.6"></path><path d="m8.9 10.1 1.4.8"></path><path d="M17.3 3.4c.9 2.5 0 5.2-2.2 6.7 2.4-1.2 5.2-.6 6.9 1.5"></path><path d="m15.1 10.1-1.4.8"></path><path d="M16.7 20.8c-2.6-.4-4.6-2.6-4.7-5.3-.2 2.6-2.1 4.8-4.7 5.2"></path><path d="M12 13.9v1.6"></path><path d="M13.5 5.4c-1-.2-2-.2-3 0"></path><path d="M17 16.4c.7-.7 1.2-1.6 1.5-2.5"></path><path d="M5.5 13.9c.3.9.8 1.8 1.5 2.5"></path></g>',
  },
  'lucide-bird': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 7h.01"></path><path d="M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20"></path><path d="m20 7 2 .5-2 .5"></path><path d="M10 18v3"></path><path d="M14 17.75V21"></path><path d="M7 18a6 6 0 0 0 3.84-10.61"></path></g>',
  },
  'lucide-bitcoin': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042.348-1.97M7.48 20.364l3.126-17.727"></path></g>',
  },
  'lucide-blend': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="9" r="7"></circle><circle cx="15" cy="15" r="7"></circle></g>',
  },
  'lucide-blinds': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h18"></path><path d="M20 7H8"></path><path d="M20 11H8"></path><path d="M10 19h10"></path><path d="M8 15h12"></path><path d="M4 3v14"></path><circle cx="4" cy="19" r="2"></circle></g>',
  },
  'lucide-blocks': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="14" y="3" rx="1"></rect><path d="M10 21V8a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H3"></path></g>',
  },
  'lucide-bluetooth-connected': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 5V2l5 5L7 17"></path><line x1="18" x2="21" y1="12" y2="12"></line><line x1="3" x2="6" y1="12" y2="12"></line></g>',
  },
  'lucide-bluetooth-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 17-5 5V12l-5 5"></path><path d="m2 2 20 20"></path><path d="M14.5 9.5 17 7l-5-5v4.5"></path></g>',
  },
  'lucide-bluetooth-searching': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 5V2l5 5L7 17"></path><path d="M20.83 14.83a4 4 0 0 0 0-5.66"></path><path d="M18 12h.01"></path></g>',
  },
  'lucide-bluetooth': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 7 10 10-5 5V2l5 5L7 17"></path></g>',
  },
  'lucide-bold': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8"></path></g>',
  },
  'lucide-bolt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><circle cx="12" cy="12" r="4"></circle></g>',
  },
  'lucide-bomb': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="13" r="9"></circle><path d="M14.35 4.65 16.3 2.7a2.41 2.41 0 0 1 3.4 0l1.6 1.6a2.4 2.4 0 0 1 0 3.4l-1.95 1.95"></path><path d="m22 2-1.5 1.5"></path></g>',
  },
  'lucide-bone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 10c.7-.7 1.69 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .81.7 1.8 0 2.5l-7 7c-.7.7-1.69 0-2.5 0a2.5 2.5 0 0 0 0 5c.28 0 .5.22.5.5a2.5 2.5 0 1 0 5 0c0-.81-.7-1.8 0-2.5Z"></path></g>',
  },
  'lucide-book-a': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="m8 13 4-7 4 7"></path><path d="M9.1 11h5.7"></path></g>',
  },
  'lucide-book-audio': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v7"></path><path d="M16 8v3"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M8 8v3"></path></g>',
  },
  'lucide-book-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="m9 9.5 2 2 4-4"></path></g>',
  },
  'lucide-book-copy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 16V4a2 2 0 0 1 2-2h11"></path><path d="M22 18H11a2 2 0 1 0 0 4h10.5a.5.5 0 0 0 .5-.5v-15a.5.5 0 0 0-.5-.5H11a2 2 0 0 0-2 2v12"></path><path d="M5 14H4a2 2 0 1 0 0 4h1"></path></g>',
  },
  'lucide-book-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17h1.5"></path><path d="M12 22h1.5"></path><path d="M12 2h1.5"></path><path d="M17.5 22H19a1 1 0 0 0 1-1"></path><path d="M17.5 2H19a1 1 0 0 1 1 1v1.5"></path><path d="M20 14v3h-2.5"></path><path d="M20 8.5V10"></path><path d="M4 10V8.5"></path><path d="M4 19.5V14"></path><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H8"></path><path d="M8 22H6.5a1 1 0 0 1 0-5H8"></path></g>',
  },
  'lucide-book-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13V7"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="m9 10 3 3 3-3"></path></g>',
  },
  'lucide-book-headphones': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M8 12v-2a4 4 0 0 1 8 0v2"></path><circle cx="15" cy="12" r="1"></circle><circle cx="9" cy="12" r="1"></circle></g>',
  },
  'lucide-book-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8.2A2.22 2.22 0 0 0 13.8 6c-.8 0-1.4.3-1.8.9-.4-.6-1-.9-1.8-.9A2.22 2.22 0 0 0 8 8.2c0 .6.3 1.2.7 1.6A226.652 226.652 0 0 0 12 13a404 404 0 0 0 3.3-3.1 2.413 2.413 0 0 0 .7-1.7"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path></g>',
  },
  'lucide-book-image': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 13.7-2.1-2.1a2 2 0 0 0-2.8 0L9.7 17"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><circle cx="10" cy="8" r="2"></circle></g>',
  },
  'lucide-book-key': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 3 1 1"></path><path d="m20 2-4.5 4.5"></path><path d="M20 7.898V21a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2h7.844"></path><circle cx="14" cy="8" r="2"></circle></g>',
  },
  'lucide-book-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6V4a2 2 0 1 0-4 0v2"></path><path d="M20 15v6a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H10"></path><rect x="12" y="6" width="8" height="5" rx="1"></rect></g>',
  },
  'lucide-book-marked': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v8l3-3 3 3V2"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path></g>',
  },
  'lucide-book-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-book-open-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21V7"></path><path d="m16 12 2 2 4-4"></path><path d="M22 6V4a1 1 0 0 0-1-1h-5a4 4 0 0 0-4 4 4 4 0 0 0-4-4H3a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h6a3 3 0 0 1 3 3 3 3 0 0 1 3-3h6a1 1 0 0 0 1-1v-1.3"></path></g>',
  },
  'lucide-book-open-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14"></path><path d="M16 12h2"></path><path d="M16 8h2"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path><path d="M6 12h2"></path><path d="M6 8h2"></path></g>',
  },
  'lucide-book-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14"></path><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path></g>',
  },
  'lucide-book-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v6"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-book-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M8 11h8"></path><path d="M8 7h6"></path></g>',
  },
  'lucide-book-type': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13h4"></path><path d="M12 6v7"></path><path d="M16 8V6H8v2"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path></g>',
  },
  'lucide-book-up-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13V7"></path><path d="M18 2h1a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2"></path><path d="m9 10 3-3 3 3"></path><path d="m9 5 3-3 3 3"></path></g>',
  },
  'lucide-book-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13V7"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="m9 10 3-3 3 3"></path></g>',
  },
  'lucide-book-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 13a3 3 0 1 0-6 0"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><circle cx="12" cy="8" r="2"></circle></g>',
  },
  'lucide-book-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.5 7-5 5"></path><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path><path d="m9.5 7 5 5"></path></g>',
  },
  'lucide-book': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"></path></g>',
  },
  'lucide-bookmark-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2Z"></path><path d="m9 10 2 2 4-4"></path></g>',
  },
  'lucide-bookmark-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"></path><line x1="15" x2="9" y1="10" y2="10"></line></g>',
  },
  'lucide-bookmark-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"></path><line x1="12" x2="12" y1="7" y2="13"></line><line x1="15" x2="9" y1="10" y2="10"></line></g>',
  },
  'lucide-bookmark-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2Z"></path><path d="m14.5 7.5-5 5"></path><path d="m9.5 7.5 5 5"></path></g>',
  },
  'lucide-bookmark': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"></path></g>',
  },
  'lucide-boom-box': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"></path><path d="M8 8v1"></path><path d="M12 8v1"></path><path d="M16 8v1"></path><rect width="20" height="12" x="2" y="9" rx="2"></rect><circle cx="8" cy="15" r="2"></circle><circle cx="16" cy="15" r="2"></circle></g>',
  },
  'lucide-bot-message-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6V2H8"></path><path d="m8 18-4 4V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2Z"></path><path d="M2 12h2"></path><path d="M9 11v2"></path><path d="M15 11v2"></path><path d="M20 12h2"></path></g>',
  },
  'lucide-bot-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.67 8H18a2 2 0 0 1 2 2v4.33"></path><path d="M2 14h2"></path><path d="M20 14h2"></path><path d="M22 22 2 2"></path><path d="M8 8H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 1.414-.586"></path><path d="M9 13v2"></path><path d="M9.67 4H12v2.33"></path></g>',
  },
  'lucide-bot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8V4H8"></path><rect width="16" height="12" x="4" y="8" rx="2"></rect><path d="M2 14h2"></path><path d="M20 14h2"></path><path d="M15 13v2"></path><path d="M9 13v2"></path></g>',
  },
  'lucide-bow-arrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3h4v4"></path><path d="M18.575 11.082a13 13 0 0 1 1.048 9.027 1.17 1.17 0 0 1-1.914.597L14 17"></path><path d="M7 10 3.29 6.29a1.17 1.17 0 0 1 .6-1.91 13 13 0 0 1 9.03 1.05"></path><path d="M7 14a1.7 1.7 0 0 0-1.207.5l-2.646 2.646A.5.5 0 0 0 3.5 18H5a1 1 0 0 1 1 1v1.5a.5.5 0 0 0 .854.354L9.5 18.207A1.7 1.7 0 0 0 10 17v-2a1 1 0 0 0-1-1z"></path><path d="M9.707 14.293 21 3"></path></g>',
  },
  'lucide-box': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"></path><path d="m3.3 7 8.7 5 8.7-5"></path><path d="M12 22V12"></path></g>',
  },
  'lucide-boxes': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z"></path><path d="m7 16.5-4.74-2.85"></path><path d="m7 16.5 5-3"></path><path d="M7 16.5v5.17"></path><path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z"></path><path d="m17 16.5-5-3"></path><path d="m17 16.5 4.74-2.85"></path><path d="M17 16.5v5.17"></path><path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z"></path><path d="M12 8 7.26 5.15"></path><path d="m12 8 4.74-2.85"></path><path d="M12 13.5V8"></path></g>',
  },
  'lucide-braces': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"></path><path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1"></path></g>',
  },
  'lucide-brackets': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h2a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-2"></path><path d="M8 21H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h2"></path></g>',
  },
  'lucide-brain-circuit': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"></path><path d="M9 13a4.5 4.5 0 0 0 3-4"></path><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path><path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path><path d="M6 18a4 4 0 0 1-1.967-.516"></path><path d="M12 13h4"></path><path d="M12 18h6a2 2 0 0 1 2 2v1"></path><path d="M12 8h8"></path><path d="M16 8V5a2 2 0 0 1 2-2"></path><circle cx="16" cy="13" r=".5"></circle><circle cx="18" cy="3" r=".5"></circle><circle cx="20" cy="21" r=".5"></circle><circle cx="20" cy="8" r=".5"></circle></g>',
  },
  'lucide-brain-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10.852 14.772-.383.923"></path><path d="m10.852 9.228-.383-.923"></path><path d="m13.148 14.772.382.924"></path><path d="m13.531 8.305-.383.923"></path><path d="m14.772 10.852.923-.383"></path><path d="m14.772 13.148.923.383"></path><path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 0 0-5.63-1.446 3 3 0 0 0-.368 1.571 4 4 0 0 0-2.525 5.771"></path><path d="M17.998 5.125a4 4 0 0 1 2.525 5.771"></path><path d="M19.505 10.294a4 4 0 0 1-1.5 7.706"></path><path d="M4.032 17.483A4 4 0 0 0 11.464 20c.18-.311.892-.311 1.072 0a4 4 0 0 0 7.432-2.516"></path><path d="M4.5 10.291A4 4 0 0 0 6 18"></path><path d="M6.002 5.125a3 3 0 0 0 .4 1.375"></path><path d="m9.228 10.852-.923-.383"></path><path d="m9.228 13.148-.923.383"></path><circle cx="12" cy="12" r="3"></circle></g>',
  },
  'lucide-brain': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"></path><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"></path><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"></path><path d="M17.599 6.5a3 3 0 0 0 .399-1.375"></path><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path><path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path><path d="M19.938 10.5a4 4 0 0 1 .585.396"></path><path d="M6 18a4 4 0 0 1-1.967-.516"></path><path d="M19.967 17.484A4 4 0 0 1 18 18"></path></g>',
  },
  'lucide-brick-wall-fire': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3v2.107"></path><path d="M17 9c1 3 2.5 3.5 3.5 4.5A5 5 0 0 1 22 17a5 5 0 0 1-10 0c0-.3 0-.6.1-.9a2 2 0 1 0 3.3-2C13 11.5 16 9 17 9"></path><path d="M21 8.274V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.938"></path><path d="M3 15h5.253"></path><path d="M3 9h8.228"></path><path d="M8 15v6"></path><path d="M8 3v6"></path></g>',
  },
  'lucide-brick-wall': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M12 9v6"></path><path d="M16 15v6"></path><path d="M16 3v6"></path><path d="M3 15h18"></path><path d="M3 9h18"></path><path d="M8 15v6"></path><path d="M8 3v6"></path></g>',
  },
  'lucide-briefcase-business': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12h.01"></path><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"></path><path d="M22 13a18.15 18.15 0 0 1-20 0"></path><rect width="20" height="14" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-briefcase-conveyor-belt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 20v2"></path><path d="M14 20v2"></path><path d="M18 20v2"></path><path d="M21 20H3"></path><path d="M6 20v2"></path><path d="M8 16V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12"></path><rect x="4" y="6" width="16" height="10" rx="2"></rect></g>',
  },
  'lucide-briefcase-medical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 11v4"></path><path d="M14 13h-4"></path><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"></path><path d="M18 6v14"></path><path d="M6 6v14"></path><rect width="20" height="14" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-briefcase': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path><rect width="20" height="14" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-bring-to-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="8" width="8" height="8" rx="2"></rect><path d="M4 10a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2"></path><path d="M14 20a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2"></path></g>',
  },
  'lucide-brush-cleaning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 22-1-4"></path><path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1"></path><path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z"></path><path d="m8 22 1-4"></path></g>',
  },
  'lucide-brush': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 10 3 3"></path><path d="M6.5 21A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z"></path><path d="M9.969 17.031 21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031"></path></g>',
  },
  'lucide-bubbles': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.2 14.8a2 2 0 0 1 2 2"></path><circle cx="18.5" cy="8.5" r="3.5"></circle><circle cx="7.5" cy="16.5" r="5.5"></circle><circle cx="7.5" cy="4.5" r="2.5"></circle></g>',
  },
  'lucide-bug-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 7.13V6a3 3 0 0 0-5.14-2.1L8 2"></path><path d="M14.12 3.88 16 2"></path><path d="M22 13h-4v-2a4 4 0 0 0-4-4h-1.3"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="m2 2 20 20"></path><path d="M7.7 7.7A4 4 0 0 0 6 11v3a6 6 0 0 0 11.13 3.13"></path><path d="M12 20v-8"></path><path d="M6 13H2"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path></g>',
  },
  'lucide-bug-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.765 21.522a.5.5 0 0 1-.765-.424v-8.196a.5.5 0 0 1 .765-.424l5.878 3.674a1 1 0 0 1 0 1.696z"></path><path d="M14.12 3.88 16 2"></path><path d="M18 11a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4v3a6.1 6.1 0 0 0 2 4.5"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path><path d="M6 13H2"></path><path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path><path d="m8 2 1.88 1.88"></path><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path></g>',
  },
  'lucide-bug': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m8 2 1.88 1.88"></path><path d="M14.12 3.88 16 2"></path><path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"></path><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6"></path><path d="M12 20v-9"></path><path d="M6.53 9C4.6 8.8 3 7.1 3 5"></path><path d="M6 13H2"></path><path d="M3 21c0-2.1 1.7-3.9 3.8-4"></path><path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"></path><path d="M22 13h-4"></path><path d="M17.2 17c2.1.1 3.8 1.9 3.8 4"></path></g>',
  },
  'lucide-building-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path><path d="M10 6h4"></path><path d="M10 10h4"></path><path d="M10 14h4"></path><path d="M10 18h4"></path></g>',
  },
  'lucide-building': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></g>',
  },
  'lucide-bus-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6 2 7"></path><path d="M10 6h4"></path><path d="m22 7-2-1"></path><rect width="16" height="16" x="4" y="3" rx="2"></rect><path d="M4 11h16"></path><path d="M8 15h.01"></path><path d="M16 15h.01"></path><path d="M6 19v2"></path><path d="M18 21v-2"></path></g>',
  },
  'lucide-bus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"></path><path d="M15 6v6"></path><path d="M2 12h19.6"></path><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"></path><circle cx="7" cy="18" r="2"></circle><path d="M9 18h5"></path><circle cx="16" cy="18" r="2"></circle></g>',
  },
  'lucide-cable-car': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3h.01"></path><path d="M14 2h.01"></path><path d="m2 9 20-5"></path><path d="M12 12V6.5"></path><rect width="16" height="10" x="4" y="12" rx="3"></rect><path d="M9 12v5"></path><path d="M15 12v5"></path><path d="M4 17h16"></path></g>',
  },
  'lucide-cable': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1"></path><path d="M19 15V6.5a1 1 0 0 0-7 0v11a1 1 0 0 1-7 0V9"></path><path d="M21 21v-2h-4"></path><path d="M3 5h4V3"></path><path d="M7 5a1 1 0 0 1 1 1v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a1 1 0 0 1 1-1V3"></path></g>',
  },
  'lucide-cake-slice': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="2"></circle><path d="M7.2 7.9 3 11v9c0 .6.4 1 1 1h16c.6 0 1-.4 1-1v-9c0-2-3-6-7-8l-3.6 2.6"></path><path d="M16 13H3"></path><path d="M16 17H3"></path></g>',
  },
  'lucide-cake': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"></path><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1"></path><path d="M2 21h20"></path><path d="M7 8v3"></path><path d="M12 8v3"></path><path d="M17 8v3"></path><path d="M7 4h.01"></path><path d="M12 4h.01"></path><path d="M17 4h.01"></path></g>',
  },
  'lucide-calculator': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2"></rect><line x1="8" x2="16" y1="6" y2="6"></line><line x1="16" x2="16" y1="14" y2="18"></line><path d="M16 10h.01"></path><path d="M12 10h.01"></path><path d="M8 10h.01"></path><path d="M12 14h.01"></path><path d="M8 14h.01"></path><path d="M12 18h.01"></path><path d="M8 18h.01"></path></g>',
  },
  'lucide-calendar-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 14h1v4"></path><path d="M16 2v4"></path><path d="M3 10h18"></path><path d="M8 2v4"></path><rect x="3" y="4" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-calendar-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14 18 4 4 4-4"></path><path d="M16 2v4"></path><path d="M18 14v8"></path><path d="M21 11.354V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7.343"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></g>',
  },
  'lucide-calendar-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14 18 4-4 4 4"></path><path d="M16 2v4"></path><path d="M18 22v-8"></path><path d="M21 11.343V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></g>',
  },
  'lucide-calendar-check-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><path d="M21 14V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8"></path><path d="M3 10h18"></path><path d="m16 20 2 2 4-4"></path></g>',
  },
  'lucide-calendar-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path><path d="m9 16 2 2 4-4"></path></g>',
  },
  'lucide-calendar-clock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5"></path><path d="M16 2v4"></path><path d="M8 2v4"></path><path d="M3 10h5"></path><path d="M17.5 17.5 16 16.3V14"></path><circle cx="16" cy="16" r="6"></circle></g>',
  },
  'lucide-calendar-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15.228 16.852-.923-.383"></path><path d="m15.228 19.148-.923.383"></path><path d="M16 2v4"></path><path d="m16.47 14.305.382.923"></path><path d="m16.852 20.772-.383.924"></path><path d="m19.148 15.228.383-.923"></path><path d="m19.53 21.696-.382-.924"></path><path d="m20.772 16.852.924-.383"></path><path d="m20.772 19.148.924.383"></path><path d="M21 11V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"></path><path d="M3 10h18"></path><path d="M8 2v4"></path><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-calendar-days': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path><path d="M8 14h.01"></path><path d="M12 14h.01"></path><path d="M16 14h.01"></path><path d="M8 18h.01"></path><path d="M12 18h.01"></path><path d="M16 18h.01"></path></g>',
  },
  'lucide-calendar-fold': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><path d="M21 17V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11Z"></path><path d="M3 10h18"></path><path d="M15 22v-4a2 2 0 0 1 2-2h4"></path></g>',
  },
  'lucide-calendar-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h18V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7"></path><path d="M8 2v4"></path><path d="M16 2v4"></path><path d="M21.29 14.7a2.43 2.43 0 0 0-2.65-.52c-.3.12-.57.3-.8.53l-.34.34-.35-.34a2.43 2.43 0 0 0-2.65-.53c-.3.12-.56.3-.79.53-.95.94-1 2.53.2 3.74L17.5 22l3.6-3.55c1.2-1.21 1.14-2.8.19-3.74Z"></path></g>',
  },
  'lucide-calendar-minus-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path><path d="M10 16h4"></path></g>',
  },
  'lucide-calendar-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 19h6"></path><path d="M16 2v4"></path><path d="M21 15V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8.5"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></g>',
  },
  'lucide-calendar-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.2 4.2A2 2 0 0 0 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 1.82-1.18"></path><path d="M21 15.5V6a2 2 0 0 0-2-2H9.5"></path><path d="M16 2v4"></path><path d="M3 10h7"></path><path d="M21 10h-5.5"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-calendar-plus-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path><path d="M10 16h4"></path><path d="M12 14v4"></path></g>',
  },
  'lucide-calendar-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 19h6"></path><path d="M16 2v4"></path><path d="M19 16v6"></path><path d="M21 12.598V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8.5"></path><path d="M3 10h18"></path><path d="M8 2v4"></path></g>',
  },
  'lucide-calendar-range': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M16 2v4"></path><path d="M3 10h18"></path><path d="M8 2v4"></path><path d="M17 14h-6"></path><path d="M13 18H7"></path><path d="M7 14h.01"></path><path d="M17 18h.01"></path></g>',
  },
  'lucide-calendar-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 2v4"></path><path d="M21 11.75V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7.25"></path><path d="m22 22-1.875-1.875"></path><path d="M3 10h18"></path><path d="M8 2v4"></path><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-calendar-sync': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 10v4h4"></path><path d="m11 14 1.535-1.605a5 5 0 0 1 8 1.5"></path><path d="M16 2v4"></path><path d="m21 18-1.535 1.605a5 5 0 0 1-8-1.5"></path><path d="M21 22v-4h-4"></path><path d="M21 8.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4.3"></path><path d="M3 10h4"></path><path d="M8 2v4"></path></g>',
  },
  'lucide-calendar-x-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><path d="M21 13V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8"></path><path d="M3 10h18"></path><path d="m17 22 5-5"></path><path d="m17 17 5 5"></path></g>',
  },
  'lucide-calendar-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path><path d="m14 14-4 4"></path><path d="m10 14 4 4"></path></g>',
  },
  'lucide-calendar': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></g>',
  },
  'lucide-camera-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"></line><path d="M7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16"></path><path d="M9.5 4h5L17 7h3a2 2 0 0 1 2 2v7.5"></path><path d="M14.121 15.121A3 3 0 1 1 9.88 10.88"></path></g>',
  },
  'lucide-camera': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path><circle cx="12" cy="13" r="3"></circle></g>',
  },
  'lucide-candy-cane': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.7 21a2 2 0 0 1-3.5-2l8.6-14a6 6 0 0 1 10.4 6 2 2 0 1 1-3.464-2 2 2 0 1 0-3.464-2Z"></path><path d="M17.75 7 15 2.1"></path><path d="M10.9 4.8 13 9"></path><path d="m7.9 9.7 2 4.4"></path><path d="M4.9 14.7 7 18.9"></path></g>',
  },
  'lucide-candy-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10v7.9"></path><path d="M11.802 6.145a5 5 0 0 1 6.053 6.053"></path><path d="M14 6.1v2.243"></path><path d="m15.5 15.571-.964.964a5 5 0 0 1-7.071 0 5 5 0 0 1 0-7.07l.964-.965"></path><path d="M16 7V3a1 1 0 0 1 1.707-.707 2.5 2.5 0 0 0 2.152.717 1 1 0 0 1 1.131 1.131 2.5 2.5 0 0 0 .717 2.152A1 1 0 0 1 21 8h-4"></path><path d="m2 2 20 20"></path><path d="M8 17v4a1 1 0 0 1-1.707.707 2.5 2.5 0 0 0-2.152-.717 1 1 0 0 1-1.131-1.131 2.5 2.5 0 0 0-.717-2.152A1 1 0 0 1 3 16h4"></path></g>',
  },
  'lucide-candy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 7v10.9"></path><path d="M14 6.1V17"></path><path d="M16 7V3a1 1 0 0 1 1.707-.707 2.5 2.5 0 0 0 2.152.717 1 1 0 0 1 1.131 1.131 2.5 2.5 0 0 0 .717 2.152A1 1 0 0 1 21 8h-4"></path><path d="M16.536 7.465a5 5 0 0 0-7.072 0l-2 2a5 5 0 0 0 0 7.07 5 5 0 0 0 7.072 0l2-2a5 5 0 0 0 0-7.07"></path><path d="M8 17v4a1 1 0 0 1-1.707.707 2.5 2.5 0 0 0-2.152-.717 1 1 0 0 1-1.131-1.131 2.5 2.5 0 0 0-.717-2.152A1 1 0 0 1 3 16h4"></path></g>',
  },
  'lucide-cannabis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-4"></path><path d="M7 12c-1.5 0-4.5 1.5-5 3 3.5 1.5 6 1 6 1-1.5 1.5-2 3.5-2 5 2.5 0 4.5-1.5 6-3 1.5 1.5 3.5 3 6 3 0-1.5-.5-3.5-2-5 0 0 2.5.5 6-1-.5-1.5-3.5-3-5-3 1.5-1 4-4 4-6-2.5 0-5.5 1.5-7 3 0-2.5-.5-5-2-7-1.5 2-2 4.5-2 7-1.5-1.5-4.5-3-7-3 0 2 2.5 5 4 6"></path></g>',
  },
  'lucide-captions-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 5H19a2 2 0 0 1 2 2v8.5"></path><path d="M17 11h-.5"></path><path d="M19 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2"></path><path d="m2 2 20 20"></path><path d="M7 11h4"></path><path d="M7 15h2.5"></path></g>',
  },
  'lucide-captions': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="14" x="3" y="5" rx="2" ry="2"></rect><path d="M7 15h4M15 15h2M7 11h2M13 11h4"></path></g>',
  },
  'lucide-car-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"></path><path d="M7 14h.01"></path><path d="M17 14h.01"></path><rect width="18" height="8" x="3" y="10" rx="2"></rect><path d="M5 18v2"></path><path d="M19 18v2"></path></g>',
  },
  'lucide-car-taxi-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2h4"></path><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8"></path><path d="M7 14h.01"></path><path d="M17 14h.01"></path><rect width="18" height="8" x="3" y="10" rx="2"></rect><path d="M5 18v2"></path><path d="M19 18v2"></path></g>',
  },
  'lucide-car': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path><circle cx="7" cy="17" r="2"></circle><path d="M9 17h6"></path><circle cx="17" cy="17" r="2"></circle></g>',
  },
  'lucide-caravan': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 19V9a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v8a2 2 0 0 0 2 2h2"></path><path d="M2 9h3a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2"></path><path d="M22 17v1a1 1 0 0 1-1 1H10v-9a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v9"></path><circle cx="8" cy="19" r="2"></circle></g>',
  },
  'lucide-carrot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.27 21.7s9.87-3.5 12.73-6.36a4.5 4.5 0 0 0-6.36-6.37C5.77 11.84 2.27 21.7 2.27 21.7zM8.64 14l-2.05-2.04M15.34 15l-2.46-2.46"></path><path d="M22 9s-1.33-2-3.5-2C16.86 7 15 9 15 9s1.33 2 3.5 2S22 9 22 9z"></path><path d="M15 2s-2 1.33-2 3.5S15 9 15 9s2-1.84 2-3.5C17 3.33 15 2 15 2z"></path></g>',
  },
  'lucide-case-lower': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="12" r="3"></circle><path d="M10 9v6"></path><circle cx="17" cy="12" r="3"></circle><path d="M14 7v8"></path></g>',
  },
  'lucide-case-sensitive': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 15 4-8 4 8"></path><path d="M4 13h6"></path><circle cx="18" cy="12" r="3"></circle><path d="M21 9v6"></path></g>',
  },
  'lucide-case-upper': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 15 4-8 4 8"></path><path d="M4 13h6"></path><path d="M15 11h4.5a2 2 0 0 1 0 4H15V7h4a2 2 0 0 1 0 4"></path></g>',
  },
  'lucide-cassette-tape': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><circle cx="8" cy="10" r="2"></circle><path d="M8 12h8"></path><circle cx="16" cy="10" r="2"></circle><path d="m6 20 .7-2.9A1.4 1.4 0 0 1 8.1 16h7.8a1.4 1.4 0 0 1 1.4 1l.7 3"></path></g>',
  },
  'lucide-cast': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6"></path><path d="M2 12a9 9 0 0 1 8 8"></path><path d="M2 16a5 5 0 0 1 4 4"></path><line x1="2" x2="2.01" y1="20" y2="20"></line></g>',
  },
  'lucide-castle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 20v-9H2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z"></path><path d="M18 11V4H6v7"></path><path d="M15 22v-4a3 3 0 0 0-3-3a3 3 0 0 0-3 3v4"></path><path d="M22 11V9"></path><path d="M2 11V9"></path><path d="M6 4V2"></path><path d="M18 4V2"></path><path d="M10 4V2"></path><path d="M14 4V2"></path></g>',
  },
  'lucide-cat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"></path><path d="M8 14v.5"></path><path d="M16 14v.5"></path><path d="M11.25 16.25h1.5L12 17l-.75-.75Z"></path></g>',
  },
  'lucide-cctv': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.75 12h3.632a1 1 0 0 1 .894 1.447l-2.034 4.069a1 1 0 0 1-1.708.134l-2.124-2.97"></path><path d="M17.106 9.053a1 1 0 0 1 .447 1.341l-3.106 6.211a1 1 0 0 1-1.342.447L3.61 12.3a2.92 2.92 0 0 1-1.3-3.91L3.69 5.6a2.92 2.92 0 0 1 3.92-1.3z"></path><path d="M2 19h3.76a2 2 0 0 0 1.8-1.1L9 15"></path><path d="M2 21v-4"></path><path d="M7 9h.01"></path></g>',
  },
  'lucide-chart-area': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M7 11.207a.5.5 0 0 1 .146-.353l2-2a.5.5 0 0 1 .708 0l3.292 3.292a.5.5 0 0 0 .708 0l4.292-4.292a.5.5 0 0 1 .854.353V16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1z"></path></g>',
  },
  'lucide-chart-bar-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><rect x="7" y="13" width="9" height="4" rx="1"></rect><rect x="7" y="5" width="12" height="4" rx="1"></rect></g>',
  },
  'lucide-chart-bar-decreasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M7 11h8"></path><path d="M7 16h3"></path><path d="M7 6h12"></path></g>',
  },
  'lucide-chart-bar-increasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M7 11h8"></path><path d="M7 16h12"></path><path d="M7 6h3"></path></g>',
  },
  'lucide-chart-bar-stacked': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 13v4"></path><path d="M15 5v4"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><rect x="7" y="13" width="9" height="4" rx="1"></rect><rect x="7" y="5" width="12" height="4" rx="1"></rect></g>',
  },
  'lucide-chart-bar': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M7 16h8"></path><path d="M7 11h12"></path><path d="M7 6h3"></path></g>',
  },
  'lucide-chart-candlestick': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5v4"></path><rect width="4" height="6" x="7" y="9" rx="1"></rect><path d="M9 15v2"></path><path d="M17 3v2"></path><rect width="4" height="8" x="15" y="5" rx="1"></rect><path d="M17 13v3"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path></g>',
  },
  'lucide-chart-column-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><rect x="15" y="5" width="4" height="12" rx="1"></rect><rect x="7" y="8" width="4" height="9" rx="1"></rect></g>',
  },
  'lucide-chart-column-decreasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 17V9"></path><path d="M18 17v-3"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M8 17V5"></path></g>',
  },
  'lucide-chart-column-increasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 17V9"></path><path d="M18 17V5"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M8 17v-3"></path></g>',
  },
  'lucide-chart-column-stacked': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 13H7"></path><path d="M19 9h-4"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><rect x="15" y="5" width="4" height="12" rx="1"></rect><rect x="7" y="8" width="4" height="9" rx="1"></rect></g>',
  },
  'lucide-chart-column': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M18 17V9"></path><path d="M13 17V5"></path><path d="M8 17v-3"></path></g>',
  },
  'lucide-chart-gantt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 6h8"></path><path d="M12 16h6"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M8 11h7"></path></g>',
  },
  'lucide-chart-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="m19 9-5 5-4-4-3 3"></path></g>',
  },
  'lucide-chart-network': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13.11 7.664 1.78 2.672"></path><path d="m14.162 12.788-3.324 1.424"></path><path d="m20 4-6.06 1.515"></path><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><circle cx="12" cy="6" r="2"></circle><circle cx="16" cy="12" r="2"></circle><circle cx="9" cy="15" r="2"></circle></g>',
  },
  'lucide-chart-no-axes-column-decreasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20V10"></path><path d="M18 20v-4"></path><path d="M6 20V4"></path></g>',
  },
  'lucide-chart-no-axes-column-increasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="20" y2="10"></line><line x1="18" x2="18" y1="20" y2="4"></line><line x1="6" x2="6" y1="20" y2="16"></line></g>',
  },
  'lucide-chart-no-axes-column': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="18" y1="20" y2="10"></line><line x1="12" x2="12" y1="20" y2="4"></line><line x1="6" x2="6" y1="20" y2="14"></line></g>',
  },
  'lucide-chart-no-axes-combined': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16v5"></path><path d="M16 14v7"></path><path d="M20 10v11"></path><path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15"></path><path d="M4 18v3"></path><path d="M8 14v7"></path></g>',
  },
  'lucide-chart-no-axes-gantt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6h10"></path><path d="M6 12h9"></path><path d="M11 18h7"></path></g>',
  },
  'lucide-chart-pie': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z"></path><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path></g>',
  },
  'lucide-chart-scatter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle><circle cx="18.5" cy="5.5" r=".5" fill="currentColor"></circle><circle cx="11.5" cy="11.5" r=".5" fill="currentColor"></circle><circle cx="7.5" cy="16.5" r=".5" fill="currentColor"></circle><circle cx="17.5" cy="14.5" r=".5" fill="currentColor"></circle><path d="M3 3v16a2 2 0 0 0 2 2h16"></path></g>',
  },
  'lucide-chart-spline': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v16a2 2 0 0 0 2 2h16"></path><path d="M7 16c.5-2 1.5-7 4-7 2 0 2 3 4 3 2.5 0 4.5-5 5-7"></path></g>',
  },
  'lucide-check-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 7 17l-5-5"></path><path d="m22 10-7.5 7.5L13 16"></path></g>',
  },
  'lucide-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></g>',
  },
  'lucide-chef-hat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z"></path><path d="M6 17h12"></path></g>',
  },
  'lucide-cherry': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3-2.5-2-5 .24-5 3Z"></path><path d="M12 17a5 5 0 0 0 10 0c0-2.76-2.5-5-5-3-2.5-2-5 .24-5 3Z"></path><path d="M7 14c3.22-2.91 4.29-8.75 5-12 1.66 2.38 4.94 9 5 12"></path><path d="M22 9c-4.29 0-7.14-2.33-10-7 5.71 0 10 4.67 10 7Z"></path></g>',
  },
  'lucide-chevron-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"></path></g>',
  },
  'lucide-chevron-first': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 18-6-6 6-6"></path><path d="M7 6v12"></path></g>',
  },
  'lucide-chevron-last': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 18 6-6-6-6"></path><path d="M17 6v12"></path></g>',
  },
  'lucide-chevron-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></g>',
  },
  'lucide-chevron-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></g>',
  },
  'lucide-chevron-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"></path></g>',
  },
  'lucide-chevrons-down-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 20 5-5 5 5"></path><path d="m7 4 5 5 5-5"></path></g>',
  },
  'lucide-chevrons-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 6 5 5 5-5"></path><path d="m7 13 5 5 5-5"></path></g>',
  },
  'lucide-chevrons-left-right-ellipsis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 8 4 4-4 4"></path><path d="m6 8-4 4 4 4"></path><path d="M8 12h.01"></path><path d="M12 12h.01"></path><path d="M16 12h.01"></path></g>',
  },
  'lucide-chevrons-left-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 7-5 5 5 5"></path><path d="m15 7 5 5-5 5"></path></g>',
  },
  'lucide-chevrons-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 17-5-5 5-5"></path><path d="m18 17-5-5 5-5"></path></g>',
  },
  'lucide-chevrons-right-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 17-5-5 5-5"></path><path d="m4 17 5-5-5-5"></path></g>',
  },
  'lucide-chevrons-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 17 5-5-5-5"></path><path d="m13 17 5-5-5-5"></path></g>',
  },
  'lucide-chevrons-up-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 15 5 5 5-5"></path><path d="m7 9 5-5 5 5"></path></g>',
  },
  'lucide-chevrons-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 11-5-5-5 5"></path><path d="m17 18-5-5-5 5"></path></g>',
  },
  'lucide-chrome': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="4"></circle><line x1="21.17" x2="12" y1="8" y2="8"></line><line x1="3.95" x2="8.54" y1="6.06" y2="14"></line><line x1="10.88" x2="15.46" y1="21.94" y2="14"></line></g>',
  },
  'lucide-church': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9h4"></path><path d="M12 7v5"></path><path d="M14 22v-4a2 2 0 0 0-4 0v4"></path><path d="M18 22V5.618a1 1 0 0 0-.553-.894l-4.553-2.277a2 2 0 0 0-1.788 0L6.553 4.724A1 1 0 0 0 6 5.618V22"></path><path d="m18 7 3.447 1.724a1 1 0 0 1 .553.894V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9.618a1 1 0 0 1 .553-.894L6 7"></path></g>',
  },
  'lucide-cigarette-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h13"></path><path d="M18 8c0-2.5-2-2.5-2-5"></path><path d="m2 2 20 20"></path><path d="M21 12a1 1 0 0 1 1 1v2a1 1 0 0 1-.5.866"></path><path d="M22 8c0-2.5-2-2.5-2-5"></path><path d="M7 12v4"></path></g>',
  },
  'lucide-cigarette': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14"></path><path d="M18 8c0-2.5-2-2.5-2-5"></path><path d="M21 16a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path><path d="M22 8c0-2.5-2-2.5-2-5"></path><path d="M7 12v4"></path></g>',
  },
  'lucide-circle-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></g>',
  },
  'lucide-circle-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v8"></path><path d="m8 12 4 4 4-4"></path></g>',
  },
  'lucide-circle-arrow-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 12H8"></path><path d="m12 8-4 4 4 4"></path></g>',
  },
  'lucide-circle-arrow-out-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12a10 10 0 1 1 10 10"></path><path d="m2 22 10-10"></path><path d="M8 22H2v-6"></path></g>',
  },
  'lucide-circle-arrow-out-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22a10 10 0 1 1 10-10"></path><path d="M22 22 12 12"></path><path d="M22 16v6h-6"></path></g>',
  },
  'lucide-circle-arrow-out-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8V2h6"></path><path d="m2 2 10 10"></path><path d="M12 2A10 10 0 1 1 2 12"></path></g>',
  },
  'lucide-circle-arrow-out-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12A10 10 0 1 1 12 2"></path><path d="M22 2 12 12"></path><path d="M16 2h6v6"></path></g>',
  },
  'lucide-circle-arrow-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 12h8"></path><path d="m12 16 4-4-4-4"></path></g>',
  },
  'lucide-circle-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m16 12-4-4-4 4"></path><path d="M12 16V8"></path></g>',
  },
  'lucide-circle-check-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></g>',
  },
  'lucide-circle-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></g>',
  },
  'lucide-circle-chevron-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m16 10-4 4-4-4"></path></g>',
  },
  'lucide-circle-chevron-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m14 16-4-4 4-4"></path></g>',
  },
  'lucide-circle-chevron-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m10 8 4 4-4 4"></path></g>',
  },
  'lucide-circle-chevron-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m8 14 4-4 4 4"></path></g>',
  },
  'lucide-circle-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.1 2.182a10 10 0 0 1 3.8 0"></path><path d="M13.9 21.818a10 10 0 0 1-3.8 0"></path><path d="M17.609 3.721a10 10 0 0 1 2.69 2.7"></path><path d="M2.182 13.9a10 10 0 0 1 0-3.8"></path><path d="M20.279 17.609a10 10 0 0 1-2.7 2.69"></path><path d="M21.818 10.1a10 10 0 0 1 0 3.8"></path><path d="M3.721 6.391a10 10 0 0 1 2.7-2.69"></path><path d="M6.391 20.279a10 10 0 0 1-2.69-2.7"></path></g>',
  },
  'lucide-circle-divide': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="8" x2="16" y1="12" y2="12"></line><line x1="12" x2="12" y1="16" y2="16"></line><line x1="12" x2="12" y1="8" y2="8"></line><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circle-dollar-sign': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path><path d="M12 18V6"></path></g>',
  },
  'lucide-circle-dot-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.1 2.18a9.93 9.93 0 0 1 3.8 0"></path><path d="M17.6 3.71a9.95 9.95 0 0 1 2.69 2.7"></path><path d="M21.82 10.1a9.93 9.93 0 0 1 0 3.8"></path><path d="M20.29 17.6a9.95 9.95 0 0 1-2.7 2.69"></path><path d="M13.9 21.82a9.94 9.94 0 0 1-3.8 0"></path><path d="M6.4 20.29a9.95 9.95 0 0 1-2.69-2.7"></path><path d="M2.18 13.9a9.93 9.93 0 0 1 0-3.8"></path><path d="M3.71 6.4a9.95 9.95 0 0 1 2.7-2.69"></path><circle cx="12" cy="12" r="1"></circle></g>',
  },
  'lucide-circle-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="1"></circle></g>',
  },
  'lucide-circle-ellipsis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M17 12h.01"></path><path d="M12 12h.01"></path><path d="M7 12h.01"></path></g>',
  },
  'lucide-circle-equal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10h10"></path><path d="M7 14h10"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circle-fading-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 0 1 7.38 16.75"></path><path d="m16 12-4-4-4 4"></path><path d="M12 16V8"></path><path d="M2.5 8.875a10 10 0 0 0-.5 3"></path><path d="M2.83 16a10 10 0 0 0 2.43 3.4"></path><path d="M4.636 5.235a10 10 0 0 1 .891-.857"></path><path d="M8.644 21.42a10 10 0 0 0 7.631-.38"></path></g>',
  },
  'lucide-circle-fading-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 0 1 7.38 16.75"></path><path d="M12 8v8"></path><path d="M16 12H8"></path><path d="M2.5 8.875a10 10 0 0 0-.5 3"></path><path d="M2.83 16a10 10 0 0 0 2.43 3.4"></path><path d="M4.636 5.235a10 10 0 0 1 .891-.857"></path><path d="M8.644 21.42a10 10 0 0 0 7.631-.38"></path></g>',
  },
  'lucide-circle-gauge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.6 2.7a10 10 0 1 0 5.7 5.7"></path><circle cx="12" cy="12" r="2"></circle><path d="M13.4 10.6 19 5"></path></g>',
  },
  'lucide-circle-help': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></g>',
  },
  'lucide-circle-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 12h8"></path></g>',
  },
  'lucide-circle-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 2 20 20"></path><path d="M8.35 2.69A10 10 0 0 1 21.3 15.65"></path><path d="M19.08 19.08A10 10 0 1 1 4.92 4.92"></path></g>',
  },
  'lucide-circle-parking-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m5 5 14 14"></path><path d="M13 13a3 3 0 1 0 0-6H9v2"></path><path d="M9 17v-2.34"></path></g>',
  },
  'lucide-circle-parking': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9 17V7h4a3 3 0 0 1 0 6H9"></path></g>',
  },
  'lucide-circle-pause': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="10" x2="10" y1="15" y2="9"></line><line x1="14" x2="14" y1="15" y2="9"></line></g>',
  },
  'lucide-circle-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="M9 9h.01"></path><path d="M15 15h.01"></path></g>',
  },
  'lucide-circle-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon></g>',
  },
  'lucide-circle-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 12h8"></path><path d="M12 8v8"></path></g>',
  },
  'lucide-circle-power': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v4"></path><path d="M7.998 9.003a5 5 0 1 0 8-.005"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circle-slash-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 2 22"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circle-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="9" x2="15" y1="15" y2="9"></line></g>',
  },
  'lucide-circle-small': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="6"></circle></g>',
  },
  'lucide-circle-stop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><rect x="9" y="9" width="6" height="6" rx="1"></rect></g>',
  },
  'lucide-circle-user-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 20a6 6 0 0 0-12 0"></path><circle cx="12" cy="10" r="4"></circle><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circle-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="10" r="3"></circle><path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"></path></g>',
  },
  'lucide-circle-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></g>',
  },
  'lucide-circle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-circuit-board': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M11 9h4a2 2 0 0 0 2-2V3"></path><circle cx="9" cy="9" r="2"></circle><path d="M7 21v-4a2 2 0 0 1 2-2h4"></path><circle cx="15" cy="15" r="2"></circle></g>',
  },
  'lucide-citrus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.66 17.67a1.08 1.08 0 0 1-.04 1.6A12 12 0 0 1 4.73 2.38a1.1 1.1 0 0 1 1.61-.04z"></path><path d="M19.65 15.66A8 8 0 0 1 8.35 4.34"></path><path d="m14 10-5.5 5.5"></path><path d="M14 17.85V10H6.15"></path></g>',
  },
  'lucide-clapperboard': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Z"></path><path d="m6.2 5.3 3.1 3.9"></path><path d="m12.4 3.4 3.1 4"></path><path d="M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"></path></g>',
  },
  'lucide-clipboard-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="m9 14 2 2 4-4"></path></g>',
  },
  'lucide-clipboard-copy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path><path d="M16 4h2a2 2 0 0 1 2 2v4"></path><path d="M21 14H11"></path><path d="m15 10-4 4 4 4"></path></g>',
  },
  'lucide-clipboard-list': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></g>',
  },
  'lucide-clipboard-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M9 14h6"></path></g>',
  },
  'lucide-clipboard-paste': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 14h10"></path><path d="M16 4h2a2 2 0 0 1 2 2v1.344"></path><path d="m17 18 4-4-4-4"></path><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 1.793-1.113"></path><rect x="8" y="2" width="8" height="4" rx="1"></rect></g>',
  },
  'lucide-clipboard-pen-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1"></rect><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-.5"></path><path d="M16 4h2a2 2 0 0 1 1.73 1"></path><path d="M8 18h1"></path><path d="M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path></g>',
  },
  'lucide-clipboard-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5.5"></path><path d="M4 13.5V6a2 2 0 0 1 2-2h2"></path><path d="M13.378 15.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path></g>',
  },
  'lucide-clipboard-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M9 14h6"></path><path d="M12 17v-6"></path></g>',
  },
  'lucide-clipboard-type': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M9 12v-1h6v1"></path><path d="M11 17h2"></path><path d="M12 11v6"></path></g>',
  },
  'lucide-clipboard-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="m15 11-6 6"></path><path d="m9 11 6 6"></path></g>',
  },
  'lucide-clipboard': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></g>',
  },
  'lucide-clock-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 14.5 8"></polyline></g>',
  },
  'lucide-clock-10': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 8 10"></polyline></g>',
  },
  'lucide-clock-11': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 9.5 8"></polyline></g>',
  },
  'lucide-clock-12': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12"></polyline></g>',
  },
  'lucide-clock-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 10"></polyline></g>',
  },
  'lucide-clock-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16.5 12"></polyline></g>',
  },
  'lucide-clock-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></g>',
  },
  'lucide-clock-5': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 14.5 16"></polyline></g>',
  },
  'lucide-clock-6': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 12 16.5"></polyline></g>',
  },
  'lucide-clock-7': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 9.5 16"></polyline></g>',
  },
  'lucide-clock-8': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 8 14"></polyline></g>',
  },
  'lucide-clock-9': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 7.5 12"></polyline></g>',
  },
  'lucide-clock-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v6l4 2"></path><path d="M16 21.16a10 10 0 1 1 5-13.516"></path><path d="M20 11.5v6"></path><path d="M20 21.5h.01"></path></g>',
  },
  'lucide-clock-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.338 21.994A10 10 0 1 1 21.925 13.227"></path><path d="M12 6v6l2 1"></path><path d="m14 18 4 4 4-4"></path><path d="M18 14v8"></path></g>',
  },
  'lucide-clock-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.228 21.925A10 10 0 1 1 21.994 12.338"></path><path d="M12 6v6l1.562.781"></path><path d="m14 18 4-4 4 4"></path><path d="M18 22v-8"></path></g>',
  },
  'lucide-clock-fading': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 0 1 7.38 16.75"></path><path d="M12 6v6l4 2"></path><path d="M2.5 8.875a10 10 0 0 0-.5 3"></path><path d="M2.83 16a10 10 0 0 0 2.43 3.4"></path><path d="M4.636 5.235a10 10 0 0 1 .891-.857"></path><path d="M8.644 21.42a10 10 0 0 0 7.631-.38"></path></g>',
  },
  'lucide-clock-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v6l3.644 1.822"></path><path d="M16 19h6"></path><path d="M19 16v6"></path><path d="M21.92 13.267a10 10 0 1 0-8.653 8.653"></path></g>',
  },
  'lucide-cloud-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12v4"></path><path d="M12 20h.01"></path><path d="M17 18h.5a1 1 0 0 0 0-9h-1.79A7 7 0 1 0 7 17.708"></path></g>',
  },
  'lucide-cloud-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10.852 19.772-.383.924"></path><path d="m13.148 14.228.383-.923"></path><path d="M13.148 19.772a3 3 0 1 0-2.296-5.544l-.383-.923"></path><path d="m13.53 20.696-.382-.924a3 3 0 1 1-2.296-5.544"></path><path d="m14.772 15.852.923-.383"></path><path d="m14.772 18.148.923.383"></path><path d="M4.2 15.1a7 7 0 1 1 9.93-9.858A7 7 0 0 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.2"></path><path d="m9.228 15.852-.923-.383"></path><path d="m9.228 18.148-.923.383"></path></g>',
  },
  'lucide-cloud-download': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13v8l-4-4"></path><path d="m12 21 4-4"></path><path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284"></path></g>',
  },
  'lucide-cloud-drizzle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M8 19v1"></path><path d="M8 14v1"></path><path d="M16 19v1"></path><path d="M16 14v1"></path><path d="M12 21v1"></path><path d="M12 16v1"></path></g>',
  },
  'lucide-cloud-fog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M16 17H7"></path><path d="M17 21H9"></path></g>',
  },
  'lucide-cloud-hail': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M16 14v2"></path><path d="M8 14v2"></path><path d="M16 20h.01"></path><path d="M8 20h.01"></path><path d="M12 16v2"></path><path d="M12 22h.01"></path></g>',
  },
  'lucide-cloud-lightning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 16.326A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.973"></path><path d="m13 12-3 5h4l-3 5"></path></g>',
  },
  'lucide-cloud-moon-rain': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.188 8.5A6 6 0 0 1 16 4a1 1 0 0 0 6 6 6 6 0 0 1-3 5.197"></path><path d="M11 20v2"></path><path d="M3 20a5 5 0 1 1 8.9-4H13a3 3 0 0 1 2 5.24"></path><path d="M7 19v2"></path></g>',
  },
  'lucide-cloud-moon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.188 8.5A6 6 0 0 1 16 4a1 1 0 0 0 6 6 6 6 0 0 1-3 5.197"></path><path d="M13 16a3 3 0 1 1 0 6H7a5 5 0 1 1 4.9-6Z"></path></g>',
  },
  'lucide-cloud-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 2 20 20"></path><path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193"></path><path d="M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07"></path></g>',
  },
  'lucide-cloud-rain-wind': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="m9.2 22 3-7"></path><path d="m9 13-3 7"></path><path d="m17 13-3 7"></path></g>',
  },
  'lucide-cloud-rain': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M16 14v6"></path><path d="M8 14v6"></path><path d="M12 16v6"></path></g>',
  },
  'lucide-cloud-snow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="M8 15h.01"></path><path d="M8 19h.01"></path><path d="M12 17h.01"></path><path d="M12 21h.01"></path><path d="M16 15h.01"></path><path d="M16 19h.01"></path></g>',
  },
  'lucide-cloud-sun-rain': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="M20 12h2"></path><path d="m19.07 4.93-1.41 1.41"></path><path d="M15.947 12.65a4 4 0 0 0-5.925-4.128"></path><path d="M3 20a5 5 0 1 1 8.9-4H13a3 3 0 0 1 2 5.24"></path><path d="M11 20v2"></path><path d="M7 19v2"></path></g>',
  },
  'lucide-cloud-sun': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="M20 12h2"></path><path d="m19.07 4.93-1.41 1.41"></path><path d="M15.947 12.65a4 4 0 0 0-5.925-4.128"></path><path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z"></path></g>',
  },
  'lucide-cloud-upload': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13v8"></path><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"></path><path d="m8 17 4-4 4 4"></path></g>',
  },
  'lucide-cloud': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"></path></g>',
  },
  'lucide-cloudy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 21H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"></path><path d="M22 10a3 3 0 0 0-3-3h-2.207a5.502 5.502 0 0 0-10.702.5"></path></g>',
  },
  'lucide-clover': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.17 7.83 2 22"></path><path d="M4.02 12a2.827 2.827 0 1 1 3.81-4.17A2.827 2.827 0 1 1 12 4.02a2.827 2.827 0 1 1 4.17 3.81A2.827 2.827 0 1 1 19.98 12a2.827 2.827 0 1 1-3.81 4.17A2.827 2.827 0 1 1 12 19.98a2.827 2.827 0 1 1-4.17-3.81A1 1 0 1 1 4 12"></path><path d="m7.83 7.83 8.34 8.34"></path></g>',
  },
  'lucide-club': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.28 9.05a5.5 5.5 0 1 0-10.56 0A5.5 5.5 0 1 0 12 17.66a5.5 5.5 0 1 0 5.28-8.6Z"></path><path d="M12 17.66L12 22"></path></g>',
  },
  'lucide-code-xml': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 16 4-4-4-4"></path><path d="m6 8-4 4 4 4"></path><path d="m14.5 4-5 16"></path></g>',
  },
  'lucide-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></g>',
  },
  'lucide-codepen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"></polygon><line x1="12" x2="12" y1="22" y2="15.5"></line><polyline points="22 8.5 12 15.5 2 8.5"></polyline><polyline points="2 15.5 12 8.5 22 15.5"></polyline><line x1="12" x2="12" y1="2" y2="8.5"></line></g>',
  },
  'lucide-codesandbox': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="7.5 4.21 12 6.81 16.5 4.21"></polyline><polyline points="7.5 19.79 7.5 14.6 3 12"></polyline><polyline points="21 12 16.5 14.6 16.5 19.79"></polyline><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" x2="12" y1="22.08" y2="12"></line></g>',
  },
  'lucide-coffee': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v2"></path><path d="M14 2v2"></path><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1"></path><path d="M6 2v2"></path></g>',
  },
  'lucide-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"></path><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path><path d="M12 2v2"></path><path d="M12 22v-2"></path><path d="m17 20.66-1-1.73"></path><path d="M11 10.27 7 3.34"></path><path d="m20.66 17-1.73-1"></path><path d="m3.34 7 1.73 1"></path><path d="M14 12h8"></path><path d="M2 12h2"></path><path d="m20.66 7-1.73 1"></path><path d="m3.34 17 1.73-1"></path><path d="m17 3.34-1 1.73"></path><path d="m11 13.73-4 6.93"></path></g>',
  },
  'lucide-coins': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"></circle><path d="M18.09 10.37A6 6 0 1 1 10.34 18"></path><path d="M7 6h1v4"></path><path d="m16.71 13.88.7.71-2.82 2.82"></path></g>',
  },
  'lucide-columns-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M12 3v18"></path></g>',
  },
  'lucide-columns-3-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5"></path><path d="m14.3 19.6 1-.4"></path><path d="M15 3v7.5"></path><path d="m15.2 16.9-.9-.3"></path><path d="m16.6 21.7.3-.9"></path><path d="m16.8 15.3-.4-1"></path><path d="m19.1 15.2.3-.9"></path><path d="m19.6 21.7-.4-1"></path><path d="m20.7 16.8 1-.4"></path><path d="m21.7 19.4-.9-.3"></path><path d="M9 3v18"></path><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-columns-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 3v18"></path><path d="M15 3v18"></path></g>',
  },
  'lucide-columns-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7.5 3v18"></path><path d="M12 3v18"></path><path d="M16.5 3v18"></path></g>',
  },
  'lucide-combine': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 18H5a3 3 0 0 1-3-3v-1"></path><path d="M14 2a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2"></path><path d="M20 2a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2"></path><path d="m7 21 3-3-3-3"></path><rect x="14" y="14" width="8" height="8" rx="2"></rect><rect x="2" y="2" width="8" height="8" rx="2"></rect></g>',
  },
  'lucide-command': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3"></path></g>',
  },
  'lucide-compass': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-component': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.536 11.293a1 1 0 0 0 0 1.414l2.376 2.377a1 1 0 0 0 1.414 0l2.377-2.377a1 1 0 0 0 0-1.414l-2.377-2.377a1 1 0 0 0-1.414 0z"></path><path d="M2.297 11.293a1 1 0 0 0 0 1.414l2.377 2.377a1 1 0 0 0 1.414 0l2.377-2.377a1 1 0 0 0 0-1.414L6.088 8.916a1 1 0 0 0-1.414 0z"></path><path d="M8.916 17.912a1 1 0 0 0 0 1.415l2.377 2.376a1 1 0 0 0 1.414 0l2.377-2.376a1 1 0 0 0 0-1.415l-2.377-2.376a1 1 0 0 0-1.414 0z"></path><path d="M8.916 4.674a1 1 0 0 0 0 1.414l2.377 2.376a1 1 0 0 0 1.414 0l2.377-2.376a1 1 0 0 0 0-1.414l-2.377-2.377a1 1 0 0 0-1.414 0z"></path></g>',
  },
  'lucide-computer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="8" x="5" y="2" rx="2"></rect><rect width="20" height="8" x="2" y="14" rx="2"></rect><path d="M6 18h2"></path><path d="M12 18h6"></path></g>',
  },
  'lucide-concierge-bell': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1Z"></path><path d="M20 16a8 8 0 1 0-16 0"></path><path d="M12 4v4"></path><path d="M10 4h4"></path></g>',
  },
  'lucide-cone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20.9 18.55-8-15.98a1 1 0 0 0-1.8 0l-8 15.98"></path><ellipse cx="12" cy="19" rx="9" ry="3"></ellipse></g>',
  },
  'lucide-construction': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="8" rx="1"></rect><path d="M17 14v7"></path><path d="M7 14v7"></path><path d="M17 3v3"></path><path d="M7 3v3"></path><path d="M10 14 2.3 6.3"></path><path d="m14 6 7.7 7.7"></path><path d="m8 6 8 8"></path></g>',
  },
  'lucide-contact-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 2v2"></path><path d="M17.915 22a6 6 0 0 0-12 0"></path><path d="M8 2v2"></path><circle cx="12" cy="12" r="4"></circle><rect x="3" y="4" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-contact': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 2v2"></path><path d="M7 22v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"></path><path d="M8 2v2"></path><circle cx="12" cy="11" r="3"></circle><rect x="3" y="4" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-container': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 7.7c0-.6-.4-1.2-.8-1.5l-6.3-3.9a1.72 1.72 0 0 0-1.7 0l-10.3 6c-.5.2-.9.8-.9 1.4v6.6c0 .5.4 1.2.8 1.5l6.3 3.9a1.72 1.72 0 0 0 1.7 0l10.3-6c.5-.3.9-1 .9-1.5Z"></path><path d="M10 21.9V14L2.1 9.1"></path><path d="m10 14 11.9-6.9"></path><path d="M14 19.8v-8.1"></path><path d="M18 17.5V9.4"></path></g>',
  },
  'lucide-contrast': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 18a6 6 0 0 0 0-12v12z"></path></g>',
  },
  'lucide-cookie': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"></path><path d="M8.5 8.5v.01"></path><path d="M16 15.5v.01"></path><path d="M12 12v.01"></path><path d="M11 17v.01"></path><path d="M7 14v.01"></path></g>',
  },
  'lucide-cooking-pot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h20"></path><path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8"></path><path d="m4 8 16-4"></path><path d="m8.86 6.78-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8"></path></g>',
  },
  'lucide-copy-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 15 2 2 4-4"></path><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copy-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="18" y1="15" y2="15"></line><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copy-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="15" x2="15" y1="12" y2="18"></line><line x1="12" x2="18" y1="15" y2="15"></line><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copy-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="18" y1="18" y2="12"></line><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copy-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="18" y1="12" y2="18"></line><line x1="12" x2="18" y1="18" y2="12"></line><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></g>',
  },
  'lucide-copyleft': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.17 14.83a4 4 0 1 0 0-5.66"></path></g>',
  },
  'lucide-copyright': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M14.83 14.83a4 4 0 1 1 0-5.66"></path></g>',
  },
  'lucide-corner-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 10 4 15 9 20"></polyline><path d="M20 4v7a4 4 0 0 1-4 4H4"></path></g>',
  },
  'lucide-corner-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 10 20 15 15 20"></polyline><path d="M4 4v7a4 4 0 0 0 4 4h12"></path></g>',
  },
  'lucide-corner-left-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="14 15 9 20 4 15"></polyline><path d="M20 4h-7a4 4 0 0 0-4 4v12"></path></g>',
  },
  'lucide-corner-left-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="14 9 9 4 4 9"></polyline><path d="M20 20h-7a4 4 0 0 1-4-4V4"></path></g>',
  },
  'lucide-corner-right-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="10 15 15 20 20 15"></polyline><path d="M4 4h7a4 4 0 0 1 4 4v12"></path></g>',
  },
  'lucide-corner-right-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="10 9 15 4 20 9"></polyline><path d="M4 20h7a4 4 0 0 0 4-4V4"></path></g>',
  },
  'lucide-corner-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></g>',
  },
  'lucide-corner-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 14 20 9 15 4"></polyline><path d="M4 20v-7a4 4 0 0 1 4-4h12"></path></g>',
  },
  'lucide-cpu': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v2"></path><path d="M12 2v2"></path><path d="M17 20v2"></path><path d="M17 2v2"></path><path d="M2 12h2"></path><path d="M2 17h2"></path><path d="M2 7h2"></path><path d="M20 12h2"></path><path d="M20 17h2"></path><path d="M20 7h2"></path><path d="M7 20v2"></path><path d="M7 2v2"></path><rect x="4" y="4" width="16" height="16" rx="2"></rect><rect x="8" y="8" width="8" height="8" rx="1"></rect></g>',
  },
  'lucide-creative-commons': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M10 9.3a2.8 2.8 0 0 0-3.5 1 3.1 3.1 0 0 0 0 3.4 2.7 2.7 0 0 0 3.5 1"></path><path d="M17 9.3a2.8 2.8 0 0 0-3.5 1 3.1 3.1 0 0 0 0 3.4 2.7 2.7 0 0 0 3.5 1"></path></g>',
  },
  'lucide-credit-card': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></g>',
  },
  'lucide-croissant': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4.6 13.11 5.79-3.21c1.89-1.05 4.79 1.78 3.71 3.71l-3.22 5.81C8.8 23.16.79 15.23 4.6 13.11Z"></path><path d="m10.5 9.5-1-2.29C9.2 6.48 8.8 6 8 6H4.5C2.79 6 2 6.5 2 8.5a7.71 7.71 0 0 0 2 4.83"></path><path d="M8 6c0-1.55.24-4-2-4-2 0-2.5 2.17-2.5 4"></path><path d="m14.5 13.5 2.29 1c.73.3 1.21.7 1.21 1.5v3.5c0 1.71-.5 2.5-2.5 2.5a7.71 7.71 0 0 1-4.83-2"></path><path d="M18 16c1.55 0 4-.24 4 2 0 2-2.17 2.5-4 2.5"></path></g>',
  },
  'lucide-crop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2v14a2 2 0 0 0 2 2h14"></path><path d="M18 22V8a2 2 0 0 0-2-2H2"></path></g>',
  },
  'lucide-cross': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h4a1 1 0 0 1 1 1v4a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-4a1 1 0 0 1 1-1h4a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-4a1 1 0 0 1-1-1V4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4a1 1 0 0 1-1 1z"></path></g>',
  },
  'lucide-crosshair': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="22" x2="18" y1="12" y2="12"></line><line x1="6" x2="2" y1="12" y2="12"></line><line x1="12" x2="12" y1="6" y2="2"></line><line x1="12" x2="12" y1="22" y2="18"></line></g>',
  },
  'lucide-crown': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path><path d="M5 21h14"></path></g>',
  },
  'lucide-cuboid': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21.12 6.4-6.05-4.06a2 2 0 0 0-2.17-.05L2.95 8.41a2 2 0 0 0-.95 1.7v5.82a2 2 0 0 0 .88 1.66l6.05 4.07a2 2 0 0 0 2.17.05l9.95-6.12a2 2 0 0 0 .95-1.7V8.06a2 2 0 0 0-.88-1.66Z"></path><path d="M10 22v-8L2.25 9.15"></path><path d="m10 14 11.77-6.87"></path></g>',
  },
  'lucide-cup-soda': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 8 1.75 12.28a2 2 0 0 0 2 1.72h4.54a2 2 0 0 0 2-1.72L18 8"></path><path d="M5 8h14"></path><path d="M7 15a6.47 6.47 0 0 1 5 0 6.47 6.47 0 0 0 5 0"></path><path d="m12 8 1-6h2"></path></g>',
  },
  'lucide-currency': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8"></circle><line x1="3" x2="6" y1="3" y2="6"></line><line x1="21" x2="18" y1="3" y2="6"></line><line x1="3" x2="6" y1="21" y2="18"></line><line x1="21" x2="18" y1="21" y2="18"></line></g>',
  },
  'lucide-cylinder': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5v14a9 3 0 0 0 18 0V5"></path></g>',
  },
  'lucide-dam': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 11.31c1.17.56 1.54 1.69 3.5 1.69 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M11.75 18c.35.5 1.45 1 2.75 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M2 10h4"></path><path d="M2 14h4"></path><path d="M2 18h4"></path><path d="M2 6h4"></path><path d="M7 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1L10 4a1 1 0 0 0-1-1z"></path></g>',
  },
  'lucide-database-backup': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 12a9 3 0 0 0 5 2.69"></path><path d="M21 9.3V5"></path><path d="M3 5v14a9 3 0 0 0 6.47 2.88"></path><path d="M12 12v4h4"></path><path d="M13 20a5 5 0 0 0 9-3 4.5 4.5 0 0 0-4.5-4.5c-1.33 0-2.54.54-3.41 1.41L12 16"></path></g>',
  },
  'lucide-database-zap': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5V19A9 3 0 0 0 15 21.84"></path><path d="M21 5V8"></path><path d="M21 12L18 17H22L19 22"></path><path d="M3 12A9 3 0 0 0 14.59 14.87"></path></g>',
  },
  'lucide-database': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5V19A9 3 0 0 0 21 19V5"></path><path d="M3 12A9 3 0 0 0 21 12"></path></g>',
  },
  'lucide-decimals-arrow-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13 21-3-3 3-3"></path><path d="M20 18H10"></path><path d="M3 11h.01"></path><rect x="6" y="3" width="5" height="8" rx="2.5"></rect></g>',
  },
  'lucide-decimals-arrow-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 18h10"></path><path d="m17 21 3-3-3-3"></path><path d="M3 11h.01"></path><rect x="15" y="3" width="5" height="8" rx="2.5"></rect><rect x="6" y="3" width="5" height="8" rx="2.5"></rect></g>',
  },
  'lucide-delete': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 5a2 2 0 0 0-1.344.519l-6.328 5.74a1 1 0 0 0 0 1.481l6.328 5.741A2 2 0 0 0 10 19h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"></path><path d="m12 9 6 6"></path><path d="m18 9-6 6"></path></g>',
  },
  'lucide-dessert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4" r="2"></circle><path d="M10.2 3.2C5.5 4 2 8.1 2 13a2 2 0 0 0 4 0v-1a2 2 0 0 1 4 0v4a2 2 0 0 0 4 0v-4a2 2 0 0 1 4 0v1a2 2 0 0 0 4 0c0-4.9-3.5-9-8.2-9.8"></path><path d="M3.2 14.8a9 9 0 0 0 17.6 0"></path></g>',
  },
  'lucide-diameter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="19" cy="19" r="2"></circle><circle cx="5" cy="5" r="2"></circle><path d="M6.48 3.66a10 10 0 0 1 13.86 13.86"></path><path d="m6.41 6.41 11.18 11.18"></path><path d="M3.66 6.48a10 10 0 0 0 13.86 13.86"></path></g>',
  },
  'lucide-diamond-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0z"></path><path d="M8 12h8"></path></g>',
  },
  'lucide-diamond-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0Z"></path><path d="M9.2 9.2h.01"></path><path d="m14.5 9.5-5 5"></path><path d="M14.7 14.8h.01"></path></g>',
  },
  'lucide-diamond-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8v8"></path><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0z"></path><path d="M8 12h8"></path></g>',
  },
  'lucide-diamond': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z"></path></g>',
  },
  'lucide-dice-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M12 12h.01"></path></g>',
  },
  'lucide-dice-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M15 9h.01"></path><path d="M9 15h.01"></path></g>',
  },
  'lucide-dice-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M16 8h.01"></path><path d="M12 12h.01"></path><path d="M8 16h.01"></path></g>',
  },
  'lucide-dice-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M16 8h.01"></path><path d="M8 8h.01"></path><path d="M8 16h.01"></path><path d="M16 16h.01"></path></g>',
  },
  'lucide-dice-5': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M16 8h.01"></path><path d="M8 8h.01"></path><path d="M8 16h.01"></path><path d="M16 16h.01"></path><path d="M12 12h.01"></path></g>',
  },
  'lucide-dice-6': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M16 8h.01"></path><path d="M16 12h.01"></path><path d="M16 16h.01"></path><path d="M8 8h.01"></path><path d="M8 12h.01"></path><path d="M8 16h.01"></path></g>',
  },
  'lucide-dices': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="12" height="12" x="2" y="10" rx="2" ry="2"></rect><path d="m17.92 14 3.5-3.5a2.24 2.24 0 0 0 0-3l-5-4.92a2.24 2.24 0 0 0-3 0L10 6"></path><path d="M6 18h.01"></path><path d="M10 14h.01"></path><path d="M15 6h.01"></path><path d="M18 9h.01"></path></g>',
  },
  'lucide-diff': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v14"></path><path d="M5 10h14"></path><path d="M5 21h14"></path></g>',
  },
  'lucide-disc-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="4"></circle><path d="M12 12h.01"></path></g>',
  },
  'lucide-disc-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M6 12c0-1.7.7-3.2 1.8-4.2"></path><circle cx="12" cy="12" r="2"></circle><path d="M18 12c0 1.7-.7 3.2-1.8 4.2"></path></g>',
  },
  'lucide-disc-album': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><circle cx="12" cy="12" r="5"></circle><path d="M12 12h.01"></path></g>',
  },
  'lucide-disc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-divide': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="6" r="1"></circle><line x1="5" x2="19" y1="12" y2="12"></line><circle cx="12" cy="18" r="1"></circle></g>',
  },
  'lucide-dna-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2c-1.35 1.5-2.092 3-2.5 4.5L14 8"></path><path d="m17 6-2.891-2.891"></path><path d="M2 15c3.333-3 6.667-3 10-3"></path><path d="m2 2 20 20"></path><path d="m20 9 .891.891"></path><path d="M22 9c-1.5 1.35-3 2.092-4.5 2.5l-1-1"></path><path d="M3.109 14.109 4 15"></path><path d="m6.5 12.5 1 1"></path><path d="m7 18 2.891 2.891"></path><path d="M9 22c1.35-1.5 2.092-3 2.5-4.5L10 16"></path></g>',
  },
  'lucide-dna': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 16 1.5 1.5"></path><path d="m14 8-1.5-1.5"></path><path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993"></path><path d="m16.5 10.5 1 1"></path><path d="m17 6-2.891-2.891"></path><path d="M2 15c6.667-6 13.333 0 20-6"></path><path d="m20 9 .891.891"></path><path d="M3.109 14.109 4 15"></path><path d="m6.5 12.5 1 1"></path><path d="m7 18 2.891 2.891"></path><path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993"></path></g>',
  },
  'lucide-dock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8h20"></path><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M6 16h12"></path></g>',
  },
  'lucide-dog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.25 16.25h1.5L12 17z"></path><path d="M16 14v.5"></path><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444a11.702 11.702 0 0 0-.493-3.309"></path><path d="M8 14v.5"></path><path d="M8.5 8.5c-.384 1.05-1.083 2.028-2.344 2.5-1.931.722-3.576-.297-3.656-1-.113-.994 1.177-6.53 4-7 1.923-.321 3.651.845 3.651 2.235A7.497 7.497 0 0 1 14 5.277c0-1.39 1.844-2.598 3.767-2.277 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"></path></g>',
  },
  'lucide-dollar-sign': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="2" y2="22"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></g>',
  },
  'lucide-donut': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.5 10a2.5 2.5 0 0 1-2.4-3H18a2.95 2.95 0 0 1-2.6-4.4 10 10 0 1 0 6.3 7.1c-.3.2-.8.3-1.2.3"></path><circle cx="12" cy="12" r="3"></circle></g>',
  },
  'lucide-door-closed-locked': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12h.01"></path><path d="M18 9V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"></path><path d="M2 20h8"></path><path d="M20 17v-2a2 2 0 1 0-4 0v2"></path><rect x="14" y="17" width="8" height="5" rx="1"></rect></g>',
  },
  'lucide-door-closed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12h.01"></path><path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"></path><path d="M2 20h20"></path></g>',
  },
  'lucide-door-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20H2"></path><path d="M11 4.562v16.157a1 1 0 0 0 1.242.97L19 20V5.562a2 2 0 0 0-1.515-1.94l-4-1A2 2 0 0 0 11 4.561z"></path><path d="M11 4H8a2 2 0 0 0-2 2v14"></path><path d="M14 12h.01"></path><path d="M22 20h-3"></path></g>',
  },
  'lucide-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12.1" cy="12.1" r="1"></circle></g>',
  },
  'lucide-download': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></g>',
  },
  'lucide-drafting-compass': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12.99 6.74 1.93 3.44"></path><path d="M19.136 12a10 10 0 0 1-14.271 0"></path><path d="m21 21-2.16-3.84"></path><path d="m3 21 8.02-14.26"></path><circle cx="12" cy="5" r="2"></circle></g>',
  },
  'lucide-drama': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 11h.01"></path><path d="M14 6h.01"></path><path d="M18 6h.01"></path><path d="M6.5 13.1h.01"></path><path d="M22 5c0 9-4 12-6 12s-6-3-6-12c0-2 2-3 6-3s6 1 6 3"></path><path d="M17.4 9.9c-.8.8-2 .8-2.8 0"></path><path d="M10.1 7.1C9 7.2 7.7 7.7 6 8.6c-3.5 2-4.7 3.9-3.7 5.6 4.5 7.8 9.5 8.4 11.2 7.4.9-.5 1.9-2.1 1.9-4.7"></path><path d="M9.1 16.5c.3-1.1 1.4-1.7 2.4-1.4"></path></g>',
  },
  'lucide-dribbble': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M19.13 5.09C15.22 9.14 10 10.44 2.25 10.94"></path><path d="M21.75 12.84c-6.62-1.41-12.14 1-16.38 6.32"></path><path d="M8.56 2.75c4.37 6 6 9.42 8 17.72"></path></g>',
  },
  'lucide-drill': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 18a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H5a3 3 0 0 1-3-3 1 1 0 0 1 1-1z"></path><path d="M13 10H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1l-.81 3.242a1 1 0 0 1-.97.758H8"></path><path d="M14 4h3a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-3"></path><path d="M18 6h4"></path><path d="m5 10-2 8"></path><path d="m7 18 2-8"></path></g>',
  },
  'lucide-droplet-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.715 13.186C18.29 11.858 17.384 10.607 16 9.5c-2-1.6-3.5-4-4-6.5a10.7 10.7 0 0 1-.884 2.586"></path><path d="m2 2 20 20"></path><path d="M8.795 8.797A11 11 0 0 1 8 9.5C6 11.1 5 13 5 15a7 7 0 0 0 13.222 3.208"></path></g>',
  },
  'lucide-droplet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"></path></g>',
  },
  'lucide-droplets': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"></path><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"></path></g>',
  },
  'lucide-drum': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 2 8 8"></path><path d="m22 2-8 8"></path><ellipse cx="12" cy="9" rx="10" ry="5"></ellipse><path d="M7 13.4v7.9"></path><path d="M12 14v8"></path><path d="M17 13.4v7.9"></path><path d="M2 9v8a10 5 0 0 0 20 0V9"></path></g>',
  },
  'lucide-drumstick': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.4 15.63a7.875 6 135 1 1 6.23-6.23 4.5 3.43 135 0 0-6.23 6.23"></path><path d="m8.29 12.71-2.6 2.6a2.5 2.5 0 1 0-1.65 4.65A2.5 2.5 0 1 0 8.7 18.3l2.59-2.59"></path></g>',
  },
  'lucide-dumbbell': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.596 12.768a2 2 0 1 0 2.829-2.829l-1.768-1.767a2 2 0 0 0 2.828-2.829l-2.828-2.828a2 2 0 0 0-2.829 2.828l-1.767-1.768a2 2 0 1 0-2.829 2.829z"></path><path d="m2.5 21.5 1.4-1.4"></path><path d="m20.1 3.9 1.4-1.4"></path><path d="M5.343 21.485a2 2 0 1 0 2.829-2.828l1.767 1.768a2 2 0 1 0 2.829-2.829l-6.364-6.364a2 2 0 1 0-2.829 2.829l1.768 1.767a2 2 0 0 0-2.828 2.829z"></path><path d="m9.6 14.4 4.8-4.8"></path></g>',
  },
  'lucide-ear-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18.5a3.5 3.5 0 1 0 7 0c0-1.57.92-2.52 2.04-3.46"></path><path d="M6 8.5c0-.75.13-1.47.36-2.14"></path><path d="M8.8 3.15A6.5 6.5 0 0 1 19 8.5c0 1.63-.44 2.81-1.09 3.76"></path><path d="M12.5 6A2.5 2.5 0 0 1 15 8.5M10 13a2 2 0 0 0 1.82-1.18"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-ear': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0"></path><path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4"></path></g>',
  },
  'lucide-earth-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3.34V5a3 3 0 0 0 3 3"></path><path d="M11 21.95V18a2 2 0 0 0-2-2 2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05"></path><path d="M21.54 15H17a2 2 0 0 0-2 2v4.54"></path><path d="M12 2a10 10 0 1 0 9.54 13"></path><path d="M20 6V4a2 2 0 1 0-4 0v2"></path><rect width="8" height="5" x="14" y="6" rx="1"></rect></g>',
  },
  'lucide-earth': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.54 15H17a2 2 0 0 0-2 2v4.54"></path><path d="M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17"></path><path d="M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-eclipse': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a7 7 0 1 0 10 10"></path></g>',
  },
  'lucide-egg-fried': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11.5" cy="12.5" r="3.5"></circle><path d="M3 8c0-3.5 2.5-6 6.5-6 5 0 4.83 3 7.5 5s5 2 5 6c0 4.5-2.5 6.5-7 6.5-2.5 0-2.5 2.5-6 2.5s-7-2-7-5.5c0-3 1.5-3 1.5-5C3.5 10 3 9 3 8Z"></path></g>',
  },
  'lucide-egg-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.399 6.399C5.362 8.157 4.65 10.189 4.5 12c-.37 4.43 1.27 9.95 7.5 10 3.256-.026 5.259-1.547 6.375-3.625"></path><path d="M19.532 13.875A14.07 14.07 0 0 0 19.5 12c-.36-4.34-3.95-9.96-7.5-10-1.04.012-2.082.502-3.046 1.297"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-egg': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22c6.23-.05 7.87-5.57 7.5-10-.36-4.34-3.95-9.96-7.5-10-3.55.04-7.14 5.66-7.5 10-.37 4.43 1.27 9.95 7.5 10z"></path></g>',
  },
  'lucide-ellipsis-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></g>',
  },
  'lucide-ellipsis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></g>',
  },
  'lucide-equal-approximately': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 15a6.5 6.5 0 0 1 7 0 6.5 6.5 0 0 0 7 0"></path><path d="M5 9a6.5 6.5 0 0 1 7 0 6.5 6.5 0 0 0 7 0"></path></g>',
  },
  'lucide-equal-not': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="5" x2="19" y1="9" y2="9"></line><line x1="5" x2="19" y1="15" y2="15"></line><line x1="19" x2="5" y1="5" y2="19"></line></g>',
  },
  'lucide-equal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="5" x2="19" y1="9" y2="9"></line><line x1="5" x2="19" y1="15" y2="15"></line></g>',
  },
  'lucide-eraser': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"></path><path d="M22 21H7"></path><path d="m5 11 9 9"></path></g>',
  },
  'lucide-ethernet-port': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 20 3-3h2a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h2l3 3z"></path><path d="M6 8v1"></path><path d="M10 8v1"></path><path d="M14 8v1"></path><path d="M18 8v1"></path></g>',
  },
  'lucide-euro': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10h12"></path><path d="M4 14h9"></path><path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2"></path></g>',
  },
  'lucide-expand': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 15 6 6"></path><path d="m15 9 6-6"></path><path d="M21 16v5h-5"></path><path d="M21 8V3h-5"></path><path d="M3 16v5h5"></path><path d="m3 21 6-6"></path><path d="M3 8V3h5"></path><path d="M9 9 3 3"></path></g>',
  },
  'lucide-external-link': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path></g>',
  },
  'lucide-eye-closed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-.722-3.25"></path><path d="M2 8a10.645 10.645 0 0 0 20 0"></path><path d="m20 15-1.726-2.05"></path><path d="m4 15 1.726-2.05"></path><path d="m9 18 .722-3.25"></path></g>',
  },
  'lucide-eye-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"></path><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"></path><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-eye': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path><circle cx="12" cy="12" r="3"></circle></g>',
  },
  'lucide-facebook': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></g>',
  },
  'lucide-factory': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M17 18h1"></path><path d="M12 18h1"></path><path d="M7 18h1"></path></g>',
  },
  'lucide-fan': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.827 16.379a6.082 6.082 0 0 1-8.618-7.002l5.412 1.45a6.082 6.082 0 0 1 7.002-8.618l-1.45 5.412a6.082 6.082 0 0 1 8.618 7.002l-5.412-1.45a6.082 6.082 0 0 1-7.002 8.618l1.45-5.412Z"></path><path d="M12 12v.01"></path></g>',
  },
  'lucide-fast-forward': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 19 22 12 13 5 13 19"></polygon><polygon points="2 19 11 12 2 5 2 19"></polygon></g>',
  },
  'lucide-feather': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.67 19a2 2 0 0 0 1.416-.588l6.154-6.172a6 6 0 0 0-8.49-8.49L5.586 9.914A2 2 0 0 0 5 11.328V18a1 1 0 0 0 1 1z"></path><path d="M16 8 2 22"></path><path d="M17.5 15H9"></path></g>',
  },
  'lucide-fence': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3 2 5v15c0 .6.4 1 1 1h2c.6 0 1-.4 1-1V5Z"></path><path d="M6 8h4"></path><path d="M6 18h4"></path><path d="m12 3-2 2v15c0 .6.4 1 1 1h2c.6 0 1-.4 1-1V5Z"></path><path d="M14 8h4"></path><path d="M14 18h4"></path><path d="m20 3-2 2v15c0 .6.4 1 1 1h2c.6 0 1-.4 1-1V5Z"></path></g>',
  },
  'lucide-ferris-wheel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="2"></circle><path d="M12 2v4"></path><path d="m6.8 15-3.5 2"></path><path d="m20.7 7-3.5 2"></path><path d="M6.8 9 3.3 7"></path><path d="m20.7 17-3.5-2"></path><path d="m9 22 3-8 3 8"></path><path d="M8 22h8"></path><path d="M18 18.7a9 9 0 1 0-12 0"></path></g>',
  },
  'lucide-figma': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 5.5A3.5 3.5 0 0 1 8.5 2H12v7H8.5A3.5 3.5 0 0 1 5 5.5z"></path><path d="M12 2h3.5a3.5 3.5 0 1 1 0 7H12V2z"></path><path d="M12 12.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 1 1-7 0z"></path><path d="M5 19.5A3.5 3.5 0 0 1 8.5 16H12v3.5a3.5 3.5 0 1 1-7 0z"></path><path d="M5 12.5A3.5 3.5 0 0 1 8.5 9H12v7H8.5A3.5 3.5 0 0 1 5 12.5z"></path></g>',
  },
  'lucide-file-archive': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12v-1"></path><path d="M10 18v-2"></path><path d="M10 7V6"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M15.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v16a2 2 0 0 0 .274 1.01"></path><circle cx="10" cy="20" r="2"></circle></g>',
  },
  'lucide-file-audio-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v2"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><circle cx="3" cy="17" r="1"></circle><path d="M2 17v-3a4 4 0 0 1 8 0v3"></path><circle cx="9" cy="17" r="1"></circle></g>',
  },
  'lucide-file-audio': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 22h.5a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M2 19a2 2 0 1 1 4 0v1a2 2 0 1 1-4 0v-4a6 6 0 0 1 12 0v4a2 2 0 1 1-4 0v-1a2 2 0 1 1 4 0"></path></g>',
  },
  'lucide-file-axis-3d': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m8 18 4-4"></path><path d="M8 10v8h8"></path></g>',
  },
  'lucide-file-badge-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13.69 12.479 1.29 4.88a.5.5 0 0 1-.697.591l-1.844-.849a1 1 0 0 0-.88.001l-1.846.85a.5.5 0 0 1-.693-.593l1.29-4.88"></path><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"></path><circle cx="12" cy="10" r="3"></circle></g>',
  },
  'lucide-file-badge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22h6a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M5 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"></path><path d="M7 16.5 8 22l-3-1-3 1 1-5.5"></path></g>',
  },
  'lucide-file-box': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M3 13.1a2 2 0 0 0-1 1.76v3.24a2 2 0 0 0 .97 1.78L6 21.7a2 2 0 0 0 2.03.01L11 19.9a2 2 0 0 0 1-1.76V14.9a2 2 0 0 0-.97-1.78L8 11.3a2 2 0 0 0-2.03-.01Z"></path><path d="M7 17v5"></path><path d="M11.7 14.2 7 17l-4.7-2.8"></path></g>',
  },
  'lucide-file-chart-column-increasing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M8 18v-2"></path><path d="M12 18v-4"></path><path d="M16 18v-6"></path></g>',
  },
  'lucide-file-chart-column': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M8 18v-1"></path><path d="M12 18v-6"></path><path d="M16 18v-3"></path></g>',
  },
  'lucide-file-chart-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m16 13-3.5 3.5-2-2L8 17"></path></g>',
  },
  'lucide-file-chart-pie': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M16 22h2a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3.5"></path><path d="M4.017 11.512a6 6 0 1 0 8.466 8.475"></path><path d="M9 16a1 1 0 0 1-1-1v-4c0-.552.45-1.008.995-.917a6 6 0 0 1 4.922 4.922c.091.544-.365.995-.917.995z"></path></g>',
  },
  'lucide-file-check-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m3 15 2 2 4-4"></path></g>',
  },
  'lucide-file-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m9 15 2 2 4-4"></path></g>',
  },
  'lucide-file-clock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 22h2a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><circle cx="8" cy="16" r="6"></circle><path d="M9.5 17.5 8 16.25V14"></path></g>',
  },
  'lucide-file-code-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m5 12-3 3 3 3"></path><path d="m9 18 3-3-3-3"></path></g>',
  },
  'lucide-file-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12.5 8 15l2 2.5"></path><path d="m14 12.5 2 2.5-2 2.5"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"></path></g>',
  },
  'lucide-file-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m2.305 15.53.923-.382"></path><path d="m3.228 12.852-.924-.383"></path><path d="M4.677 21.5a2 2 0 0 0 1.313.5H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v2.5"></path><path d="m4.852 11.228-.383-.923"></path><path d="m4.852 16.772-.383.924"></path><path d="m7.148 11.228.383-.923"></path><path d="m7.53 17.696-.382-.924"></path><path d="m8.772 12.852.923-.383"></path><path d="m8.772 15.148.923.383"></path><circle cx="6" cy="14" r="3"></circle></g>',
  },
  'lucide-file-diff': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M9 10h6"></path><path d="M12 13V7"></path><path d="M9 17h6"></path></g>',
  },
  'lucide-file-digit': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><rect width="4" height="6" x="2" y="12" rx="2"></rect><path d="M10 12h2v6"></path><path d="M10 18h4"></path></g>',
  },
  'lucide-file-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M12 18v-6"></path><path d="m9 15 3 3 3-3"></path></g>',
  },
  'lucide-file-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v2"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10.29 10.7a2.43 2.43 0 0 0-2.66-.52c-.29.12-.56.3-.78.53l-.35.34-.35-.34a2.43 2.43 0 0 0-2.65-.53c-.3.12-.56.3-.79.53-.95.94-1 2.53.2 3.74L6.5 18l3.6-3.55c1.2-1.21 1.14-2.8.19-3.74Z"></path></g>',
  },
  'lucide-file-image': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><circle cx="10" cy="12" r="2"></circle><path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22"></path></g>',
  },
  'lucide-file-input': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M2 15h10"></path><path d="m9 18 3-3-3-3"></path></g>',
  },
  'lucide-file-json-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M4 12a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1"></path><path d="M8 18a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1"></path></g>',
  },
  'lucide-file-json': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 12a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1"></path><path d="M14 18a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1"></path></g>',
  },
  'lucide-file-key-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v6"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><circle cx="4" cy="16" r="2"></circle><path d="m10 10-4.5 4.5"></path><path d="m9 11 1 1"></path></g>',
  },
  'lucide-file-key': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><circle cx="10" cy="16" r="2"></circle><path d="m16 10-4.5 4.5"></path><path d="m15 11 1 1"></path></g>',
  },
  'lucide-file-lock-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v1"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><rect width="8" height="5" x="2" y="13" rx="1"></rect><path d="M8 13v-2a2 2 0 1 0-4 0v2"></path></g>',
  },
  'lucide-file-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><rect width="8" height="6" x="8" y="12" rx="1"></rect><path d="M10 12v-2a2 2 0 1 1 4 0v2"></path></g>',
  },
  'lucide-file-minus-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M3 15h6"></path></g>',
  },
  'lucide-file-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M9 15h6"></path></g>',
  },
  'lucide-file-music': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v8.4"></path><path d="M8 18v-7.7L16 9v7"></path><circle cx="14" cy="16" r="2"></circle><circle cx="6" cy="18" r="2"></circle></g>',
  },
  'lucide-file-output': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M4 7V4a2 2 0 0 1 2-2 2 2 0 0 0-2 2"></path><path d="M4.063 20.999a2 2 0 0 0 2 1L18 22a2 2 0 0 0 2-2V7l-5-5H6"></path><path d="m5 11-3 3"></path><path d="m5 17-3-3h10"></path></g>',
  },
  'lucide-file-pen-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 5-2.414-2.414A2 2 0 0 0 14.172 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2"></path><path d="M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path><path d="M8 18h1"></path></g>',
  },
  'lucide-file-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v9.5"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M13.378 15.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path></g>',
  },
  'lucide-file-plus-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M3 15h6"></path><path d="M6 12v6"></path></g>',
  },
  'lucide-file-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M9 15h6"></path><path d="M12 18v-6"></path></g>',
  },
  'lucide-file-question': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17h.01"></path><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"></path><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3"></path></g>',
  },
  'lucide-file-scan': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10V7l-5-5H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M16 14a2 2 0 0 0-2 2"></path><path d="M20 14a2 2 0 0 1 2 2"></path><path d="M20 22a2 2 0 0 0 2-2"></path><path d="M16 22a2 2 0 0 1-2-2"></path></g>',
  },
  'lucide-file-search-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><circle cx="11.5" cy="14.5" r="2.5"></circle><path d="M13.3 16.3 15 18"></path></g>',
  },
  'lucide-file-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M4.268 21a2 2 0 0 0 1.727 1H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3"></path><path d="m9 18-1.5-1.5"></path><circle cx="5" cy="14" r="3"></circle></g>',
  },
  'lucide-file-sliders': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M8 12h8"></path><path d="M10 11v2"></path><path d="M8 17h8"></path><path d="M14 16v2"></path></g>',
  },
  'lucide-file-spreadsheet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M8 13h2"></path><path d="M14 13h2"></path><path d="M8 17h2"></path><path d="M14 17h2"></path></g>',
  },
  'lucide-file-stack': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7h-3a2 2 0 0 1-2-2V2"></path><path d="M21 6v6.5c0 .8-.7 1.5-1.5 1.5h-7c-.8 0-1.5-.7-1.5-1.5v-9c0-.8.7-1.5 1.5-1.5H17Z"></path><path d="M7 8v8.8c0 .3.2.6.4.8.2.2.5.4.8.4H15"></path><path d="M3 12v8.8c0 .3.2.6.4.8.2.2.5.4.8.4H11"></path></g>',
  },
  'lucide-file-symlink': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 18 3-3-3-3"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M4 11V4a2 2 0 0 1 2-2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h7"></path></g>',
  },
  'lucide-file-terminal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m8 16 2-2-2-2"></path><path d="M12 18h4"></path></g>',
  },
  'lucide-file-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></g>',
  },
  'lucide-file-type-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M2 13v-1h6v1"></path><path d="M5 12v6"></path><path d="M4 18h2"></path></g>',
  },
  'lucide-file-type': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M9 13v-1h6v1"></path><path d="M12 12v6"></path><path d="M11 18h2"></path></g>',
  },
  'lucide-file-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M12 12v6"></path><path d="m15 15-3-3-3 3"></path></g>',
  },
  'lucide-file-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M15 18a3 3 0 1 0-6 0"></path><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"></path><circle cx="12" cy="13" r="2"></circle></g>',
  },
  'lucide-file-video-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><rect width="8" height="6" x="2" y="12" rx="1"></rect><path d="m10 15.5 4 2.5v-6l-4 2.5"></path></g>',
  },
  'lucide-file-video': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m10 11 5 3-5 3v-6Z"></path></g>',
  },
  'lucide-file-volume-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M8 15h.01"></path><path d="M11.5 13.5a2.5 2.5 0 0 1 0 3"></path><path d="M15 12a5 5 0 0 1 0 6"></path></g>',
  },
  'lucide-file-volume': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 11a5 5 0 0 1 0 6"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M4 6.765V4a2 2 0 0 1 2-2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-.93-.23"></path><path d="M7 10.51a.5.5 0 0 0-.826-.38l-1.893 1.628A1 1 0 0 1 3.63 12H2.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h1.129a1 1 0 0 1 .652.242l1.893 1.63a.5.5 0 0 0 .826-.38z"></path></g>',
  },
  'lucide-file-warning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></g>',
  },
  'lucide-file-x-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v4"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m8 12.5-5 5"></path><path d="m3 12.5 5 5"></path></g>',
  },
  'lucide-file-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="m14.5 12.5-5 5"></path><path d="m9.5 12.5 5 5"></path></g>',
  },
  'lucide-file': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path></g>',
  },
  'lucide-files': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-3a2 2 0 0 1-2-2V2"></path><path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z"></path><path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8"></path></g>',
  },
  'lucide-film': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 3v18"></path><path d="M3 7.5h4"></path><path d="M3 12h18"></path><path d="M3 16.5h4"></path><path d="M17 3v18"></path><path d="M17 7.5h4"></path><path d="M17 16.5h4"></path></g>',
  },
  'lucide-fingerprint': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"></path><path d="M14 13.12c0 2.38 0 6.38-1 8.88"></path><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"></path><path d="M2 12a10 10 0 0 1 18-6"></path><path d="M2 16h.01"></path><path d="M21.8 16c.2-2 .131-5.354 0-6"></path><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2"></path><path d="M8.65 22c.21-.66.45-1.32.57-2"></path><path d="M9 6.8a6 6 0 0 1 9 5.2v2"></path></g>',
  },
  'lucide-fire-extinguisher': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6.5V3a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v3.5"></path><path d="M9 18h8"></path><path d="M18 3h-3"></path><path d="M11 3a6 6 0 0 0-6 6v11"></path><path d="M5 13h4"></path><path d="M17 10a4 4 0 0 0-8 0v10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2Z"></path></g>',
  },
  'lucide-fish-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 12.47v.03m0-.5v.47m-.475 5.056A6.744 6.744 0 0 1 15 18c-3.56 0-7.56-2.53-8.5-6 .348-1.28 1.114-2.433 2.121-3.38m3.444-2.088A8.802 8.802 0 0 1 15 6c3.56 0 6.06 2.54 7 6-.309 1.14-.786 2.177-1.413 3.058"></path><path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .23 6.5-1.24 1.5-1.24 5-.23 6.5C5.58 18.03 7 16 7 13.33m7.48-4.372A9.77 9.77 0 0 1 16 6.07m0 11.86a9.77 9.77 0 0 1-1.728-3.618"></path><path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 0 1.49-3.98M8.53 3h5.27a2 2 0 0 1 1.98 1.67l.23 1.4M2 2l20 20"></path></g>',
  },
  'lucide-fish-symbol': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 16s9-15 20-4C11 23 2 8 2 8"></path></g>',
  },
  'lucide-fish': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z"></path><path d="M18 12v.5"></path><path d="M16 17.93a9.77 9.77 0 0 1 0-11.86"></path><path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .23 6.5-1.24 1.5-1.24 5-.23 6.5C5.58 18.03 7 16 7 13.33"></path><path d="M10.46 7.26C10.2 5.88 9.17 4.24 8 3h5.8a2 2 0 0 1 1.98 1.67l.23 1.4"></path><path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 0 1.49-3.98"></path></g>',
  },
  'lucide-flag-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2c3 0 5 2 8 2s4-1 4-1v11"></path><path d="M4 22V4"></path><path d="M4 15s1-1 4-1 5 2 8 2"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-flag-triangle-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 22V2L7 7l10 5"></path></g>',
  },
  'lucide-flag-triangle-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 22V2l10 5-10 5"></path></g>',
  },
  'lucide-flag': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path><line x1="4" x2="4" y1="22" y2="15"></line></g>',
  },
  'lucide-flame-kindling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2c1 3 2.5 3.5 3.5 4.5A5 5 0 0 1 17 10a5 5 0 1 1-10 0c0-.3 0-.6.1-.9a2 2 0 1 0 3.3-2C8 4.5 11 2 12 2Z"></path><path d="m5 22 14-4"></path><path d="m5 18 14 4"></path></g>',
  },
  'lucide-flame': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></g>',
  },
  'lucide-flashlight-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 16v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V10c0-2-2-2-2-4"></path><path d="M7 2h11v4c0 2-2 2-2 4v1"></path><line x1="11" x2="18" y1="6" y2="6"></line><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-flashlight': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6c0 2-2 2-2 4v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V10c0-2-2-2-2-4V2h12z"></path><line x1="6" x2="18" y1="6" y2="6"></line><line x1="12" x2="12" y1="12" y2="12"></line></g>',
  },
  'lucide-flask-conical-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v2.343"></path><path d="M14 2v6.343"></path><path d="m2 2 20 20"></path><path d="M20 20a2 2 0 0 1-2 2H6a2 2 0 0 1-1.755-2.96l5.227-9.563"></path><path d="M6.453 15H15"></path><path d="M8.5 2h7"></path></g>',
  },
  'lucide-flask-conical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2"></path><path d="M6.453 15h11.094"></path><path d="M8.5 2h7"></path></g>',
  },
  'lucide-flask-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v6.292a7 7 0 1 0 4 0V2"></path><path d="M5 15h14"></path><path d="M8.5 2h7"></path></g>',
  },
  'lucide-flip-horizontal-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 7 5 5-5 5V7"></path><path d="m21 7-5 5 5 5V7"></path><path d="M12 20v2"></path><path d="M12 14v2"></path><path d="M12 8v2"></path><path d="M12 2v2"></path></g>',
  },
  'lucide-flip-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h3"></path><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"></path><path d="M12 20v2"></path><path d="M12 14v2"></path><path d="M12 8v2"></path><path d="M12 2v2"></path></g>',
  },
  'lucide-flip-vertical-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 3-5 5-5-5h10"></path><path d="m17 21-5-5-5 5h10"></path><path d="M4 12H2"></path><path d="M10 12H8"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path></g>',
  },
  'lucide-flip-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"></path><path d="M21 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3"></path><path d="M4 12H2"></path><path d="M10 12H8"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path></g>',
  },
  'lucide-flower-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5a3 3 0 1 1 3 3m-3-3a3 3 0 1 0-3 3m3-3v1M9 8a3 3 0 1 0 3 3M9 8h1m5 0a3 3 0 1 1-3 3m3-3h-1m-2 3v-1"></path><circle cx="12" cy="8" r="2"></circle><path d="M12 10v12"></path><path d="M12 22c4.2 0 7-1.667 7-5-4.2 0-7 1.667-7 5Z"></path><path d="M12 22c-4.2 0-7-1.667-7-5 4.2 0 7 1.667 7 5Z"></path></g>',
  },
  'lucide-flower': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M12 16.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 1 1 12 7.5a4.5 4.5 0 1 1 4.5 4.5 4.5 4.5 0 1 1-4.5 4.5"></path><path d="M12 7.5V9"></path><path d="M7.5 12H9"></path><path d="M16.5 12H15"></path><path d="M12 16.5V15"></path><path d="m8 8 1.88 1.88"></path><path d="M14.12 9.88 16 8"></path><path d="m8 16 1.88-1.88"></path><path d="M14.12 14.12 16 16"></path></g>',
  },
  'lucide-focus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path></g>',
  },
  'lucide-fold-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h6"></path><path d="M22 12h-6"></path><path d="M12 2v2"></path><path d="M12 8v2"></path><path d="M12 14v2"></path><path d="M12 20v2"></path><path d="m19 9-3 3 3 3"></path><path d="m5 15 3-3-3-3"></path></g>',
  },
  'lucide-fold-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-6"></path><path d="M12 8V2"></path><path d="M4 12H2"></path><path d="M10 12H8"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path><path d="m15 19-3-3-3 3"></path><path d="m15 5-3 3-3-3"></path></g>',
  },
  'lucide-folder-archive': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="15" cy="19" r="2"></circle><path d="M20.9 19.8A2 2 0 0 0 22 18V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h5.1"></path><path d="M15 11v-1"></path><path d="M15 17v-2"></path></g>',
  },
  'lucide-folder-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="m9 13 2 2 4-4"></path></g>',
  },
  'lucide-folder-clock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="16" cy="16" r="6"></circle><path d="M7 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2"></path><path d="M16 14v2l1 1"></path></g>',
  },
  'lucide-folder-closed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="M2 10h20"></path></g>',
  },
  'lucide-folder-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10.5 8 13l2 2.5"></path><path d="m14 10.5 2 2.5-2 2.5"></path><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"></path></g>',
  },
  'lucide-folder-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.98a2 2 0 0 1 1.69.9l.66 1.2A2 2 0 0 0 12 6h8a2 2 0 0 1 2 2v3.3"></path><path d="m14.305 19.53.923-.382"></path><path d="m15.228 16.852-.923-.383"></path><path d="m16.852 15.228-.383-.923"></path><path d="m16.852 20.772-.383.924"></path><path d="m19.148 15.228.383-.923"></path><path d="m19.53 21.696-.382-.924"></path><path d="m20.772 16.852.924-.383"></path><path d="m20.772 19.148.924.383"></path><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-folder-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path><circle cx="12" cy="13" r="1"></circle></g>',
  },
  'lucide-folder-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="M12 10v6"></path><path d="m15 13-3 3-3-3"></path></g>',
  },
  'lucide-folder-git-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v5"></path><circle cx="13" cy="12" r="2"></circle><path d="M18 19c-2.8 0-5-2.2-5-5v8"></path><circle cx="20" cy="19" r="2"></circle></g>',
  },
  'lucide-folder-git': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="2"></circle><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="M14 13h3"></path><path d="M7 13h3"></path></g>',
  },
  'lucide-folder-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v1.5"></path><path d="M13.9 17.45c-1.2-1.2-1.14-2.8-.2-3.73a2.43 2.43 0 0 1 3.44 0l.36.34.34-.34a2.43 2.43 0 0 1 3.45-.01c.95.95 1 2.53-.2 3.74L17.5 21Z"></path></g>',
  },
  'lucide-folder-input': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1"></path><path d="M2 13h10"></path><path d="m9 16 3-3-3-3"></path></g>',
  },
  'lucide-folder-kanban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path><path d="M8 10v4"></path><path d="M12 10v2"></path><path d="M16 10v6"></path></g>',
  },
  'lucide-folder-key': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="16" cy="20" r="2"></circle><path d="M10 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v2"></path><path d="m22 14-4.5 4.5"></path><path d="m21 15 1 1"></path></g>',
  },
  'lucide-folder-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="5" x="14" y="17" rx="1"></rect><path d="M10 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v2.5"></path><path d="M20 17v-2a2 2 0 1 0-4 0v2"></path></g>',
  },
  'lucide-folder-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 13h6"></path><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path></g>',
  },
  'lucide-folder-open-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 14 1.45-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.55 6a2 2 0 0 1-1.94 1.5H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H18a2 2 0 0 1 2 2v2"></path><circle cx="14" cy="15" r="1"></circle></g>',
  },
  'lucide-folder-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"></path></g>',
  },
  'lucide-folder-output': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7.5V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-1.5"></path><path d="M2 13h10"></path><path d="m5 10-3 3 3 3"></path></g>',
  },
  'lucide-folder-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 11.5V5a2 2 0 0 1 2-2h3.9c.7 0 1.3.3 1.7.9l.8 1.2c.4.6 1 .9 1.7.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-9.5"></path><path d="M11.378 13.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path></g>',
  },
  'lucide-folder-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 10v6"></path><path d="M9 13h6"></path><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path></g>',
  },
  'lucide-folder-root': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path><circle cx="12" cy="13" r="2"></circle><path d="M12 15v5"></path></g>',
  },
  'lucide-folder-search-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11.5" cy="12.5" r="2.5"></circle><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="M13.3 14.3 15 16"></path></g>',
  },
  'lucide-folder-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.7 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v4.1"></path><path d="m21 21-1.9-1.9"></path><circle cx="17" cy="17" r="3"></circle></g>',
  },
  'lucide-folder-symlink': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h7"></path><path d="m8 16 3-3-3-3"></path></g>',
  },
  'lucide-folder-sync': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v.5"></path><path d="M12 10v4h4"></path><path d="m12 14 1.535-1.605a5 5 0 0 1 8 1.5"></path><path d="M22 22v-4h-4"></path><path d="m22 18-1.535 1.605a5 5 0 0 1-8-1.5"></path></g>',
  },
  'lucide-folder-tree': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2.5a1 1 0 0 1-.8-.4l-.9-1.2A1 1 0 0 0 15 3h-2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z"></path><path d="M20 21a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2.9a1 1 0 0 1-.88-.55l-.42-.85a1 1 0 0 0-.92-.6H13a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z"></path><path d="M3 5a2 2 0 0 0 2 2h3"></path><path d="M3 3v13a2 2 0 0 0 2 2h3"></path></g>',
  },
  'lucide-folder-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="M12 10v6"></path><path d="m9 13 3-3 3 3"></path></g>',
  },
  'lucide-folder-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path><path d="m9.5 10.5 5 5"></path><path d="m14.5 10.5-5 5"></path></g>',
  },
  'lucide-folder': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path></g>',
  },
  'lucide-folders': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 17a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3.9a2 2 0 0 1-1.69-.9l-.81-1.2a2 2 0 0 0-1.67-.9H8a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2Z"></path><path d="M2 8v11a2 2 0 0 0 2 2h14"></path></g>',
  },
  'lucide-footprints': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"></path><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"></path><path d="M16 17h4"></path><path d="M4 13h4"></path></g>',
  },
  'lucide-forklift': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12H5a2 2 0 0 0-2 2v5"></path><circle cx="13" cy="19" r="2"></circle><circle cx="5" cy="19" r="2"></circle><path d="M8 19h3m5-17v17h6M6 12V7c0-1.1.9-2 2-2h3l5 5"></path></g>',
  },
  'lucide-forward': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 17 20 12 15 7"></polyline><path d="M4 18v-2a4 4 0 0 1 4-4h12"></path></g>',
  },
  'lucide-frame': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="22" x2="2" y1="6" y2="6"></line><line x1="22" x2="2" y1="18" y2="18"></line><line x1="6" x2="6" y1="2" y2="22"></line><line x1="18" x2="18" y1="2" y2="22"></line></g>',
  },
  'lucide-framer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 16V9h14V2H5l14 14h-7m-7 0 7 7v-7m-7 0h7"></path></g>',
  },
  'lucide-frown': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 16s-1.5-2-4-2-4 2-4 2"></path><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line></g>',
  },
  'lucide-fuel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="15" y1="22" y2="22"></line><line x1="4" x2="14" y1="9" y2="9"></line><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"></path><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5"></path></g>',
  },
  'lucide-fullscreen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><rect width="10" height="8" x="7" y="8" rx="1"></rect></g>',
  },
  'lucide-funnel-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.354 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14v6a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341l1.218-1.348"></path><path d="M16 6h6"></path><path d="M19 3v6"></path></g>',
  },
  'lucide-funnel-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.531 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14v6a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341l.427-.473"></path><path d="m16.5 3.5 5 5"></path><path d="m21.5 3.5-5 5"></path></g>',
  },
  'lucide-funnel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path></g>',
  },
  'lucide-gallery-horizontal-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7v10"></path><path d="M6 5v14"></path><rect width="12" height="18" x="10" y="3" rx="2"></rect></g>',
  },
  'lucide-gallery-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3v18"></path><rect width="12" height="18" x="6" y="3" rx="2"></rect><path d="M22 3v18"></path></g>',
  },
  'lucide-gallery-thumbnails': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="14" x="3" y="3" rx="2"></rect><path d="M4 21h1"></path><path d="M9 21h1"></path><path d="M14 21h1"></path><path d="M19 21h1"></path></g>',
  },
  'lucide-gallery-vertical-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 2h10"></path><path d="M5 6h14"></path><rect width="18" height="12" x="3" y="10" rx="2"></rect></g>',
  },
  'lucide-gallery-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2h18"></path><rect width="18" height="12" x="3" y="6" rx="2"></rect><path d="M3 22h18"></path></g>',
  },
  'lucide-gamepad-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="11" y2="11"></line><line x1="8" x2="8" y1="9" y2="13"></line><line x1="15" x2="15.01" y1="12" y2="12"></line><line x1="18" x2="18.01" y1="10" y2="10"></line><path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z"></path></g>',
  },
  'lucide-gamepad': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="12" y2="12"></line><line x1="8" x2="8" y1="10" y2="14"></line><line x1="15" x2="15.01" y1="13" y2="13"></line><line x1="18" x2="18.01" y1="11" y2="11"></line><rect width="20" height="12" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-gauge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 14 4-4"></path><path d="M3.34 19a10 10 0 1 1 17.32 0"></path></g>',
  },
  'lucide-gavel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.5 12.5-8 8a2.119 2.119 0 1 1-3-3l8-8"></path><path d="m16 16 6-6"></path><path d="m8 8 6-6"></path><path d="m9 7 8 8"></path><path d="m21 11-8-8"></path></g>',
  },
  'lucide-gem': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l4 6-10 13L2 9Z"></path><path d="M11 3 8 9l4 13 4-13-3-6"></path><path d="M2 9h20"></path></g>',
  },
  'lucide-ghost': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 10h.01"></path><path d="M15 10h.01"></path><path d="M12 2a8 8 0 0 0-8 8v12l3-3 2.5 2.5L12 19l2.5 2.5L17 19l3 3V10a8 8 0 0 0-8-8z"></path></g>',
  },
  'lucide-gift': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13"></path><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path></g>',
  },
  'lucide-git-branch-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3v12"></path><path d="M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path><path d="M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"></path><path d="M15 6a9 9 0 0 0-9 9"></path><path d="M18 15v6"></path><path d="M21 18h-6"></path></g>',
  },
  'lucide-git-branch': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="6" y1="3" y2="15"></line><circle cx="18" cy="6" r="3"></circle><circle cx="6" cy="18" r="3"></circle><path d="M18 9a9 9 0 0 1-9 9"></path></g>',
  },
  'lucide-git-commit-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><line x1="3" x2="9" y1="12" y2="12"></line><line x1="15" x2="21" y1="12" y2="12"></line></g>',
  },
  'lucide-git-commit-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v6"></path><circle cx="12" cy="12" r="3"></circle><path d="M12 15v6"></path></g>',
  },
  'lucide-git-compare-arrows': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="6" r="3"></circle><path d="M12 6h5a2 2 0 0 1 2 2v7"></path><path d="m15 9-3-3 3-3"></path><circle cx="19" cy="18" r="3"></circle><path d="M12 18H7a2 2 0 0 1-2-2V9"></path><path d="m9 15 3 3-3 3"></path></g>',
  },
  'lucide-git-compare': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="18" r="3"></circle><circle cx="6" cy="6" r="3"></circle><path d="M13 6h3a2 2 0 0 1 2 2v7"></path><path d="M11 18H8a2 2 0 0 1-2-2V9"></path></g>',
  },
  'lucide-git-fork': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="18" r="3"></circle><circle cx="6" cy="6" r="3"></circle><circle cx="18" cy="6" r="3"></circle><path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9"></path><path d="M12 12v3"></path></g>',
  },
  'lucide-git-graph': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="6" r="3"></circle><path d="M5 9v6"></path><circle cx="5" cy="18" r="3"></circle><path d="M12 3v18"></path><circle cx="19" cy="6" r="3"></circle><path d="M16 15.7A9 9 0 0 0 19 9"></path></g>',
  },
  'lucide-git-merge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="18" r="3"></circle><circle cx="6" cy="6" r="3"></circle><path d="M6 21V9a9 9 0 0 0 9 9"></path></g>',
  },
  'lucide-git-pull-request-arrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="6" r="3"></circle><path d="M5 9v12"></path><circle cx="19" cy="18" r="3"></circle><path d="m15 9-3-3 3-3"></path><path d="M12 6h5a2 2 0 0 1 2 2v7"></path></g>',
  },
  'lucide-git-pull-request-closed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"></circle><path d="M6 9v12"></path><path d="m21 3-6 6"></path><path d="m21 9-6-6"></path><path d="M18 11.5V15"></path><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-git-pull-request-create-arrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="6" r="3"></circle><path d="M5 9v12"></path><path d="m15 9-3-3 3-3"></path><path d="M12 6h5a2 2 0 0 1 2 2v3"></path><path d="M19 15v6"></path><path d="M22 18h-6"></path></g>',
  },
  'lucide-git-pull-request-create': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"></circle><path d="M6 9v12"></path><path d="M13 6h3a2 2 0 0 1 2 2v3"></path><path d="M18 15v6"></path><path d="M21 18h-6"></path></g>',
  },
  'lucide-git-pull-request-draft': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="18" r="3"></circle><circle cx="6" cy="6" r="3"></circle><path d="M18 6V5"></path><path d="M18 11v-1"></path><line x1="6" x2="6" y1="9" y2="21"></line></g>',
  },
  'lucide-git-pull-request': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="18" r="3"></circle><circle cx="6" cy="6" r="3"></circle><path d="M13 6h3a2 2 0 0 1 2 2v7"></path><line x1="6" x2="6" y1="9" y2="21"></line></g>',
  },
  'lucide-github': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"></path><path d="M9 18c-4.51 2-5-2-7-2"></path></g>',
  },
  'lucide-gitlab': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m22 13.29-3.33-10a.42.42 0 0 0-.14-.18.38.38 0 0 0-.22-.11.39.39 0 0 0-.23.07.42.42 0 0 0-.14.18l-2.26 6.67H8.32L6.1 3.26a.42.42 0 0 0-.1-.18.38.38 0 0 0-.26-.08.39.39 0 0 0-.23.07.42.42 0 0 0-.14.18L2 13.29a.74.74 0 0 0 .27.83L12 21l9.69-6.88a.71.71 0 0 0 .31-.83Z"></path></g>',
  },
  'lucide-glass-water': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.116 4.104A1 1 0 0 1 6.11 3h11.78a1 1 0 0 1 .994 1.105L17.19 20.21A2 2 0 0 1 15.2 22H8.8a2 2 0 0 1-2-1.79z"></path><path d="M6 12a5 5 0 0 1 6 0 5 5 0 0 0 6 0"></path></g>',
  },
  'lucide-glasses': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="15" r="4"></circle><circle cx="18" cy="15" r="4"></circle><path d="M14 15a2 2 0 0 0-2-2 2 2 0 0 0-2 2"></path><path d="M2.5 13 5 7c.7-1.3 1.4-2 3-2"></path><path d="M21.5 13 19 7c-.7-1.3-1.5-2-3-2"></path></g>',
  },
  'lucide-globe-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.686 15A14.5 14.5 0 0 1 12 22a14.5 14.5 0 0 1 0-20 10 10 0 1 0 9.542 13"></path><path d="M2 12h8.5"></path><path d="M20 6V4a2 2 0 1 0-4 0v2"></path><rect width="8" height="5" x="14" y="6" rx="1"></rect></g>',
  },
  'lucide-globe': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path><path d="M2 12h20"></path></g>',
  },
  'lucide-goal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13V2l8 4-8 4"></path><path d="M20.561 10.222a9 9 0 1 1-12.55-5.29"></path><path d="M8.002 9.997a5 5 0 1 0 8.9 2.02"></path></g>',
  },
  'lucide-grab': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11.5V9a2 2 0 0 0-2-2a2 2 0 0 0-2 2v1.4"></path><path d="M14 10V8a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"></path><path d="M10 9.9V9a2 2 0 0 0-2-2a2 2 0 0 0-2 2v5"></path><path d="M6 14a2 2 0 0 0-2-2a2 2 0 0 0-2 2"></path><path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-4a8 8 0 0 1-8-8 2 2 0 1 1 4 0"></path></g>',
  },
  'lucide-graduation-cap': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"></path><path d="M22 10v6"></path><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"></path></g>',
  },
  'lucide-grape': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 5V2l-5.89 5.89"></path><circle cx="16.6" cy="15.89" r="3"></circle><circle cx="8.11" cy="7.4" r="3"></circle><circle cx="12.35" cy="11.65" r="3"></circle><circle cx="13.91" cy="5.85" r="3"></circle><circle cx="18.15" cy="10.09" r="3"></circle><circle cx="6.56" cy="13.2" r="3"></circle><circle cx="10.8" cy="17.44" r="3"></circle><circle cx="5" cy="19" r="3"></circle></g>',
  },
  'lucide-grid-2x2-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v17a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a1 1 0 0 1-1 1H3"></path><path d="m16 19 2 2 4-4"></path></g>',
  },
  'lucide-grid-2x2-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v17a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a1 1 0 0 1-1 1H3"></path><path d="M16 19h6"></path><path d="M19 22v-6"></path></g>',
  },
  'lucide-grid-2x2-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v17a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a1 1 0 0 1-1 1H3"></path><path d="m16 16 5 5"></path><path d="m16 21 5-5"></path></g>',
  },
  'lucide-grid-2x2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"></path><path d="M3 12h18"></path><rect x="3" y="3" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-grid-3x3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="M3 15h18"></path><path d="M9 3v18"></path><path d="M15 3v18"></path></g>',
  },
  'lucide-grip-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="9" r="1"></circle><circle cx="19" cy="9" r="1"></circle><circle cx="5" cy="9" r="1"></circle><circle cx="12" cy="15" r="1"></circle><circle cx="19" cy="15" r="1"></circle><circle cx="5" cy="15" r="1"></circle></g>',
  },
  'lucide-grip-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="12" r="1"></circle><circle cx="9" cy="5" r="1"></circle><circle cx="9" cy="19" r="1"></circle><circle cx="15" cy="12" r="1"></circle><circle cx="15" cy="5" r="1"></circle><circle cx="15" cy="19" r="1"></circle></g>',
  },
  'lucide-grip': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="1"></circle><circle cx="19" cy="5" r="1"></circle><circle cx="5" cy="5" r="1"></circle><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle><circle cx="12" cy="19" r="1"></circle><circle cx="19" cy="19" r="1"></circle><circle cx="5" cy="19" r="1"></circle></g>',
  },
  'lucide-group': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5c0-1.1.9-2 2-2h2"></path><path d="M17 3h2c1.1 0 2 .9 2 2v2"></path><path d="M21 17v2c0 1.1-.9 2-2 2h-2"></path><path d="M7 21H5c-1.1 0-2-.9-2-2v-2"></path><rect width="7" height="5" x="7" y="7" rx="1"></rect><rect width="7" height="5" x="10" y="12" rx="1"></rect></g>',
  },
  'lucide-guitar': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11.9 12.1 4.514-4.514"></path><path d="M20.1 2.3a1 1 0 0 0-1.4 0l-1.114 1.114A2 2 0 0 0 17 4.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 17.828 7h1.344a2 2 0 0 0 1.414-.586L21.7 5.3a1 1 0 0 0 0-1.4z"></path><path d="m6 16 2 2"></path><path d="M8.23 9.85A3 3 0 0 1 11 8a5 5 0 0 1 5 5 3 3 0 0 1-1.85 2.77l-.92.38A2 2 0 0 0 12 18a4 4 0 0 1-4 4 6 6 0 0 1-6-6 4 4 0 0 1 4-4 2 2 0 0 0 1.85-1.23z"></path></g>',
  },
  'lucide-ham': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.144 21.144A7.274 10.445 45 1 0 2.856 10.856"></path><path d="M13.144 21.144A7.274 4.365 45 0 0 2.856 10.856a7.274 4.365 45 0 0 10.288 10.288"></path><path d="M16.565 10.435 18.6 8.4a2.501 2.501 0 1 0 1.65-4.65 2.5 2.5 0 1 0-4.66 1.66l-2.024 2.025"></path><path d="m8.5 16.5-1-1"></path></g>',
  },
  'lucide-hamburger': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16H4a2 2 0 1 1 0-4h16a2 2 0 1 1 0 4h-4.25"></path><path d="M5 12a2 2 0 0 1-2-2 9 7 0 0 1 18 0 2 2 0 0 1-2 2"></path><path d="M5 16a2 2 0 0 0-2 2 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 2 2 0 0 0-2-2q0 0 0 0"></path><path d="m6.67 12 6.13 4.6a2 2 0 0 0 2.8-.4l3.15-4.2"></path></g>',
  },
  'lucide-hammer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9"></path><path d="m18 15 4-4"></path><path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5"></path></g>',
  },
  'lucide-hand-coins': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"></path><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"></path><path d="m2 16 6 6"></path><circle cx="16" cy="9" r="2.9"></circle><circle cx="6" cy="5" r="3"></circle></g>',
  },
  'lucide-hand-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 14h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16"></path><path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"></path><path d="m2 15 6 6"></path><path d="M19.5 8.5c.7-.7 1.5-1.6 1.5-2.7A2.73 2.73 0 0 0 16 4a2.78 2.78 0 0 0-5 1.8c0 1.2.8 2 1.5 2.8L16 12Z"></path></g>',
  },
  'lucide-hand-helping': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"></path><path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"></path><path d="m2 13 6 6"></path></g>',
  },
  'lucide-hand-metal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 12.5V10a2 2 0 0 0-2-2a2 2 0 0 0-2 2v1.4"></path><path d="M14 11V9a2 2 0 1 0-4 0v2"></path><path d="M10 10.5V5a2 2 0 1 0-4 0v9"></path><path d="m7 15-1.76-1.76a2 2 0 0 0-2.83 2.82l3.6 3.6C7.5 21.14 9.2 22 12 22h2a8 8 0 0 0 8-8V7a2 2 0 1 0-4 0v5"></path></g>',
  },
  'lucide-hand-platter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3V2"></path><path d="m15.4 17.4 3.2-2.8a2 2 0 1 1 2.8 2.9l-3.6 3.3c-.7.8-1.7 1.2-2.8 1.2h-4c-1.1 0-2.1-.4-2.8-1.2l-1.302-1.464A1 1 0 0 0 6.151 19H5"></path><path d="M2 14h12a2 2 0 0 1 0 4h-2"></path><path d="M4 10h16"></path><path d="M5 10a7 7 0 0 1 14 0"></path><path d="M5 14v6a1 1 0 0 1-1 1H2"></path></g>',
  },
  'lucide-hand': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"></path><path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"></path><path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"></path><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"></path></g>',
  },
  'lucide-handshake': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 17 2 2a1 1 0 1 0 3-3"></path><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"></path><path d="m21 3 1 11h-2"></path><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"></path><path d="M3 4h8"></path></g>',
  },
  'lucide-hard-drive-download': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v8"></path><path d="m16 6-4 4-4-4"></path><rect width="20" height="8" x="2" y="14" rx="2"></rect><path d="M6 18h.01"></path><path d="M10 18h.01"></path></g>',
  },
  'lucide-hard-drive-upload': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 6-4-4-4 4"></path><path d="M12 2v8"></path><rect width="20" height="8" x="2" y="14" rx="2"></rect><path d="M6 18h.01"></path><path d="M10 18h.01"></path></g>',
  },
  'lucide-hard-drive': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="22" x2="2" y1="12" y2="12"></line><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path><line x1="6" x2="6.01" y1="16" y2="16"></line><line x1="10" x2="10.01" y1="16" y2="16"></line></g>',
  },
  'lucide-hard-hat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5"></path><path d="M14 6a6 6 0 0 1 6 6v3"></path><path d="M4 15v-3a6 6 0 0 1 6-6"></path><rect x="2" y="15" width="20" height="4" rx="1"></rect></g>',
  },
  'lucide-hash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="9" y2="9"></line><line x1="4" x2="20" y1="15" y2="15"></line><line x1="10" x2="8" y1="3" y2="21"></line><line x1="16" x2="14" y1="3" y2="21"></line></g>',
  },
  'lucide-haze': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5.2 6.2 1.4 1.4"></path><path d="M2 13h2"></path><path d="M20 13h2"></path><path d="m17.4 7.6 1.4-1.4"></path><path d="M22 17H2"></path><path d="M22 21H2"></path><path d="M16 13a4 4 0 0 0-8 0"></path><path d="M12 5V2.5"></path></g>',
  },
  'lucide-hdmi-port': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 9a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h1l2 2h12l2-2h1a1 1 0 0 0 1-1Z"></path><path d="M7.5 12h9"></path></g>',
  },
  'lucide-heading-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h8"></path><path d="M4 18V6"></path><path d="M12 18V6"></path><path d="m17 12 3-2v8"></path></g>',
  },
  'lucide-heading-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h8"></path><path d="M4 18V6"></path><path d="M12 18V6"></path><path d="M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1"></path></g>',
  },
  'lucide-heading-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h8"></path><path d="M4 18V6"></path><path d="M12 18V6"></path><path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2"></path><path d="M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2"></path></g>',
  },
  'lucide-heading-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 18V6"></path><path d="M17 10v3a1 1 0 0 0 1 1h3"></path><path d="M21 10v8"></path><path d="M4 12h8"></path><path d="M4 18V6"></path></g>',
  },
  'lucide-heading-5': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h8"></path><path d="M4 18V6"></path><path d="M12 18V6"></path><path d="M17 13v-3h4"></path><path d="M17 17.7c.4.2.8.3 1.3.3 1.5 0 2.7-1.1 2.7-2.5S19.8 13 18.3 13H17"></path></g>',
  },
  'lucide-heading-6': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h8"></path><path d="M4 18V6"></path><path d="M12 18V6"></path><circle cx="19" cy="16" r="2"></circle><path d="M20 10c-2 2-3 3.5-3 6"></path></g>',
  },
  'lucide-heading': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 12h12"></path><path d="M6 20V4"></path><path d="M18 20V4"></path></g>',
  },
  'lucide-headphone-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 14h-1.343"></path><path d="M9.128 3.47A9 9 0 0 1 21 12v3.343"></path><path d="m2 2 20 20"></path><path d="M20.414 20.414A2 2 0 0 1 19 21h-1a2 2 0 0 1-2-2v-3"></path><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 2.636-6.364"></path></g>',
  },
  'lucide-headphones': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"></path></g>',
  },
  'lucide-headset': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z"></path><path d="M21 16v2a4 4 0 0 1-4 4h-5"></path></g>',
  },
  'lucide-heart-crack': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path><path d="m12 13-1-1 2-2-3-3 2-2"></path></g>',
  },
  'lucide-heart-handshake': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path><path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66"></path><path d="m18 15-2-2"></path><path d="m15 18-2-2"></path></g>',
  },
  'lucide-heart-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 19.5 12 21l-7-7c-1.5-1.45-3-3.2-3-5.5A5.5 5.5 0 0 1 7.5 3c1.76 0 3 .5 4.5 2 1.5-1.5 2.74-2 4.5-2a5.5 5.5 0 0 1 5.402 6.5"></path><path d="M15 15h6"></path></g>',
  },
  'lucide-heart-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" y1="2" x2="22" y2="22"></line><path d="M16.5 16.5 12 21l-7-7c-1.5-1.45-3-3.2-3-5.5a5.5 5.5 0 0 1 2.14-4.35"></path><path d="M8.76 3.1c1.15.22 2.13.78 3.24 1.9 1.5-1.5 2.74-2 4.5-2A5.5 5.5 0 0 1 22 8.5c0 2.12-1.3 3.78-2.67 5.17"></path></g>',
  },
  'lucide-heart-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 19.5 12 21l-7-7c-1.5-1.45-3-3.2-3-5.5A5.5 5.5 0 0 1 7.5 3c1.76 0 3 .5 4.5 2 1.5-1.5 2.74-2 4.5-2a5.5 5.5 0 0 1 5.402 6.5"></path><path d="M15 15h6"></path><path d="M18 12v6"></path></g>',
  },
  'lucide-heart-pulse': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"></path></g>',
  },
  'lucide-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path></g>',
  },
  'lucide-heater': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 8c2-3-2-3 0-6"></path><path d="M15.5 8c2-3-2-3 0-6"></path><path d="M6 10h.01"></path><path d="M6 14h.01"></path><path d="M10 16v-4"></path><path d="M14 16v-4"></path><path d="M18 16v-4"></path><path d="M20 6a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3"></path><path d="M5 20v2"></path><path d="M19 20v2"></path></g>',
  },
  'lucide-hexagon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path></g>',
  },
  'lucide-highlighter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 11-6 6v3h9l3-3"></path><path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4"></path></g>',
  },
  'lucide-history': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M12 7v5l4 2"></path></g>',
  },
  'lucide-hop-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.82 16.12c1.69.6 3.91.79 5.18.85.28.01.53-.09.7-.27"></path><path d="M11.14 20.57c.52.24 2.44 1.12 4.08 1.37.46.06.86-.25.9-.71.12-1.52-.3-3.43-.5-4.28"></path><path d="M16.13 21.05c1.65.63 3.68.84 4.87.91a.9.9 0 0 0 .7-.26"></path><path d="M17.99 5.52a20.83 20.83 0 0 1 3.15 4.5.8.8 0 0 1-.68 1.13c-1.17.1-2.5.02-3.9-.25"></path><path d="M20.57 11.14c.24.52 1.12 2.44 1.37 4.08.04.3-.08.59-.31.75"></path><path d="M4.93 4.93a10 10 0 0 0-.67 13.4c.35.43.96.4 1.17-.12.69-1.71 1.07-5.07 1.07-6.71 1.34.45 3.1.9 4.88.62a.85.85 0 0 0 .48-.24"></path><path d="M5.52 17.99c1.05.95 2.91 2.42 4.5 3.15a.8.8 0 0 0 1.13-.68c.2-2.34-.33-5.3-1.57-8.28"></path><path d="M8.35 2.68a10 10 0 0 1 9.98 1.58c.43.35.4.96-.12 1.17-1.5.6-4.3.98-6.07 1.05"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-hop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.82 16.12c1.69.6 3.91.79 5.18.85.55.03 1-.42.97-.97-.06-1.27-.26-3.5-.85-5.18"></path><path d="M11.5 6.5c1.64 0 5-.38 6.71-1.07.52-.2.55-.82.12-1.17A10 10 0 0 0 4.26 18.33c.35.43.96.4 1.17-.12.69-1.71 1.07-5.07 1.07-6.71 1.34.45 3.1.9 4.88.62a.88.88 0 0 0 .73-.74c.3-2.14-.15-3.5-.61-4.88"></path><path d="M15.62 16.95c.2.85.62 2.76.5 4.28a.77.77 0 0 1-.9.7 16.64 16.64 0 0 1-4.08-1.36"></path><path d="M16.13 21.05c1.65.63 3.68.84 4.87.91a.9.9 0 0 0 .96-.96 17.68 17.68 0 0 0-.9-4.87"></path><path d="M16.94 15.62c.86.2 2.77.62 4.29.5a.77.77 0 0 0 .7-.9 16.64 16.64 0 0 0-1.36-4.08"></path><path d="M17.99 5.52a20.82 20.82 0 0 1 3.15 4.5.8.8 0 0 1-.68 1.13c-2.33.2-5.3-.32-8.27-1.57"></path><path d="M4.93 4.93 3 3a.7.7 0 0 1 0-1"></path><path d="M9.58 12.18c1.24 2.98 1.77 5.95 1.57 8.28a.8.8 0 0 1-1.13.68 20.82 20.82 0 0 1-4.5-3.15"></path></g>',
  },
  'lucide-hospital': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v4"></path><path d="M14 14h-4"></path><path d="M14 18h-4"></path><path d="M14 8h-4"></path><path d="M18 12h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2"></path><path d="M18 22V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v18"></path></g>',
  },
  'lucide-hotel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 22v-6.57"></path><path d="M12 11h.01"></path><path d="M12 7h.01"></path><path d="M14 15.43V22"></path><path d="M15 16a5 5 0 0 0-6 0"></path><path d="M16 11h.01"></path><path d="M16 7h.01"></path><path d="M8 11h.01"></path><path d="M8 7h.01"></path><rect x="4" y="2" width="16" height="20" rx="2"></rect></g>',
  },
  'lucide-hourglass': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></g>',
  },
  'lucide-house-plug': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12V8.964"></path><path d="M14 12V8.964"></path><path d="M15 12a1 1 0 0 1 1 1v2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2a1 1 0 0 1 1-1z"></path><path d="M8.5 21H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2h-5a2 2 0 0 1-2-2v-2"></path></g>',
  },
  'lucide-house-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.22 2.416a2 2 0 0 0-2.511.057l-7 5.999A2 2 0 0 0 3 10v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7.354"></path><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path><path d="M15 6h6"></path><path d="M18 3v6"></path></g>',
  },
  'lucide-house-wifi': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 13.866a4 4 0 0 1 5 .01"></path><path d="M12 17h.01"></path><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><path d="M7 10.754a8 8 0 0 1 10 0"></path></g>',
  },
  'lucide-house': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path></g>',
  },
  'lucide-ice-cream-bowl': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17c5 0 8-2.69 8-6H4c0 3.31 3 6 8 6m-4 4h8m-4-3v3M5.14 11a3.5 3.5 0 1 1 6.71 0"></path><path d="M12.14 11a3.5 3.5 0 1 1 6.71 0"></path><path d="M15.5 6.5a3.5 3.5 0 1 0-7 0"></path></g>',
  },
  'lucide-ice-cream-cone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 11 4.08 10.35a1 1 0 0 0 1.84 0L17 11"></path><path d="M17 7A5 5 0 0 0 7 7"></path><path d="M17 7a2 2 0 0 1 0 4H7a2 2 0 0 1 0-4"></path></g>',
  },
  'lucide-id-card': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 10h2"></path><path d="M16 14h2"></path><path d="M6.17 15a3 3 0 0 1 5.66 0"></path><circle cx="9" cy="11" r="2"></circle><rect x="2" y="5" width="20" height="14" rx="2"></rect></g>',
  },
  'lucide-image-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10l-3.1-3.1a2 2 0 0 0-2.814.014L6 21"></path><path d="m14 19 3 3v-5.5"></path><path d="m17 22 3-3"></path><circle cx="9" cy="9" r="2"></circle></g>',
  },
  'lucide-image-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"></path><line x1="16" x2="22" y1="5" y2="5"></line><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></g>',
  },
  'lucide-image-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"></line><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"></path><line x1="13.5" x2="6" y1="13.5" y2="21"></line><line x1="18" x2="21" y1="12" y2="15"></line><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.052-.22 1.41-.59"></path><path d="M21 15V5a2 2 0 0 0-2-2H9"></path></g>',
  },
  'lucide-image-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 16-5 5"></path><path d="M11 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6.5"></path><path d="M15.765 22a.5.5 0 0 1-.765-.424V13.38a.5.5 0 0 1 .765-.424l5.878 3.674a1 1 0 0 1 0 1.696z"></path><circle cx="9" cy="9" r="2"></circle></g>',
  },
  'lucide-image-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 5h6"></path><path d="M19 2v6"></path><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"></path><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path><circle cx="9" cy="9" r="2"></circle></g>',
  },
  'lucide-image-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10l-3.1-3.1a2 2 0 0 0-2.814.014L6 21"></path><path d="m14 19.5 3-3 3 3"></path><path d="M17 22v-5.5"></path><circle cx="9" cy="9" r="2"></circle></g>',
  },
  'lucide-image-upscale': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"></path><path d="M17 21h2a2 2 0 0 0 2-2"></path><path d="M21 12v3"></path><path d="m21 3-5 5"></path><path d="M3 7V5a2 2 0 0 1 2-2"></path><path d="m5 21 4.144-4.144a1.21 1.21 0 0 1 1.712 0L13 19"></path><path d="M9 3h3"></path><rect x="3" y="11" width="10" height="10" rx="1"></rect></g>',
  },
  'lucide-image': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></g>',
  },
  'lucide-images': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 22H4a2 2 0 0 1-2-2V6"></path><path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18"></path><circle cx="12" cy="8" r="2"></circle><rect width="16" height="16" x="6" y="2" rx="2"></rect></g>',
  },
  'lucide-import': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"></path><path d="m8 11 4 4 4-4"></path><path d="M8 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4"></path></g>',
  },
  'lucide-inbox': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path></g>',
  },
  'lucide-indent-decrease': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12H11"></path><path d="M21 18H11"></path><path d="M21 6H11"></path><path d="m7 8-4 4 4 4"></path></g>',
  },
  'lucide-indent-increase': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12H11"></path><path d="M21 18H11"></path><path d="M21 6H11"></path><path d="m3 8 4 4-4 4"></path></g>',
  },
  'lucide-indian-rupee': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12"></path><path d="M6 8h12"></path><path d="m6 13 8.5 8"></path><path d="M6 13h3"></path><path d="M9 13c6.667 0 6.667-10 0-10"></path></g>',
  },
  'lucide-infinity': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8"></path></g>',
  },
  'lucide-info': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></g>',
  },
  'lucide-inspection-panel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 7h.01"></path><path d="M17 7h.01"></path><path d="M7 17h.01"></path><path d="M17 17h.01"></path></g>',
  },
  'lucide-instagram': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line></g>',
  },
  'lucide-italic': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="19" x2="10" y1="4" y2="4"></line><line x1="14" x2="5" y1="20" y2="20"></line><line x1="15" x2="9" y1="4" y2="20"></line></g>',
  },
  'lucide-iteration-ccw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0-4.4-3.6-8-8-8s-8 3.6-8 8 3.6 8 8 8h8"></path><polyline points="16 14 20 18 16 22"></polyline></g>',
  },
  'lucide-iteration-cw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8H4"></path><polyline points="8 22 4 18 8 14"></polyline></g>',
  },
  'lucide-japanese-yen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9.5V21m0-11.5L6 3m6 6.5L18 3"></path><path d="M6 15h12"></path><path d="M6 11h12"></path></g>',
  },
  'lucide-joystick': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 17a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2Z"></path><path d="M6 15v-2"></path><path d="M12 15V9"></path><circle cx="12" cy="6" r="3"></circle></g>',
  },
  'lucide-kanban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 5v11"></path><path d="M12 5v6"></path><path d="M18 5v14"></path></g>',
  },
  'lucide-key-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle></g>',
  },
  'lucide-key-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.4 2.7a2.5 2.5 0 0 1 3.4 0l5.5 5.5a2.5 2.5 0 0 1 0 3.4l-3.7 3.7a2.5 2.5 0 0 1-3.4 0L8.7 9.8a2.5 2.5 0 0 1 0-3.4z"></path><path d="m14 7 3 3"></path><path d="m9.4 10.6-6.814 6.814A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814"></path></g>',
  },
  'lucide-key': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4"></path><path d="m21 2-9.6 9.6"></path><circle cx="7.5" cy="15.5" r="5.5"></circle></g>',
  },
  'lucide-keyboard-music': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M6 8h4"></path><path d="M14 8h.01"></path><path d="M18 8h.01"></path><path d="M2 12h20"></path><path d="M6 12v4"></path><path d="M10 12v4"></path><path d="M14 12v4"></path><path d="M18 12v4"></path></g>',
  },
  'lucide-keyboard-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M 20 4 A2 2 0 0 1 22 6"></path><path d="M 22 6 L 22 16.41"></path><path d="M 7 16 L 16 16"></path><path d="M 9.69 4 L 20 4"></path><path d="M14 8h.01"></path><path d="M18 8h.01"></path><path d="m2 2 20 20"></path><path d="M20 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2"></path><path d="M6 8h.01"></path><path d="M8 12h.01"></path></g>',
  },
  'lucide-keyboard': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 8h.01"></path><path d="M12 12h.01"></path><path d="M14 8h.01"></path><path d="M16 12h.01"></path><path d="M18 8h.01"></path><path d="M6 8h.01"></path><path d="M7 16h10"></path><path d="M8 12h.01"></path><rect width="20" height="16" x="2" y="4" rx="2"></rect></g>',
  },
  'lucide-lamp-ceiling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v5"></path><path d="M14.829 15.998a3 3 0 1 1-5.658 0"></path><path d="M20.92 14.606A1 1 0 0 1 20 16H4a1 1 0 0 1-.92-1.394l3-7A1 1 0 0 1 7 7h10a1 1 0 0 1 .92.606z"></path></g>',
  },
  'lucide-lamp-desk': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.293 2.293a1 1 0 0 1 1.414 0l2.5 2.5 5.994 1.227a1 1 0 0 1 .506 1.687l-7 7a1 1 0 0 1-1.687-.506l-1.227-5.994-2.5-2.5a1 1 0 0 1 0-1.414z"></path><path d="m14.207 4.793-3.414 3.414"></path><path d="M3 20a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"></path><path d="m9.086 6.5-4.793 4.793a1 1 0 0 0-.18 1.17L7 18"></path></g>',
  },
  'lucide-lamp-floor': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 10v12"></path><path d="M17.929 7.629A1 1 0 0 1 17 9H7a1 1 0 0 1-.928-1.371l2-5A1 1 0 0 1 9 2h6a1 1 0 0 1 .928.629z"></path><path d="M9 22h6"></path></g>',
  },
  'lucide-lamp-wall-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.929 18.629A1 1 0 0 1 19 20H9a1 1 0 0 1-.928-1.371l2-5A1 1 0 0 1 11 13h6a1 1 0 0 1 .928.629z"></path><path d="M6 3a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"></path><path d="M8 6h4a2 2 0 0 1 2 2v5"></path></g>',
  },
  'lucide-lamp-wall-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.929 9.629A1 1 0 0 1 19 11H9a1 1 0 0 1-.928-1.371l2-5A1 1 0 0 1 11 4h6a1 1 0 0 1 .928.629z"></path><path d="M6 15a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z"></path><path d="M8 18h4a2 2 0 0 0 2-2v-5"></path></g>',
  },
  'lucide-lamp': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12v6"></path><path d="M4.077 10.615A1 1 0 0 0 5 12h14a1 1 0 0 0 .923-1.385l-3.077-7.384A2 2 0 0 0 15 2H9a2 2 0 0 0-1.846 1.23Z"></path><path d="M8 20a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1z"></path></g>',
  },
  'lucide-land-plot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 8 6-3-6-3v10"></path><path d="m8 11.99-5.5 3.14a1 1 0 0 0 0 1.74l8.5 4.86a2 2 0 0 0 2 0l8.5-4.86a1 1 0 0 0 0-1.74L16 12"></path><path d="m6.49 12.85 11.02 6.3"></path><path d="M17.51 12.85 6.5 19.15"></path></g>',
  },
  'lucide-landmark': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="22" y2="22"></line><line x1="6" x2="6" y1="18" y2="11"></line><line x1="10" x2="10" y1="18" y2="11"></line><line x1="14" x2="14" y1="18" y2="11"></line><line x1="18" x2="18" y1="18" y2="11"></line><polygon points="12 2 20 7 4 7"></polygon></g>',
  },
  'lucide-languages': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 8 6 6"></path><path d="m4 14 6-6 2-3"></path><path d="M2 5h12"></path><path d="M7 2h1"></path><path d="m22 22-5-10-5 10"></path><path d="M14 18h6"></path></g>',
  },
  'lucide-laptop-minimal-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h20"></path><path d="m9 10 2 2 4-4"></path><rect x="3" y="4" width="18" height="12" rx="2"></rect></g>',
  },
  'lucide-laptop-minimal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="12" x="3" y="4" rx="2" ry="2"></rect><line x1="2" x2="22" y1="20" y2="20"></line></g>',
  },
  'lucide-laptop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16"></path></g>',
  },
  'lucide-lasso-select': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 22a5 5 0 0 1-2-4"></path><path d="M7 16.93c.96.43 1.96.74 2.99.91"></path><path d="M3.34 14A6.8 6.8 0 0 1 2 10c0-4.42 4.48-8 10-8s10 3.58 10 8a7.19 7.19 0 0 1-.33 2"></path><path d="M5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"></path><path d="M14.33 22h-.09a.35.35 0 0 1-.24-.32v-10a.34.34 0 0 1 .33-.34c.08 0 .15.03.21.08l7.34 6a.33.33 0 0 1-.21.59h-4.49l-2.57 3.85a.35.35 0 0 1-.28.14z"></path></g>',
  },
  'lucide-lasso': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 22a5 5 0 0 1-2-4"></path><path d="M3.3 14A6.8 6.8 0 0 1 2 10c0-4.4 4.5-8 10-8s10 3.6 10 8-4.5 8-10 8a12 12 0 0 1-5-1"></path><path d="M5 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"></path></g>',
  },
  'lucide-laugh': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M18 13a6 6 0 0 1-6 5 6 6 0 0 1-6-5h12Z"></path><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line></g>',
  },
  'lucide-layers-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 13.74a2 2 0 0 1-2 0L2.5 8.87a1 1 0 0 1 0-1.74L11 2.26a2 2 0 0 1 2 0l8.5 4.87a1 1 0 0 1 0 1.74z"></path><path d="m20 14.285 1.5.845a1 1 0 0 1 0 1.74L13 21.74a2 2 0 0 1-2 0l-8.5-4.87a1 1 0 0 1 0-1.74l1.5-.845"></path></g>',
  },
  'lucide-layers': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"></path><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"></path><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"></path></g>',
  },
  'lucide-layout-dashboard': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"></rect><rect width="7" height="5" x="14" y="3" rx="1"></rect><rect width="7" height="9" x="14" y="12" rx="1"></rect><rect width="7" height="5" x="3" y="16" rx="1"></rect></g>',
  },
  'lucide-layout-grid': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"></rect><rect width="7" height="7" x="14" y="3" rx="1"></rect><rect width="7" height="7" x="14" y="14" rx="1"></rect><rect width="7" height="7" x="3" y="14" rx="1"></rect></g>',
  },
  'lucide-layout-list': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"></rect><rect width="7" height="7" x="3" y="14" rx="1"></rect><path d="M14 4h7"></path><path d="M14 9h7"></path><path d="M14 15h7"></path><path d="M14 20h7"></path></g>',
  },
  'lucide-layout-panel-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="18" x="3" y="3" rx="1"></rect><rect width="7" height="7" x="14" y="3" rx="1"></rect><rect width="7" height="7" x="14" y="14" rx="1"></rect></g>',
  },
  'lucide-layout-panel-top': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="7" x="3" y="3" rx="1"></rect><rect width="7" height="7" x="3" y="14" rx="1"></rect><rect width="7" height="7" x="14" y="14" rx="1"></rect></g>',
  },
  'lucide-layout-template': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="7" x="3" y="3" rx="1"></rect><rect width="9" height="7" x="3" y="14" rx="1"></rect><rect width="5" height="7" x="16" y="14" rx="1"></rect></g>',
  },
  'lucide-leaf': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path></g>',
  },
  'lucide-leafy-green': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22c1.25-.987 2.27-1.975 3.9-2.2a5.56 5.56 0 0 1 3.8 1.5 4 4 0 0 0 6.187-2.353 3.5 3.5 0 0 0 3.69-5.116A3.5 3.5 0 0 0 20.95 8 3.5 3.5 0 1 0 16 3.05a3.5 3.5 0 0 0-5.831 1.373 3.5 3.5 0 0 0-5.116 3.69 4 4 0 0 0-2.348 6.155C3.499 15.42 4.409 16.712 4.2 18.1 3.926 19.743 3.014 20.732 2 22"></path><path d="M2 22 17 7"></path></g>',
  },
  'lucide-lectern': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12h3a2 2 0 0 0 1.902-1.38l1.056-3.333A1 1 0 0 0 21 6H3a1 1 0 0 0-.958 1.287l1.056 3.334A2 2 0 0 0 5 12h3"></path><path d="M18 6V3a1 1 0 0 0-1-1h-3"></path><rect width="8" height="12" x="8" y="10" rx="1"></rect></g>',
  },
  'lucide-letter-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h6"></path><path d="M15 6h6"></path><path d="m3 13 3.553-7.724a.5.5 0 0 1 .894 0L11 13"></path><path d="M3 18h18"></path><path d="M3.92 11h6.16"></path></g>',
  },
  'lucide-library-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="18" x="3" y="3" rx="1"></rect><path d="M7 3v18"></path><path d="M20.4 18.9c.2.5-.1 1.1-.6 1.3l-1.9.7c-.5.2-1.1-.1-1.3-.6L11.1 5.1c-.2-.5.1-1.1.6-1.3l1.9-.7c.5-.2 1.1.1 1.3.6Z"></path></g>',
  },
  'lucide-library': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 6 4 14"></path><path d="M12 6v14"></path><path d="M8 8v12"></path><path d="M4 4v16"></path></g>',
  },
  'lucide-life-buoy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m4.93 4.93 4.24 4.24"></path><path d="m14.83 9.17 4.24-4.24"></path><path d="m14.83 14.83 4.24 4.24"></path><path d="m9.17 14.83-4.24 4.24"></path><circle cx="12" cy="12" r="4"></circle></g>',
  },
  'lucide-ligature': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 20V8c0-2.2 1.8-4 4-4 1.5 0 2.8.8 3.5 2"></path><path d="M6 12h4"></path><path d="M14 12h2v8"></path><path d="M6 20h4"></path><path d="M14 20h4"></path></g>',
  },
  'lucide-lightbulb-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.8 11.2c.8-.9 1.2-2 1.2-3.2a6 6 0 0 0-9.3-5"></path><path d="m2 2 20 20"></path><path d="M6.3 6.3a4.67 4.67 0 0 0 1.2 5.2c.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></g>',
  },
  'lucide-lightbulb': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></g>',
  },
  'lucide-link-2-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7"></path><path d="M15 7h2a5 5 0 0 1 4 8"></path><line x1="8" x2="12" y1="12" y2="12"></line><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-link-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"></path><path d="M15 7h2a5 5 0 1 1 0 10h-2"></path><line x1="8" x2="16" y1="12" y2="12"></line></g>',
  },
  'lucide-link': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></g>',
  },
  'lucide-linkedin': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></g>',
  },
  'lucide-list-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 18H3"></path><path d="m15 18 2 2 4-4"></path><path d="M16 12H3"></path><path d="M16 6H3"></path></g>',
  },
  'lucide-list-checks': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 17 2 2 4-4"></path><path d="m3 7 2 2 4-4"></path><path d="M13 6h8"></path><path d="M13 12h8"></path><path d="M13 18h8"></path></g>',
  },
  'lucide-list-collapse': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 10 2.5-2.5L3 5"></path><path d="m3 19 2.5-2.5L3 14"></path><path d="M10 6h11"></path><path d="M10 12h11"></path><path d="M10 18h11"></path></g>',
  },
  'lucide-list-end': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12H3"></path><path d="M16 6H3"></path><path d="M10 18H3"></path><path d="M21 6v10a2 2 0 0 1-2 2h-5"></path><path d="m16 16-2 2 2 2"></path></g>',
  },
  'lucide-list-filter-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 18h4"></path><path d="M11 6H3"></path><path d="M15 6h6"></path><path d="M18 9V3"></path><path d="M7 12h8"></path></g>',
  },
  'lucide-list-filter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M7 12h10"></path><path d="M10 18h4"></path></g>',
  },
  'lucide-list-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12H3"></path><path d="M16 6H3"></path><path d="M16 18H3"></path><path d="M21 12h-6"></path></g>',
  },
  'lucide-list-music': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15V6"></path><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"></path><path d="M12 12H3"></path><path d="M16 6H3"></path><path d="M12 18H3"></path></g>',
  },
  'lucide-list-ordered': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12h11"></path><path d="M10 18h11"></path><path d="M10 6h11"></path><path d="M4 10h2"></path><path d="M4 6h1v4"></path><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path></g>',
  },
  'lucide-list-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12H3"></path><path d="M16 6H3"></path><path d="M16 18H3"></path><path d="M18 9v6"></path><path d="M21 12h-6"></path></g>',
  },
  'lucide-list-restart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 6H3"></path><path d="M7 12H3"></path><path d="M7 18H3"></path><path d="M12 18a5 5 0 0 0 9-3 4.5 4.5 0 0 0-4.5-4.5c-1.33 0-2.54.54-3.41 1.41L11 14"></path><path d="M11 10v4h4"></path></g>',
  },
  'lucide-list-start': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12H3"></path><path d="M16 18H3"></path><path d="M10 6H3"></path><path d="M21 18V8a2 2 0 0 0-2-2h-5"></path><path d="m16 8-2-2 2-2"></path></g>',
  },
  'lucide-list-todo': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="6" height="6" rx="1"></rect><path d="m3 17 2 2 4-4"></path><path d="M13 6h8"></path><path d="M13 12h8"></path><path d="M13 18h8"></path></g>',
  },
  'lucide-list-tree': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12h-8"></path><path d="M21 6H8"></path><path d="M21 18h-8"></path><path d="M3 6v4c0 1.1.9 2 2 2h3"></path><path d="M3 10v6c0 1.1.9 2 2 2h3"></path></g>',
  },
  'lucide-list-video': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12H3"></path><path d="M16 6H3"></path><path d="M12 18H3"></path><path d="m16 12 5 3-5 3v-6Z"></path></g>',
  },
  'lucide-list-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12H3"></path><path d="M16 6H3"></path><path d="M16 18H3"></path><path d="m19 10-4 4"></path><path d="m15 10 4 4"></path></g>',
  },
  'lucide-list': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h.01"></path><path d="M3 18h.01"></path><path d="M3 6h.01"></path><path d="M8 12h13"></path><path d="M8 18h13"></path><path d="M8 6h13"></path></g>',
  },
  'lucide-loader-circle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></g>',
  },
  'lucide-loader-pinwheel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12a1 1 0 0 1-10 0 1 1 0 0 0-10 0"></path><path d="M7 20.7a1 1 0 1 1 5-8.7 1 1 0 1 0 5-8.6"></path><path d="M7 3.3a1 1 0 1 1 5 8.6 1 1 0 1 0 5 8.6"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-loader': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4"></path><path d="m16.2 7.8 2.9-2.9"></path><path d="M18 12h4"></path><path d="m16.2 16.2 2.9 2.9"></path><path d="M12 18v4"></path><path d="m4.9 19.1 2.9-2.9"></path><path d="M2 12h4"></path><path d="m4.9 4.9 2.9 2.9"></path></g>',
  },
  'lucide-locate-fixed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="5" y1="12" y2="12"></line><line x1="19" x2="22" y1="12" y2="12"></line><line x1="12" x2="12" y1="2" y2="5"></line><line x1="12" x2="12" y1="19" y2="22"></line><circle cx="12" cy="12" r="7"></circle><circle cx="12" cy="12" r="3"></circle></g>',
  },
  'lucide-locate-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19v3"></path><path d="M12 2v3"></path><path d="M18.89 13.24a7 7 0 0 0-8.13-8.13"></path><path d="M19 12h3"></path><path d="M2 12h3"></path><path d="m2 2 20 20"></path><path d="M7.05 7.05a7 7 0 0 0 9.9 9.9"></path></g>',
  },
  'lucide-locate': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="5" y1="12" y2="12"></line><line x1="19" x2="22" y1="12" y2="12"></line><line x1="12" x2="12" y1="2" y2="5"></line><line x1="12" x2="12" y1="19" y2="22"></line><circle cx="12" cy="12" r="7"></circle></g>',
  },
  'lucide-location-edit': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.97 9.304A8 8 0 0 0 2 10c0 4.69 4.887 9.562 7.022 11.468"></path><path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path><circle cx="10" cy="10" r="3"></circle></g>',
  },
  'lucide-lock-keyhole-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="16" r="1"></circle><rect width="18" height="12" x="3" y="10" rx="2"></rect><path d="M7 10V7a5 5 0 0 1 9.33-2.5"></path></g>',
  },
  'lucide-lock-keyhole': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="16" r="1"></circle><rect x="3" y="10" width="18" height="12" rx="2"></rect><path d="M7 10V7a5 5 0 0 1 10 0v3"></path></g>',
  },
  'lucide-lock-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></g>',
  },
  'lucide-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></g>',
  },
  'lucide-log-in': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" x2="3" y1="12" y2="12"></line></g>',
  },
  'lucide-log-out': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" x2="9" y1="12" y2="12"></line></g>',
  },
  'lucide-logs': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 12h8"></path><path d="M13 18h8"></path><path d="M13 6h8"></path><path d="M3 12h1"></path><path d="M3 18h1"></path><path d="M3 6h1"></path><path d="M8 12h1"></path><path d="M8 18h1"></path><path d="M8 6h1"></path></g>',
  },
  'lucide-lollipop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path><path d="M11 11a2 2 0 0 0 4 0 4 4 0 0 0-8 0 6 6 0 0 0 12 0"></path></g>',
  },
  'lucide-luggage': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 20a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2"></path><path d="M8 18V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v14"></path><path d="M10 20h4"></path><circle cx="16" cy="20" r="2"></circle><circle cx="8" cy="20" r="2"></circle></g>',
  },
  'lucide-magnet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 15-4-4 6.75-6.77a7.79 7.79 0 0 1 11 11L13 22l-4-4 6.39-6.36a2.14 2.14 0 0 0-3-3L6 15"></path><path d="m5 8 4 4"></path><path d="m12 15 4 4"></path></g>',
  },
  'lucide-mail-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="m16 19 2 2 4-4"></path></g>',
  },
  'lucide-mail-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 15V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="M16 19h6"></path></g>',
  },
  'lucide-mail-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z"></path><path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10"></path></g>',
  },
  'lucide-mail-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h8"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="M19 16v6"></path><path d="M16 19h6"></path></g>',
  },
  'lucide-mail-question': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h12.5"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="M18 15.28c.2-.4.5-.8.9-1a2.1 2.1 0 0 1 2.6.4c.3.4.5.8.5 1.3 0 1.3-2 2-2 2"></path><path d="M20 22v.01"></path></g>',
  },
  'lucide-mail-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h7.5"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="M18 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"></path><circle cx="18" cy="18" r="3"></circle><path d="m22 22-1.5-1.5"></path></g>',
  },
  'lucide-mail-warning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h12.5"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="M20 14v4"></path><path d="M20 22v.01"></path></g>',
  },
  'lucide-mail-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h9"></path><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path><path d="m17 17 4 4"></path><path d="m21 17-4 4"></path></g>',
  },
  'lucide-mail': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect></g>',
  },
  'lucide-mailbox': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9.5C2 7 4 5 6.5 5H18c2.2 0 4 1.8 4 4v8Z"></path><polyline points="15,9 18,9 18,11"></polyline><path d="M6.5 5C9 5 11 7 11 9.5V17a2 2 0 0 1-2 2"></path><line x1="6" x2="7" y1="10" y2="10"></line></g>',
  },
  'lucide-mails': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="13" x="6" y="4" rx="2"></rect><path d="m22 7-7.1 3.78c-.57.3-1.23.3-1.8 0L6 7"></path><path d="M2 8v11c0 1.1.9 2 2 2h14"></path></g>',
  },
  'lucide-map-pin-check-inside': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><path d="m9 10 2 2 4-4"></path></g>',
  },
  'lucide-map-pin-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.43 12.935c.357-.967.57-1.955.57-2.935a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 1.202 0 32.197 32.197 0 0 0 .813-.728"></path><circle cx="12" cy="10" r="3"></circle><path d="m16 18 2 2 4-4"></path></g>',
  },
  'lucide-map-pin-house': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 22a1 1 0 0 1-1-1v-4a1 1 0 0 1 .445-.832l3-2a1 1 0 0 1 1.11 0l3 2A1 1 0 0 1 22 17v4a1 1 0 0 1-1 1z"></path><path d="M18 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 .601.2"></path><path d="M18 22v-3"></path><circle cx="10" cy="10" r="3"></circle></g>',
  },
  'lucide-map-pin-minus-inside': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-map-pin-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.977 14C19.6 12.701 20 11.343 20 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 1.202 0 32 32 0 0 0 .824-.738"></path><circle cx="12" cy="10" r="3"></circle><path d="M16 18h6"></path></g>',
  },
  'lucide-map-pin-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.75 7.09a3 3 0 0 1 2.16 2.16"></path><path d="M17.072 17.072c-1.634 2.17-3.527 3.912-4.471 4.727a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 1.432-4.568"></path><path d="m2 2 20 20"></path><path d="M8.475 2.818A8 8 0 0 1 20 10c0 1.183-.31 2.377-.81 3.533"></path><path d="M9.13 9.13a3 3 0 0 0 3.74 3.74"></path></g>',
  },
  'lucide-map-pin-plus-inside': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><path d="M12 7v6"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-map-pin-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.914 11.105A7.298 7.298 0 0 0 20 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 1.202 0 32 32 0 0 0 .824-.738"></path><circle cx="12" cy="10" r="3"></circle><path d="M16 18h6"></path><path d="M19 15v6"></path></g>',
  },
  'lucide-map-pin-x-inside': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><path d="m14.5 7.5-5 5"></path><path d="m9.5 7.5 5 5"></path></g>',
  },
  'lucide-map-pin-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.752 11.901A7.78 7.78 0 0 0 20 10a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 1.202 0 19 19 0 0 0 .09-.077"></path><circle cx="12" cy="10" r="3"></circle><path d="m21.5 15.5-5 5"></path><path d="m21.5 20.5-5-5"></path></g>',
  },
  'lucide-map-pin': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path><circle cx="12" cy="10" r="3"></circle></g>',
  },
  'lucide-map-pinned': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 0 1-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0 1 12 0"></path><circle cx="12" cy="8" r="2"></circle><path d="M8.714 14h-3.71a1 1 0 0 0-.948.683l-2.004 6A1 1 0 0 0 3 22h18a1 1 0 0 0 .948-1.316l-2-6a1 1 0 0 0-.949-.684h-3.712"></path></g>',
  },
  'lucide-map-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 19-1.106-.552a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0l4.212 2.106a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619V12"></path><path d="M15 5.764V12"></path><path d="M18 15v6"></path><path d="M21 18h-6"></path><path d="M9 3.236v15"></path></g>',
  },
  'lucide-map': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z"></path><path d="M15 5.764v15"></path><path d="M9 3.236v15"></path></g>',
  },
  'lucide-mars-stroke': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14 6 4 4"></path><path d="M17 3h4v4"></path><path d="m21 3-7.75 7.75"></path><circle cx="9" cy="15" r="6"></circle></g>',
  },
  'lucide-mars': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"></path><path d="m21 3-6.75 6.75"></path><circle cx="10" cy="14" r="6"></circle></g>',
  },
  'lucide-martini': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 22h8"></path><path d="M12 11v11"></path><path d="m19 3-7 8-7-8Z"></path></g>',
  },
  'lucide-maximize-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" x2="14" y1="3" y2="10"></line><line x1="3" x2="10" y1="21" y2="14"></line></g>',
  },
  'lucide-maximize': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"></path><path d="M21 8V5a2 2 0 0 0-2-2h-3"></path><path d="M3 16v3a2 2 0 0 0 2 2h3"></path><path d="M16 21h3a2 2 0 0 0 2-2v-3"></path></g>',
  },
  'lucide-medal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15"></path><path d="M11 12 5.12 2.2"></path><path d="m13 12 5.88-9.8"></path><path d="M8 7h8"></path><circle cx="12" cy="17" r="5"></circle><path d="M12 18v-2h-.5"></path></g>',
  },
  'lucide-megaphone-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.26 9.26 3 11v3l14.14 3.14"></path><path d="M21 15.34V6l-7.31 2.03"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-megaphone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m3 11 18-5v12L3 14v-3z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></g>',
  },
  'lucide-meh': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="8" x2="16" y1="15" y2="15"></line><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line></g>',
  },
  'lucide-memory-stick': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 19v-3"></path><path d="M10 19v-3"></path><path d="M14 19v-3"></path><path d="M18 19v-3"></path><path d="M8 11V9"></path><path d="M16 11V9"></path><path d="M12 11V9"></path><path d="M2 15h20"></path><path d="M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1.1a2 2 0 0 0 0 3.837V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5.1a2 2 0 0 0 0-3.837Z"></path></g>',
  },
  'lucide-menu': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h16"></path><path d="M4 18h16"></path><path d="M4 6h16"></path></g>',
  },
  'lucide-merge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m8 6 4-4 4 4"></path><path d="M12 2v10.3a4 4 0 0 1-1.172 2.872L4 22"></path><path d="m20 22-5-5"></path></g>',
  },
  'lucide-message-circle-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9.5 8 12l2 2.5"></path><path d="m14 9.5 2 2.5-2 2.5"></path><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"></path></g>',
  },
  'lucide-message-circle-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 3.1c-.5 0-1-.1-1.5-.1s-1 .1-1.5.1"></path><path d="M19.3 6.8a10.45 10.45 0 0 0-2.1-2.1"></path><path d="M20.9 13.5c.1-.5.1-1 .1-1.5s-.1-1-.1-1.5"></path><path d="M17.2 19.3a10.45 10.45 0 0 0 2.1-2.1"></path><path d="M10.5 20.9c.5.1 1 .1 1.5.1s1-.1 1.5-.1"></path><path d="M3.5 17.5 2 22l4.5-1.5"></path><path d="M3.1 10.5c0 .5-.1 1-.1 1.5s.1 1 .1 1.5"></path><path d="M6.8 4.7a10.45 10.45 0 0 0-2.1 2.1"></path></g>',
  },
  'lucide-message-circle-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="M15.8 9.2a2.5 2.5 0 0 0-3.5 0l-.3.4-.35-.3a2.42 2.42 0 1 0-3.2 3.6l3.6 3.5 3.6-3.5c1.2-1.2 1.1-2.7.2-3.7"></path></g>',
  },
  'lucide-message-circle-more': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="M8 12h.01"></path><path d="M12 12h.01"></path><path d="M16 12h.01"></path></g>',
  },
  'lucide-message-circle-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.5 14.9A9 9 0 0 0 9.1 3.5"></path><path d="m2 2 20 20"></path><path d="M5.6 5.6C3 8.3 2.2 12.5 4 16l-2 6 6-2c3.4 1.8 7.6 1.1 10.3-1.7"></path></g>',
  },
  'lucide-message-circle-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="M8 12h8"></path><path d="M12 8v8"></path></g>',
  },
  'lucide-message-circle-question': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></g>',
  },
  'lucide-message-circle-reply': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="m10 15-3-3 3-3"></path><path d="M7 12h7a2 2 0 0 1 2 2v1"></path></g>',
  },
  'lucide-message-circle-warning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="M12 8v4"></path><path d="M12 16h.01"></path></g>',
  },
  'lucide-message-circle-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></g>',
  },
  'lucide-message-circle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path></g>',
  },
  'lucide-message-square-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 7.5 8 10l2 2.5"></path><path d="m14 7.5 2 2.5-2 2.5"></path><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></g>',
  },
  'lucide-message-square-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17H7l-4 4v-7"></path><path d="M14 17h1"></path><path d="M14 3h1"></path><path d="M19 3a2 2 0 0 1 2 2"></path><path d="M21 14v1a2 2 0 0 1-2 2"></path><path d="M21 9v1"></path><path d="M3 9v1"></path><path d="M5 3a2 2 0 0 0-2 2"></path><path d="M9 3h1"></path></g>',
  },
  'lucide-message-square-diff': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 19-2 2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2"></path><path d="M9 10h6"></path><path d="M12 7v6"></path><path d="M9 17h6"></path></g>',
  },
  'lucide-message-square-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.7 3H5a2 2 0 0 0-2 2v16l4-4h12a2 2 0 0 0 2-2v-2.7"></path><circle cx="18" cy="6" r="3"></circle></g>',
  },
  'lucide-message-square-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M14.8 7.5a1.84 1.84 0 0 0-2.6 0l-.2.3-.3-.3a1.84 1.84 0 1 0-2.4 2.8L12 13l2.7-2.7c.9-.9.8-2.1.1-2.8"></path></g>',
  },
  'lucide-message-square-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 15v-2a2 2 0 1 0-4 0v2"></path><path d="M9 17H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3.5"></path><rect x="13" y="15" width="8" height="5" rx="1"></rect></g>',
  },
  'lucide-message-square-more': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M8 10h.01"></path><path d="M12 10h.01"></path><path d="M16 10h.01"></path></g>',
  },
  'lucide-message-square-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15V5a2 2 0 0 0-2-2H9"></path><path d="m2 2 20 20"></path><path d="M3.6 3.6c-.4.3-.6.8-.6 1.4v16l4-4h10"></path></g>',
  },
  'lucide-message-square-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M12 7v6"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-message-square-quote': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M8 12a2 2 0 0 0 2-2V8H8"></path><path d="M14 12a2 2 0 0 0 2-2V8h-2"></path></g>',
  },
  'lucide-message-square-reply': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="m10 7-3 3 3 3"></path><path d="M17 13v-1a2 2 0 0 0-2-2H7"></path></g>',
  },
  'lucide-message-square-share': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12v3a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h7"></path><path d="M16 3h5v5"></path><path d="m16 8 5-5"></path></g>',
  },
  'lucide-message-square-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M13 8H7"></path><path d="M17 12H7"></path></g>',
  },
  'lucide-message-square-warning': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="M12 7v2"></path><path d="M12 13h.01"></path></g>',
  },
  'lucide-message-square-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path><path d="m14.5 7.5-5 5"></path><path d="m9.5 7.5 5 5"></path></g>',
  },
  'lucide-message-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></g>',
  },
  'lucide-messages-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"></path><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"></path></g>',
  },
  'lucide-mic-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"></line><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"></path><path d="M5 10v2a7 7 0 0 0 12 5"></path><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"></path><path d="M9 9v3a3 3 0 0 0 5.12 2.12"></path><line x1="12" x2="12" y1="19" y2="22"></line></g>',
  },
  'lucide-mic-vocal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m11 7.601-5.994 8.19a1 1 0 0 0 .1 1.298l.817.818a1 1 0 0 0 1.314.087L15.09 12"></path><path d="M16.5 21.174C15.5 20.5 14.372 20 13 20c-2.058 0-3.928 2.356-6 2-2.072-.356-2.775-3.369-1.5-4.5"></path><circle cx="16" cy="7" r="5"></circle></g>',
  },
  'lucide-mic': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" x2="12" y1="19" y2="22"></line></g>',
  },
  'lucide-microchip': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 12h2"></path><path d="M18 16h2"></path><path d="M18 20h2"></path><path d="M18 4h2"></path><path d="M18 8h2"></path><path d="M4 12h2"></path><path d="M4 16h2"></path><path d="M4 20h2"></path><path d="M4 4h2"></path><path d="M4 8h2"></path><path d="M8 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-1.5c-.276 0-.494.227-.562.495a2 2 0 0 1-3.876 0C9.994 2.227 9.776 2 9.5 2z"></path></g>',
  },
  'lucide-microscope': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18h8"></path><path d="M3 22h18"></path><path d="M14 22a7 7 0 1 0 0-14h-1"></path><path d="M9 14h2"></path><path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z"></path><path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"></path></g>',
  },
  'lucide-microwave': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="15" x="2" y="4" rx="2"></rect><rect width="8" height="7" x="6" y="8" rx="1"></rect><path d="M18 8v7"></path><path d="M6 19v2"></path><path d="M18 19v2"></path></g>',
  },
  'lucide-milestone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13v8"></path><path d="M12 3v3"></path><path d="M4 6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h13a2 2 0 0 0 1.152-.365l3.424-2.317a1 1 0 0 0 0-1.635l-3.424-2.318A2 2 0 0 0 17 6z"></path></g>',
  },
  'lucide-milk-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2h8"></path><path d="M9 2v1.343M15 2v2.789a4 4 0 0 0 .672 2.219l.656.984a4 4 0 0 1 .672 2.22v1.131M7.8 7.8l-.128.192A4 4 0 0 0 7 10.212V20a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-3"></path><path d="M7 15a6.47 6.47 0 0 1 5 0 6.472 6.472 0 0 0 3.435.435"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-milk': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2h8"></path><path d="M9 2v2.789a4 4 0 0 1-.672 2.219l-.656.984A4 4 0 0 0 7 10.212V20a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-9.789a4 4 0 0 0-.672-2.219l-.656-.984A4 4 0 0 1 15 4.788V2"></path><path d="M7 15a6.472 6.472 0 0 1 5 0 6.47 6.47 0 0 0 5 0"></path></g>',
  },
  'lucide-minimize-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" x2="21" y1="10" y2="3"></line><line x1="3" x2="10" y1="21" y2="14"></line></g>',
  },
  'lucide-minimize': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"></path><path d="M21 8h-3a2 2 0 0 1-2-2V3"></path><path d="M3 16h3a2 2 0 0 1 2 2v3"></path><path d="M16 21v-3a2 2 0 0 1 2-2h3"></path></g>',
  },
  'lucide-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path></g>',
  },
  'lucide-monitor-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 10 2 2 4-4"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v4"></path><path d="m14.305 7.53.923-.382"></path><path d="m15.228 4.852-.923-.383"></path><path d="m16.852 3.228-.383-.924"></path><path d="m16.852 8.772-.383.923"></path><path d="m19.148 3.228.383-.924"></path><path d="m19.53 9.696-.382-.924"></path><path d="m20.772 4.852.924-.383"></path><path d="m20.772 7.148.924.383"></path><path d="M22 13v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"></path><path d="M8 21h8"></path><circle cx="18" cy="6" r="3"></circle></g>',
  },
  'lucide-monitor-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="19" cy="6" r="3"></circle><path d="M22 12v3a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9"></path><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13V7"></path><path d="m15 10-3 3-3-3"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 17H4a2 2 0 0 1-2-2V5c0-1.5 1-2 1-2"></path><path d="M22 15V5a2 2 0 0 0-2-2H9"></path><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-monitor-pause': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13V7"></path><path d="M14 13V7"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 7.75a.75.75 0 0 1 1.142-.638l3.664 2.249a.75.75 0 0 1 0 1.278l-3.664 2.25a.75.75 0 0 1-1.142-.64z"></path><path d="M12 17v4"></path><path d="M8 21h8"></path><rect x="2" y="3" width="20" height="14" rx="2"></rect></g>',
  },
  'lucide-monitor-smartphone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h8"></path><path d="M10 19v-3.96 3.15"></path><path d="M7 19h5"></path><rect width="6" height="10" x="16" y="12" rx="2"></rect></g>',
  },
  'lucide-monitor-speaker': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 20H8"></path><path d="M17 9h.01"></path><rect width="10" height="16" x="12" y="4" rx="2"></rect><path d="M8 6H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h4"></path><circle cx="17" cy="15" r="1"></circle></g>',
  },
  'lucide-monitor-stop': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v4"></path><path d="M8 21h8"></path><rect x="2" y="3" width="20" height="14" rx="2"></rect><rect x="9" y="7" width="6" height="6" rx="1"></rect></g>',
  },
  'lucide-monitor-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 10 3-3 3 3"></path><path d="M12 13V7"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.5 12.5-5-5"></path><path d="m9.5 12.5 5-5"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect><path d="M12 17v4"></path><path d="M8 21h8"></path></g>',
  },
  'lucide-monitor': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"></rect><line x1="8" x2="16" y1="21" y2="21"></line><line x1="12" x2="12" y1="17" y2="21"></line></g>',
  },
  'lucide-moon-star': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path></g>',
  },
  'lucide-moon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path></g>',
  },
  'lucide-mountain-snow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"></path><path d="M4.14 15.08c2.62-1.57 5.24-1.43 7.86.42 2.74 1.94 5.49 2 8.23.19"></path></g>',
  },
  'lucide-mountain': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"></path></g>',
  },
  'lucide-mouse-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6v.343"></path><path d="M18.218 18.218A7 7 0 0 1 5 15V9a7 7 0 0 1 .782-3.218"></path><path d="M19 13.343V9A7 7 0 0 0 8.56 2.902"></path><path d="M22 22 2 2"></path></g>',
  },
  'lucide-mouse-pointer-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z"></path></g>',
  },
  'lucide-mouse-pointer-ban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.034 2.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.944L8.204 7.545a1 1 0 0 0-.66.66l-1.066 3.443a.5.5 0 0 1-.944.033z"></path><circle cx="16" cy="16" r="6"></circle><path d="m11.8 11.8 8.4 8.4"></path></g>',
  },
  'lucide-mouse-pointer-click': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4.1 12 6"></path><path d="m5.1 8-2.9-.8"></path><path d="m6 12-1.9 2"></path><path d="M7.2 2.2 8 5.1"></path><path d="M9.037 9.69a.498.498 0 0 1 .653-.653l11 4.5a.5.5 0 0 1-.074.949l-4.349 1.041a1 1 0 0 0-.74.739l-1.04 4.35a.5.5 0 0 1-.95.074z"></path></g>',
  },
  'lucide-mouse-pointer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.586 12.586 19 19"></path><path d="M3.688 3.037a.497.497 0 0 0-.651.651l6.5 15.999a.501.501 0 0 0 .947-.062l1.569-6.083a2 2 0 0 1 1.448-1.479l6.124-1.579a.5.5 0 0 0 .063-.947z"></path></g>',
  },
  'lucide-mouse': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="7"></rect><path d="M12 6v4"></path></g>',
  },
  'lucide-move-3d': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3v16h16"></path><path d="m5 19 6-6"></path><path d="m2 6 3-3 3 3"></path><path d="m18 16 3 3-3 3"></path></g>',
  },
  'lucide-move-diagonal-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 13v6h-6"></path><path d="M5 11V5h6"></path><path d="m5 5 14 14"></path></g>',
  },
  'lucide-move-diagonal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H5v-6"></path><path d="M13 5h6v6"></path><path d="M19 5 5 19"></path></g>',
  },
  'lucide-move-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H5V13"></path><path d="M19 5L5 19"></path></g>',
  },
  'lucide-move-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 13V19H13"></path><path d="M5 5L19 19"></path></g>',
  },
  'lucide-move-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 18L12 22L16 18"></path><path d="M12 2V22"></path></g>',
  },
  'lucide-move-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 8 4 4-4 4"></path><path d="M2 12h20"></path><path d="m6 8-4 4 4 4"></path></g>',
  },
  'lucide-move-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8L2 12L6 16"></path><path d="M2 12H22"></path></g>',
  },
  'lucide-move-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8L22 12L18 16"></path><path d="M2 12H22"></path></g>',
  },
  'lucide-move-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 11V5H11"></path><path d="M5 5L19 19"></path></g>',
  },
  'lucide-move-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 5H19V11"></path><path d="M19 5L5 19"></path></g>',
  },
  'lucide-move-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6L12 2L16 6"></path><path d="M12 2V22"></path></g>',
  },
  'lucide-move-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20"></path><path d="m8 18 4 4 4-4"></path><path d="m8 6 4-4 4 4"></path></g>',
  },
  'lucide-move': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20"></path><path d="m15 19-3 3-3-3"></path><path d="m19 9 3 3-3 3"></path><path d="M2 12h20"></path><path d="m5 9-3 3 3 3"></path><path d="m9 5 3-3 3 3"></path></g>',
  },
  'lucide-music-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="18" r="4"></circle><path d="M12 18V2l7 4"></path></g>',
  },
  'lucide-music-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="18" r="4"></circle><path d="M16 18V2"></path></g>',
  },
  'lucide-music-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><path d="m9 9 12-2"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></g>',
  },
  'lucide-music': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></g>',
  },
  'lucide-navigation-2-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.31 9.31 5 21l7-4 7 4-1.17-3.17"></path><path d="M14.53 8.88 12 2l-1.17 3.17"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-navigation-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 19 21 12 17 5 21 12 2"></polygon></g>',
  },
  'lucide-navigation-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8.43 8.43 3 11l8 2 2 8 2.57-5.43"></path><path d="M17.39 11.73 22 2l-9.73 4.61"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-navigation': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></g>',
  },
  'lucide-network': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="16" y="16" width="6" height="6" rx="1"></rect><rect x="2" y="16" width="6" height="6" rx="1"></rect><rect x="9" y="2" width="6" height="6" rx="1"></rect><path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3"></path><path d="M12 12V8"></path></g>',
  },
  'lucide-newspaper': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18h-5"></path><path d="M18 14h-8"></path><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2"></path><rect width="8" height="4" x="10" y="6" rx="1"></rect></g>',
  },
  'lucide-nfc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"></path><path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"></path><path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"></path><path d="M16.37 2a20.16 20.16 0 0 1 0 20"></path></g>',
  },
  'lucide-non-binary': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v10"></path><path d="m8.5 4 7 4"></path><path d="m8.5 8 7-4"></path><circle cx="12" cy="17" r="5"></circle></g>',
  },
  'lucide-notebook-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"></path><path d="M2 6h4"></path><path d="M2 10h4"></path><path d="M2 14h4"></path><path d="M2 18h4"></path><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path></g>',
  },
  'lucide-notebook-tabs': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6h4"></path><path d="M2 10h4"></path><path d="M2 14h4"></path><path d="M2 18h4"></path><rect width="16" height="20" x="4" y="2" rx="2"></rect><path d="M15 2v20"></path><path d="M15 7h5"></path><path d="M15 12h5"></path><path d="M15 17h5"></path></g>',
  },
  'lucide-notebook-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6h4"></path><path d="M2 10h4"></path><path d="M2 14h4"></path><path d="M2 18h4"></path><rect width="16" height="20" x="4" y="2" rx="2"></rect><path d="M9.5 8h5"></path><path d="M9.5 12H16"></path><path d="M9.5 16H14"></path></g>',
  },
  'lucide-notebook': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6h4"></path><path d="M2 10h4"></path><path d="M2 14h4"></path><path d="M2 18h4"></path><rect width="16" height="20" x="4" y="2" rx="2"></rect><path d="M16 2v20"></path></g>',
  },
  'lucide-notepad-text-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M12 2v4"></path><path d="M16 2v4"></path><path d="M16 4h2a2 2 0 0 1 2 2v2"></path><path d="M20 12v2"></path><path d="M20 18v2a2 2 0 0 1-2 2h-1"></path><path d="M13 22h-2"></path><path d="M7 22H6a2 2 0 0 1-2-2v-2"></path><path d="M4 14v-2"></path><path d="M4 8V6a2 2 0 0 1 2-2h2"></path><path d="M8 10h6"></path><path d="M8 14h8"></path><path d="M8 18h5"></path></g>',
  },
  'lucide-notepad-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M12 2v4"></path><path d="M16 2v4"></path><rect width="16" height="18" x="4" y="4" rx="2"></rect><path d="M8 10h6"></path><path d="M8 14h8"></path><path d="M8 18h5"></path></g>',
  },
  'lucide-nut-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4V2"></path><path d="M5 10v4a7.004 7.004 0 0 0 5.277 6.787c.412.104.802.292 1.102.592L12 22l.621-.621c.3-.3.69-.488 1.102-.592a7.01 7.01 0 0 0 4.125-2.939"></path><path d="M19 10v3.343"></path><path d="M12 12c-1.349-.573-1.905-1.005-2.5-2-.546.902-1.048 1.353-2.5 2-1.018-.644-1.46-1.08-2-2-1.028.71-1.69.918-3 1 1.081-1.048 1.757-2.03 2-3 .194-.776.84-1.551 1.79-2.21m11.654 5.997c.887-.457 1.28-.891 1.556-1.787 1.032.916 1.683 1.157 3 1-1.297-1.036-1.758-2.03-2-3-.5-2-4-4-8-4-.74 0-1.461.068-2.15.192"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-nut': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4V2"></path><path d="M5 10v4a7.004 7.004 0 0 0 5.277 6.787c.412.104.802.292 1.102.592L12 22l.621-.621c.3-.3.69-.488 1.102-.592A7.003 7.003 0 0 0 19 14v-4"></path><path d="M12 4C8 4 4.5 6 4 8c-.243.97-.919 1.952-2 3 1.31-.082 1.972-.29 3-1 .54.92.982 1.356 2 2 1.452-.647 1.954-1.098 2.5-2 .595.995 1.151 1.427 2.5 2 1.31-.621 1.862-1.058 2.5-2 .629.977 1.162 1.423 2.5 2 1.209-.548 1.68-.967 2-2 1.032.916 1.683 1.157 3 1-1.297-1.036-1.758-2.03-2-3-.5-2-4-4-8-4Z"></path></g>',
  },
  'lucide-octagon-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16h.01"></path><path d="M12 8v4"></path><path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z"></path></g>',
  },
  'lucide-octagon-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z"></path><path d="M8 12h8"></path></g>',
  },
  'lucide-octagon-pause': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15V9"></path><path d="M14 15V9"></path><path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z"></path></g>',
  },
  'lucide-octagon-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 9-6 6"></path><path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z"></path><path d="m9 9 6 6"></path></g>',
  },
  'lucide-octagon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.586 16.726A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2h6.624a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586z"></path></g>',
  },
  'lucide-omega': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20h4.5a.5.5 0 0 0 .5-.5v-.282a.52.52 0 0 0-.247-.437 8 8 0 1 1 8.494-.001.52.52 0 0 0-.247.438v.282a.5.5 0 0 0 .5.5H21"></path></g>',
  },
  'lucide-option': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h6l6 18h6"></path><path d="M14 3h7"></path></g>',
  },
  'lucide-orbit': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.341 6.484A10 10 0 0 1 10.266 21.85"></path><path d="M3.659 17.516A10 10 0 0 1 13.74 2.152"></path><circle cx="12" cy="12" r="3"></circle><circle cx="19" cy="5" r="2"></circle><circle cx="5" cy="19" r="2"></circle></g>',
  },
  'lucide-origami': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12V4a1 1 0 0 1 1-1h6.297a1 1 0 0 1 .651 1.759l-4.696 4.025"></path><path d="m12 21-7.414-7.414A2 2 0 0 1 4 12.172V6.415a1.002 1.002 0 0 1 1.707-.707L20 20.009"></path><path d="m12.214 3.381 8.414 14.966a1 1 0 0 1-.167 1.199l-1.168 1.163a1 1 0 0 1-.706.291H6.351a1 1 0 0 1-.625-.219L3.25 18.8a1 1 0 0 1 .631-1.781l4.165.027"></path></g>',
  },
  'lucide-package-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v6"></path><path d="M16.76 3a2 2 0 0 1 1.8 1.1l2.23 4.479a2 2 0 0 1 .21.891V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.472a2 2 0 0 1 .211-.894L5.45 4.1A2 2 0 0 1 7.24 3z"></path><path d="M3.054 9.013h17.893"></path></g>',
  },
  'lucide-package-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 16 2 2 4-4"></path><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path><path d="m7.5 4.27 9 5.15"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" x2="12" y1="22" y2="12"></line></g>',
  },
  'lucide-package-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 16h6"></path><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path><path d="m7.5 4.27 9 5.15"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" x2="12" y1="22" y2="12"></line></g>',
  },
  'lucide-package-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-9"></path><path d="M15.17 2.21a1.67 1.67 0 0 1 1.63 0L21 4.57a1.93 1.93 0 0 1 0 3.36L8.82 14.79a1.655 1.655 0 0 1-1.64 0L3 12.43a1.93 1.93 0 0 1 0-3.36z"></path><path d="M20 13v3.87a2.06 2.06 0 0 1-1.11 1.83l-6 3.08a1.93 1.93 0 0 1-1.78 0l-6-3.08A2.06 2.06 0 0 1 4 16.87V13"></path><path d="M21 12.43a1.93 1.93 0 0 0 0-3.36L8.83 2.2a1.64 1.64 0 0 0-1.63 0L3 4.57a1.93 1.93 0 0 0 0 3.36l12.18 6.86a1.636 1.636 0 0 0 1.63 0z"></path></g>',
  },
  'lucide-package-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 16h6"></path><path d="M19 13v6"></path><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path><path d="m7.5 4.27 9 5.15"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" x2="12" y1="22" y2="12"></line></g>',
  },
  'lucide-package-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path><path d="m7.5 4.27 9 5.15"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" x2="12" y1="22" y2="12"></line><circle cx="18.5" cy="15.5" r="2.5"></circle><path d="M20.27 17.27 22 19"></path></g>',
  },
  'lucide-package-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path><path d="m7.5 4.27 9 5.15"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><line x1="12" x2="12" y1="22" y2="12"></line><path d="m17 13 5 5m-5 0 5-5"></path></g>',
  },
  'lucide-package': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path><path d="M12 22V12"></path><polyline points="3.29 7 12 12 20.71 7"></polyline><path d="m7.5 4.27 9 5.15"></path></g>',
  },
  'lucide-paint-bucket': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"></path><path d="m5 2 5 5"></path><path d="M2 13h15"></path><path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"></path></g>',
  },
  'lucide-paint-roller': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="6" x="2" y="2" rx="2"></rect><path d="M10 16v-2a2 2 0 0 1 2-2h8a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path><rect width="4" height="6" x="8" y="16" rx="1"></rect></g>',
  },
  'lucide-paintbrush-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v2"></path><path d="M14 2v4"></path><path d="M17 2a1 1 0 0 1 1 1v9H6V3a1 1 0 0 1 1-1z"></path><path d="M6 12a1 1 0 0 0-1 1v1a2 2 0 0 0 2 2h2a1 1 0 0 1 1 1v2.9a2 2 0 1 0 4 0V17a1 1 0 0 1 1-1h2a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1"></path></g>',
  },
  'lucide-paintbrush': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.622 17.897-10.68-2.913"></path><path d="M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z"></path><path d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15"></path></g>',
  },
  'lucide-palette': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"></path><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle></g>',
  },
  'lucide-panda': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.25 17.25h1.5L12 18z"></path><path d="m15 12 2 2"></path><path d="M18 6.5a.5.5 0 0 0-.5-.5"></path><path d="M20.69 9.67a4.5 4.5 0 1 0-7.04-5.5 8.35 8.35 0 0 0-3.3 0 4.5 4.5 0 1 0-7.04 5.5C2.49 11.2 2 12.88 2 14.5 2 19.47 6.48 22 12 22s10-2.53 10-7.5c0-1.62-.48-3.3-1.3-4.83"></path><path d="M6 6.5a.495.495 0 0 1 .5-.5"></path><path d="m9 12-2 2"></path></g>',
  },
  'lucide-panel-bottom-close': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 15h18"></path><path d="m15 8-3 3-3-3"></path></g>',
  },
  'lucide-panel-bottom-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M14 15h1"></path><path d="M19 15h2"></path><path d="M3 15h2"></path><path d="M9 15h1"></path></g>',
  },
  'lucide-panel-bottom-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 15h18"></path><path d="m9 10 3-3 3 3"></path></g>',
  },
  'lucide-panel-bottom': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 15h18"></path></g>',
  },
  'lucide-panel-left-close': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 3v18"></path><path d="m16 15-3-3 3-3"></path></g>',
  },
  'lucide-panel-left-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 14v1"></path><path d="M9 19v2"></path><path d="M9 3v2"></path><path d="M9 9v1"></path></g>',
  },
  'lucide-panel-left-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 3v18"></path><path d="m14 9 3 3-3 3"></path></g>',
  },
  'lucide-panel-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 3v18"></path></g>',
  },
  'lucide-panel-right-close': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M15 3v18"></path><path d="m8 9 3 3-3 3"></path></g>',
  },
  'lucide-panel-right-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M15 14v1"></path><path d="M15 19v2"></path><path d="M15 3v2"></path><path d="M15 9v1"></path></g>',
  },
  'lucide-panel-right-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M15 3v18"></path><path d="m10 15-3-3 3-3"></path></g>',
  },
  'lucide-panel-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M15 3v18"></path></g>',
  },
  'lucide-panel-top-close': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="m9 16 3-3 3 3"></path></g>',
  },
  'lucide-panel-top-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M14 9h1"></path><path d="M19 9h2"></path><path d="M3 9h2"></path><path d="M9 9h1"></path></g>',
  },
  'lucide-panel-top-open': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="m15 14-3 3-3-3"></path></g>',
  },
  'lucide-panel-top': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path></g>',
  },
  'lucide-panels-left-bottom': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 3v18"></path><path d="M9 15h12"></path></g>',
  },
  'lucide-panels-right-bottom': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 15h12"></path><path d="M15 3v18"></path></g>',
  },
  'lucide-panels-top-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="M9 21V9"></path></g>',
  },
  'lucide-paperclip': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.234 20.252 21 12.3"></path><path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"></path></g>',
  },
  'lucide-parentheses': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 21s-4-3-4-9 4-9 4-9"></path><path d="M16 3s4 3 4 9-4 9-4 9"></path></g>',
  },
  'lucide-parking-meter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 15h2"></path><path d="M12 12v3"></path><path d="M12 19v3"></path><path d="M15.282 19a1 1 0 0 0 .948-.68l2.37-6.988a7 7 0 1 0-13.2 0l2.37 6.988a1 1 0 0 0 .948.68z"></path><path d="M9 9a3 3 0 1 1 6 0"></path></g>',
  },
  'lucide-party-popper': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.8 11.3 2 22l10.7-3.79"></path><path d="M4 3h.01"></path><path d="M22 8h.01"></path><path d="M15 2h.01"></path><path d="M22 20h.01"></path><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10"></path><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17"></path><path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7"></path><path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z"></path></g>',
  },
  'lucide-pause': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="14" y="4" width="4" height="16" rx="1"></rect><rect x="6" y="4" width="4" height="16" rx="1"></rect></g>',
  },
  'lucide-paw-print': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="4" r="2"></circle><circle cx="18" cy="8" r="2"></circle><circle cx="20" cy="16" r="2"></circle><path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z"></path></g>',
  },
  'lucide-pc-case': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="20" x="5" y="2" rx="2"></rect><path d="M15 14h.01"></path><path d="M9 6h6"></path><path d="M9 10h6"></path></g>',
  },
  'lucide-pen-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"></path></g>',
  },
  'lucide-pen-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 10-6.157 6.162a2 2 0 0 0-.5.833l-1.322 4.36a.5.5 0 0 0 .622.624l4.358-1.323a2 2 0 0 0 .83-.5L14 13.982"></path><path d="m12.829 7.172 4.359-4.346a1 1 0 1 1 3.986 3.986l-4.353 4.353"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-pen-tool': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.707 21.293a1 1 0 0 1-1.414 0l-1.586-1.586a1 1 0 0 1 0-1.414l5.586-5.586a1 1 0 0 1 1.414 0l1.586 1.586a1 1 0 0 1 0 1.414z"></path><path d="m18 13-1.375-6.874a1 1 0 0 0-.746-.776L3.235 2.028a1 1 0 0 0-1.207 1.207L5.35 15.879a1 1 0 0 0 .776.746L13 18"></path><path d="m2.3 2.3 7.286 7.286"></path><circle cx="11" cy="11" r="2"></circle></g>',
  },
  'lucide-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path></g>',
  },
  'lucide-pencil-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"></path><path d="m15 5 3 3"></path></g>',
  },
  'lucide-pencil-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 10-6.157 6.162a2 2 0 0 0-.5.833l-1.322 4.36a.5.5 0 0 0 .622.624l4.358-1.323a2 2 0 0 0 .83-.5L14 13.982"></path><path d="m12.829 7.172 4.359-4.346a1 1 0 1 1 3.986 3.986l-4.353 4.353"></path><path d="m15 5 4 4"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-pencil-ruler': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 7 8.7 2.7a2.41 2.41 0 0 0-3.4 0L2.7 5.3a2.41 2.41 0 0 0 0 3.4L7 13"></path><path d="m8 6 2-2"></path><path d="m18 16 2-2"></path><path d="m17 11 4.3 4.3c.94.94.94 2.46 0 3.4l-2.6 2.6c-.94.94-2.46.94-3.4 0L11 17"></path><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path><path d="m15 5 4 4"></path></g>',
  },
  'lucide-pencil': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path><path d="m15 5 4 4"></path></g>',
  },
  'lucide-pentagon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.83 2.38a2 2 0 0 1 2.34 0l8 5.74a2 2 0 0 1 .73 2.25l-3.04 9.26a2 2 0 0 1-1.9 1.37H7.04a2 2 0 0 1-1.9-1.37L2.1 10.37a2 2 0 0 1 .73-2.25z"></path></g>',
  },
  'lucide-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="19" x2="5" y1="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></g>',
  },
  'lucide-person-standing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="1"></circle><path d="m9 20 3-6 3 6"></path><path d="m6 8 6 2 6-2"></path><path d="M12 10v4"></path></g>',
  },
  'lucide-philippine-peso': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11H4"></path><path d="M20 7H4"></path><path d="M7 21V4a1 1 0 0 1 1-1h4a1 1 0 0 1 0 12H7"></path></g>',
  },
  'lucide-phone-call': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path><path d="M14.05 2a9 9 0 0 1 8 7.94"></path><path d="M14.05 6A5 5 0 0 1 18 10"></path></g>',
  },
  'lucide-phone-forwarded': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 2 22 6 18 10"></polyline><line x1="14" x2="22" y1="6" y2="6"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></g>',
  },
  'lucide-phone-incoming': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 2 16 8 22 8"></polyline><line x1="22" x2="16" y1="2" y2="8"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></g>',
  },
  'lucide-phone-missed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="22" x2="16" y1="2" y2="8"></line><line x1="16" x2="22" y1="2" y2="8"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></g>',
  },
  'lucide-phone-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path><line x1="22" x2="2" y1="2" y2="22"></line></g>',
  },
  'lucide-phone-outgoing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 8 22 2 16 2"></polyline><line x1="16" x2="22" y1="8" y2="2"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></g>',
  },
  'lucide-phone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></g>',
  },
  'lucide-pi': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="9" x2="9" y1="4" y2="20"></line><path d="M4 7c0-1.7 1.3-3 3-3h13"></path><path d="M18 20c-1.7 0-3-1.3-3-3V4"></path></g>',
  },
  'lucide-piano': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.5 8c-1.4 0-2.6-.8-3.2-2A6.87 6.87 0 0 0 2 9v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-8.5C22 9.6 20.4 8 18.5 8"></path><path d="M2 14h20"></path><path d="M6 14v4"></path><path d="M10 14v4"></path><path d="M14 14v4"></path><path d="M18 14v4"></path></g>',
  },
  'lucide-pickaxe': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.531 12.469 6.619 20.38a1 1 0 1 1-3-3l7.912-7.912"></path><path d="M15.686 4.314A12.5 12.5 0 0 0 5.461 2.958 1 1 0 0 0 5.58 4.71a22 22 0 0 1 6.318 3.393"></path><path d="M17.7 3.7a1 1 0 0 0-1.4 0l-4.6 4.6a1 1 0 0 0 0 1.4l2.6 2.6a1 1 0 0 0 1.4 0l4.6-4.6a1 1 0 0 0 0-1.4z"></path><path d="M19.686 8.314a12.501 12.501 0 0 1 1.356 10.225 1 1 0 0 1-1.751-.119 22 22 0 0 0-3.393-6.319"></path></g>',
  },
  'lucide-picture-in-picture-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 9V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10c0 1.1.9 2 2 2h4"></path><rect width="10" height="7" x="12" y="13" rx="2"></rect></g>',
  },
  'lucide-picture-in-picture': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 10h6V4"></path><path d="m2 4 6 6"></path><path d="M21 10V7a2 2 0 0 0-2-2h-7"></path><path d="M3 14v2a2 2 0 0 0 2 2h3"></path><rect x="12" y="14" width="10" height="7" rx="1"></rect></g>',
  },
  'lucide-piggy-bank': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 17h3v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3a3.16 3.16 0 0 0 2-2h1a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1h-1a5 5 0 0 0-2-4V3a4 4 0 0 0-3.2 1.6l-.3.4H11a6 6 0 0 0-6 6v1a5 5 0 0 0 2 4v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1z"></path><path d="M16 10h.01"></path><path d="M2 8v1a2 2 0 0 0 2 2h1"></path></g>',
  },
  'lucide-pilcrow-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3v11"></path><path d="M14 9h-3a3 3 0 0 1 0-6h9"></path><path d="M18 3v11"></path><path d="M22 18H2l4-4"></path><path d="m6 22-4-4"></path></g>',
  },
  'lucide-pilcrow-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3v11"></path><path d="M10 9H7a1 1 0 0 1 0-6h8"></path><path d="M14 3v11"></path><path d="m18 14 4 4H2"></path><path d="m22 18-4 4"></path></g>',
  },
  'lucide-pilcrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 4v16"></path><path d="M17 4v16"></path><path d="M19 4H9.5a4.5 4.5 0 0 0 0 9H13"></path></g>',
  },
  'lucide-pill-bottle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11h-4a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h4"></path><path d="M6 7v13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7"></path><rect width="16" height="5" x="4" y="2" rx="1"></rect></g>',
  },
  'lucide-pill': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path><path d="m8.5 8.5 7 7"></path></g>',
  },
  'lucide-pin-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5"></path><path d="M15 9.34V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H7.89"></path><path d="m2 2 20 20"></path><path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h11"></path></g>',
  },
  'lucide-pin': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5"></path><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"></path></g>',
  },
  'lucide-pipette': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12"></path><path d="m18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z"></path><path d="m2 22 .414-.414"></path></g>',
  },
  'lucide-pizza': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 14-1 1"></path><path d="m13.75 18.25-1.25 1.42"></path><path d="M17.775 5.654a15.68 15.68 0 0 0-12.121 12.12"></path><path d="M18.8 9.3a1 1 0 0 0 2.1 7.7"></path><path d="M21.964 20.732a1 1 0 0 1-1.232 1.232l-18-5a1 1 0 0 1-.695-1.232A19.68 19.68 0 0 1 15.732 2.037a1 1 0 0 1 1.232.695z"></path></g>',
  },
  'lucide-plane-landing': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22h20"></path><path d="M3.77 10.77 2 9l2-4.5 1.1.55c.55.28.9.84.9 1.45s.35 1.17.9 1.45L8 8.5l3-6 1.05.53a2 2 0 0 1 1.09 1.52l.72 5.4a2 2 0 0 0 1.09 1.52l4.4 2.2c.42.22.78.55 1.01.96l.6 1.03c.49.88-.06 1.98-1.06 2.1l-1.18.15c-.47.06-.95-.02-1.37-.24L4.29 11.15a2 2 0 0 1-.52-.38Z"></path></g>',
  },
  'lucide-plane-takeoff': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22h20"></path><path d="M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12 5 6l.9-.45a2 2 0 0 1 2.09.2l4.02 3a2 2 0 0 0 2.1.2l4.19-2.06a2.41 2.41 0 0 1 1.73-.17L21 7a1.4 1.4 0 0 1 .87 1.99l-.38.76c-.23.46-.6.84-1.07 1.08L7.58 17.2a2 2 0 0 1-1.22.18Z"></path></g>',
  },
  'lucide-plane': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"></path></g>',
  },
  'lucide-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="6 3 20 12 6 21 6 3"></polygon></g>',
  },
  'lucide-plug-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2v6"></path><path d="M15 2v6"></path><path d="M12 17v5"></path><path d="M5 8h14"></path><path d="M6 11V8h12v3a6 6 0 1 1-12 0Z"></path></g>',
  },
  'lucide-plug-zap': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z"></path><path d="m2 22 3-3"></path><path d="M7.5 13.5 10 11"></path><path d="M10.5 16.5 13 14"></path><path d="m18 3-4 4h6l-4 4"></path></g>',
  },
  'lucide-plug': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-5"></path><path d="M9 8V2"></path><path d="M15 8V2"></path><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"></path></g>',
  },
  'lucide-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="M12 5v14"></path></g>',
  },
  'lucide-pocket-knife': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v1c0 1 2 1 2 2S3 6 3 7s2 1 2 2-2 1-2 2 2 1 2 2"></path><path d="M18 6h.01"></path><path d="M6 18h.01"></path><path d="M20.83 8.83a4 4 0 0 0-5.66-5.66l-12 12a4 4 0 1 0 5.66 5.66Z"></path><path d="M18 11.66V22a4 4 0 0 0 4-4V6"></path></g>',
  },
  'lucide-pocket': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3h16a2 2 0 0 1 2 2v6a10 10 0 0 1-10 10A10 10 0 0 1 2 11V5a2 2 0 0 1 2-2z"></path><polyline points="8 10 12 14 16 10"></polyline></g>',
  },
  'lucide-podcast': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.85 18.58a9 9 0 1 0-9.7 0"></path><path d="M8 14a5 5 0 1 1 8 0"></path><circle cx="12" cy="11" r="1"></circle><path d="M13 17a1 1 0 1 0-2 0l.5 4.5a.5.5 0 1 0 1 0Z"></path></g>',
  },
  'lucide-pointer-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 4.5V4a2 2 0 0 0-2.41-1.957"></path><path d="M13.9 8.4a2 2 0 0 0-1.26-1.295"></path><path d="M21.7 16.2A8 8 0 0 0 22 14v-3a2 2 0 1 0-4 0v-1a2 2 0 0 0-3.63-1.158"></path><path d="m7 15-1.8-1.8a2 2 0 0 0-2.79 2.86L6 19.7a7.74 7.74 0 0 0 6 2.3h2a8 8 0 0 0 5.657-2.343"></path><path d="M6 6v8"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-pointer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 14a8 8 0 0 1-8 8"></path><path d="M18 11v-1a2 2 0 0 0-2-2a2 2 0 0 0-2 2"></path><path d="M14 10V9a2 2 0 0 0-2-2a2 2 0 0 0-2 2v1"></path><path d="M10 9.5V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v10"></path><path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"></path></g>',
  },
  'lucide-popcorn': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a2 2 0 0 0 0-4 2 2 0 0 0-4 0 2 2 0 0 0-4 0 2 2 0 0 0-4 0 2 2 0 0 0 0 4"></path><path d="M10 22 9 8"></path><path d="m14 22 1-14"></path><path d="M20 8c.5 0 .9.4.8 1l-2.6 12c-.1.5-.7 1-1.2 1H7c-.6 0-1.1-.4-1.2-1L3.2 9c-.1-.6.3-1 .8-1Z"></path></g>',
  },
  'lucide-popsicle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.6 14.4c.8-.8.8-2 0-2.8l-8.1-8.1a4.95 4.95 0 1 0-7.1 7.1l8.1 8.1c.9.7 2.1.7 2.9-.1Z"></path><path d="m22 22-5.5-5.5"></path></g>',
  },
  'lucide-pound-sterling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 7c0-5.333-8-5.333-8 0"></path><path d="M10 7v14"></path><path d="M6 21h12"></path><path d="M6 13h10"></path></g>',
  },
  'lucide-power-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.36 6.64A9 9 0 0 1 20.77 15"></path><path d="M6.16 6.16a9 9 0 1 0 12.68 12.68"></path><path d="M12 2v4"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-power': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v10"></path><path d="M18.4 6.6a9 9 0 1 1-12.77.04"></path></g>',
  },
  'lucide-presentation': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h20"></path><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"></path><path d="m7 21 5-5 5 5"></path></g>',
  },
  'lucide-printer-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 22H7a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v.5"></path><path d="m16 19 2 2 4-4"></path><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2"></path><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path></g>',
  },
  'lucide-printer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path><rect x="6" y="14" width="12" height="8" rx="1"></rect></g>',
  },
  'lucide-projector': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7 3 5"></path><path d="M9 6V3"></path><path d="m13 7 2-2"></path><circle cx="9" cy="13" r="3"></circle><path d="M11.83 12H20a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h2.17"></path><path d="M16 16h2"></path></g>',
  },
  'lucide-proportions': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M12 9v11"></path><path d="M2 9h13a2 2 0 0 1 2 2v9"></path></g>',
  },
  'lucide-puzzle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z"></path></g>',
  },
  'lucide-pyramid': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 16.88a1 1 0 0 1-.32-1.43l9-13.02a1 1 0 0 1 1.64 0l9 13.01a1 1 0 0 1-.32 1.44l-8.51 4.86a2 2 0 0 1-1.98 0Z"></path><path d="M12 2v20"></path></g>',
  },
  'lucide-qr-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="5" height="5" x="3" y="3" rx="1"></rect><rect width="5" height="5" x="16" y="3" rx="1"></rect><rect width="5" height="5" x="3" y="16" rx="1"></rect><path d="M21 16h-3a2 2 0 0 0-2 2v3"></path><path d="M21 21v.01"></path><path d="M12 7v3a2 2 0 0 1-2 2H7"></path><path d="M3 12h.01"></path><path d="M12 3h.01"></path><path d="M12 16v.01"></path><path d="M16 12h1"></path><path d="M21 12v.01"></path><path d="M12 21v-1"></path></g>',
  },
  'lucide-quote': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path><path d="M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z"></path></g>',
  },
  'lucide-rabbit': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 16a3 3 0 0 1 2.24 5"></path><path d="M18 12h.01"></path><path d="M18 21h-8a4 4 0 0 1-4-4 7 7 0 0 1 7-7h.2L9.6 6.4a1 1 0 1 1 2.8-2.8L15.8 7h.2c3.3 0 6 2.7 6 6v1a2 2 0 0 1-2 2h-1a3 3 0 0 0-3 3"></path><path d="M20 8.54V4a2 2 0 1 0-4 0v3"></path><path d="M7.612 12.524a3 3 0 1 0-1.6 4.3"></path></g>',
  },
  'lucide-radar': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"></path><path d="M4 6h.01"></path><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"></path><path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"></path><path d="M12 18h.01"></path><path d="M17.99 11.66A6 6 0 0 1 15.77 16.67"></path><circle cx="12" cy="12" r="2"></circle><path d="m13.41 10.59 5.66-5.66"></path></g>',
  },
  'lucide-radiation': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12h.01"></path><path d="M7.5 4.2c-.3-.5-.9-.7-1.3-.4C3.9 5.5 2.3 8.1 2 11c-.1.5.4 1 1 1h5c0-1.5.8-2.8 2-3.4-1.1-1.9-2-3.5-2.5-4.4z"></path><path d="M21 12c.6 0 1-.4 1-1-.3-2.9-1.8-5.5-4.1-7.1-.4-.3-1.1-.2-1.3.3-.6.9-1.5 2.5-2.6 4.3 1.2.7 2 2 2 3.5h5z"></path><path d="M7.5 19.8c-.3.5-.1 1.1.4 1.3 2.6 1.2 5.6 1.2 8.2 0 .5-.2.7-.8.4-1.3-.5-.9-1.4-2.5-2.5-4.3-1.2.7-2.8.7-4 0-1.1 1.8-2 3.4-2.5 4.3z"></path></g>',
  },
  'lucide-radical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h3.28a1 1 0 0 1 .948.684l2.298 7.934a.5.5 0 0 0 .96-.044L13.82 4.771A1 1 0 0 1 14.792 4H21"></path></g>',
  },
  'lucide-radio-receiver': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 16v2"></path><path d="M19 16v2"></path><rect width="20" height="8" x="2" y="8" rx="2"></rect><path d="M18 12h.01"></path></g>',
  },
  'lucide-radio-tower': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.9 16.1C1 12.2 1 5.8 4.9 1.9"></path><path d="M7.8 4.7a6.14 6.14 0 0 0-.8 7.5"></path><circle cx="12" cy="9" r="2"></circle><path d="M16.2 4.8c2 2 2.26 5.11.8 7.47"></path><path d="M19.1 1.9a9.96 9.96 0 0 1 0 14.1"></path><path d="M9.5 18h5"></path><path d="m8 22 4-11 4 11"></path></g>',
  },
  'lucide-radio': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"></path><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"></path><circle cx="12" cy="12" r="2"></circle><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"></path><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"></path></g>',
  },
  'lucide-radius': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.34 17.52a10 10 0 1 0-2.82 2.82"></path><circle cx="19" cy="19" r="2"></circle><path d="m13.41 13.41 4.18 4.18"></path><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-rail-symbol': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 15h14"></path><path d="M5 9h14"></path><path d="m14 20-5-5 6-6-5-5"></path></g>',
  },
  'lucide-rainbow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 17a10 10 0 0 0-20 0"></path><path d="M6 17a6 6 0 0 1 12 0"></path><path d="M10 17a2 2 0 0 1 4 0"></path></g>',
  },
  'lucide-rat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 22H4a2 2 0 0 1 0-4h12"></path><path d="M13.236 18a3 3 0 0 0-2.2-5"></path><path d="M16 9h.01"></path><path d="M16.82 3.94a3 3 0 1 1 3.237 4.868l1.815 2.587a1.5 1.5 0 0 1-1.5 2.1l-2.872-.453a3 3 0 0 0-3.5 3"></path><path d="M17 4.988a3 3 0 1 0-5.2 2.052A7 7 0 0 0 4 14.015 4 4 0 0 0 8 18"></path></g>',
  },
  'lucide-ratio': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="12" height="20" x="6" y="2" rx="2"></rect><rect width="20" height="12" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-receipt-cent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M12 6.5v11"></path><path d="M15 9.4a4 4 0 1 0 0 5.2"></path></g>',
  },
  'lucide-receipt-euro': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M8 12h5"></path><path d="M16 9.5a4 4 0 1 0 0 5.2"></path></g>',
  },
  'lucide-receipt-indian-rupee': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M8 7h8"></path><path d="M12 17.5 8 15h1a4 4 0 0 0 0-8"></path><path d="M8 11h8"></path></g>',
  },
  'lucide-receipt-japanese-yen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="m12 10 3-3"></path><path d="m9 7 3 3v7.5"></path><path d="M9 11h6"></path><path d="M9 15h6"></path></g>',
  },
  'lucide-receipt-pound-sterling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M8 13h5"></path><path d="M10 17V9.5a2.5 2.5 0 0 1 5 0"></path><path d="M8 17h7"></path></g>',
  },
  'lucide-receipt-russian-ruble': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M8 15h5"></path><path d="M8 11h5a2 2 0 1 0 0-4h-3v10"></path></g>',
  },
  'lucide-receipt-swiss-franc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M10 17V7h5"></path><path d="M10 11h4"></path><path d="M8 15h5"></path></g>',
  },
  'lucide-receipt-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M14 8H8"></path><path d="M16 12H8"></path><path d="M13 16H8"></path></g>',
  },
  'lucide-receipt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path><path d="M12 17.5v-11"></path></g>',
  },
  'lucide-rectangle-ellipsis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"></rect><path d="M12 12h.01"></path><path d="M17 12h.01"></path><path d="M7 12h.01"></path></g>',
  },
  'lucide-rectangle-goggles': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4a2 2 0 0 1-1.6-.8l-1.6-2.13a1 1 0 0 0-1.6 0L9.6 17.2A2 2 0 0 1 8 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"></path></g>',
  },
  'lucide-rectangle-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"></rect></g>',
  },
  'lucide-rectangle-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="12" height="20" x="6" y="2" rx="2"></rect></g>',
  },
  'lucide-recycle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5"></path><path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12"></path><path d="m14 16-3 3 3 3"></path><path d="M8.293 13.596 7.196 9.5 3.1 10.598"></path><path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843"></path><path d="m13.378 9.633 4.096 1.098 1.097-4.096"></path></g>',
  },
  'lucide-redo-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 14 5-5-5-5"></path><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13"></path></g>',
  },
  'lucide-redo-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="17" r="1"></circle><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"></path></g>',
  },
  'lucide-redo': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"></path><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"></path></g>',
  },
  'lucide-refresh-ccw-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v6h6"></path><path d="M21 12A9 9 0 0 0 6 5.3L3 8"></path><path d="M21 22v-6h-6"></path><path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"></path><circle cx="12" cy="12" r="1"></circle></g>',
  },
  'lucide-refresh-ccw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></g>',
  },
  'lucide-refresh-cw-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8L18.74 5.74A9.75 9.75 0 0 0 12 3C11 3 10.03 3.16 9.13 3.47"></path><path d="M8 16H3v5"></path><path d="M3 12C3 9.51 4 7.26 5.64 5.64"></path><path d="m3 16 2.26 2.26A9.75 9.75 0 0 0 12 21c2.49 0 4.74-1 6.36-2.64"></path><path d="M21 12c0 1-.16 1.97-.47 2.87"></path><path d="M21 3v5h-5"></path><path d="M22 22 2 2"></path></g>',
  },
  'lucide-refresh-cw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path><path d="M8 16H3v5"></path></g>',
  },
  'lucide-refrigerator': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 6a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6Z"></path><path d="M5 10h14"></path><path d="M15 7v6"></path></g>',
  },
  'lucide-regex': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3v10"></path><path d="m12.67 5.5 8.66 5"></path><path d="m12.67 10.5 8.66-5"></path><path d="M9 17a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-2z"></path></g>',
  },
  'lucide-remove-formatting': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7V4h16v3"></path><path d="M5 20h6"></path><path d="M13 4 8 20"></path><path d="m15 15 5 5"></path><path d="m20 15-5 5"></path></g>',
  },
  'lucide-repeat-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"></path><path d="M3 11v-1a4 4 0 0 1 4-4h14"></path><path d="m7 22-4-4 4-4"></path><path d="M21 13v1a4 4 0 0 1-4 4H3"></path><path d="M11 10h1v4"></path></g>',
  },
  'lucide-repeat-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 9 3-3 3 3"></path><path d="M13 18H7a2 2 0 0 1-2-2V6"></path><path d="m22 15-3 3-3-3"></path><path d="M11 6h6a2 2 0 0 1 2 2v10"></path></g>',
  },
  'lucide-repeat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"></path><path d="M3 11v-1a4 4 0 0 1 4-4h14"></path><path d="m7 22-4-4 4-4"></path><path d="M21 13v1a4 4 0 0 1-4 4H3"></path></g>',
  },
  'lucide-replace-all': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2"></path><path d="M14 4a2 2 0 0 1 2-2"></path><path d="M16 10a2 2 0 0 1-2-2"></path><path d="M20 14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2"></path><path d="M20 2a2 2 0 0 1 2 2"></path><path d="M22 8a2 2 0 0 1-2 2"></path><path d="m3 7 3 3 3-3"></path><path d="M6 10V5a 3 3 0 0 1 3-3h1"></path><rect x="2" y="14" width="8" height="8" rx="2"></rect></g>',
  },
  'lucide-replace': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4a2 2 0 0 1 2-2"></path><path d="M16 10a2 2 0 0 1-2-2"></path><path d="M20 2a2 2 0 0 1 2 2"></path><path d="M22 8a2 2 0 0 1-2 2"></path><path d="m3 7 3 3 3-3"></path><path d="M6 10V5a3 3 0 0 1 3-3h1"></path><rect x="2" y="14" width="8" height="8" rx="2"></rect></g>',
  },
  'lucide-reply-all': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 17 2 12 7 7"></polyline><polyline points="12 17 7 12 12 7"></polyline><path d="M22 18v-2a4 4 0 0 0-4-4H7"></path></g>',
  },
  'lucide-reply': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></g>',
  },
  'lucide-rewind': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 19 2 12 11 5 11 19"></polygon><polygon points="22 19 13 12 22 5 22 19"></polygon></g>',
  },
  'lucide-ribbon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 11.22C11 9.997 10 9 10 8a2 2 0 0 1 4 0c0 1-.998 2.002-2.01 3.22"></path><path d="m12 18 2.57-3.5"></path><path d="M6.243 9.016a7 7 0 0 1 11.507-.009"></path><path d="M9.35 14.53 12 11.22"></path><path d="M9.35 14.53C7.728 12.246 6 10.221 6 7a6 5 0 0 1 12 0c-.005 3.22-1.778 5.235-3.43 7.5l3.557 4.527a1 1 0 0 1-.203 1.43l-1.894 1.36a1 1 0 0 1-1.384-.215L12 18l-2.679 3.593a1 1 0 0 1-1.39.213l-1.865-1.353a1 1 0 0 1-.203-1.422z"></path></g>',
  },
  'lucide-rocket': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path></g>',
  },
  'lucide-rocking-chair': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="3.5 2 6.5 12.5 18 12.5"></polyline><line x1="9.5" x2="5.5" y1="12.5" y2="20"></line><line x1="15" x2="18.5" y1="12.5" y2="20"></line><path d="M2.75 18a13 13 0 0 0 18.5 0"></path></g>',
  },
  'lucide-roller-coaster': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 19V5"></path><path d="M10 19V6.8"></path><path d="M14 19v-7.8"></path><path d="M18 5v4"></path><path d="M18 19v-6"></path><path d="M22 19V9"></path><path d="M2 19V9a4 4 0 0 1 4-4c2 0 4 1.33 6 4s4 4 6 4a4 4 0 1 0-3-6.65"></path></g>',
  },
  'lucide-rotate-3d': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.466 7.5C15.643 4.237 13.952 2 12 2 9.239 2 7 6.477 7 12s2.239 10 5 10c.342 0 .677-.069 1-.2"></path><path d="m15.194 13.707 3.814 1.86-1.86 3.814"></path><path d="M19 15.57c-1.804.885-4.274 1.43-7 1.43-5.523 0-10-2.239-10-5s4.477-5 10-5c4.838 0 8.873 1.718 9.8 4"></path></g>',
  },
  'lucide-rotate-ccw-key': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.5 9.5 1 1"></path><path d="m15.5 8.5-4 4"></path><path d="M3 12a9 9 0 1 0 9-9 9.74 9.74 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><circle cx="10" cy="14" r="2"></circle></g>',
  },
  'lucide-rotate-ccw-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 9V7a2 2 0 0 0-2-2h-6"></path><path d="m15 2-3 3 3 3"></path><path d="M20 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"></path></g>',
  },
  'lucide-rotate-ccw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path></g>',
  },
  'lucide-rotate-cw-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5H6a2 2 0 0 0-2 2v3"></path><path d="m9 8 3-3-3-3"></path><path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path></g>',
  },
  'lucide-rotate-cw': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path></g>',
  },
  'lucide-route-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="19" r="3"></circle><path d="M9 19h8.5c.4 0 .9-.1 1.3-.2"></path><path d="M5.2 5.2A3.5 3.53 0 0 0 6.5 12H12"></path><path d="m2 2 20 20"></path><path d="M21 15.3a3.5 3.5 0 0 0-3.3-3.3"></path><path d="M15 5h-4.3"></path><circle cx="18" cy="5" r="3"></circle></g>',
  },
  'lucide-route': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="19" r="3"></circle><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"></path><circle cx="18" cy="5" r="3"></circle></g>',
  },
  'lucide-router': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="8" x="2" y="14" rx="2"></rect><path d="M6.01 18H6"></path><path d="M10.01 18H10"></path><path d="M15 10v4"></path><path d="M17.84 7.17a4 4 0 0 0-5.66 0"></path><path d="M20.66 4.34a8 8 0 0 0-11.31 0"></path></g>',
  },
  'lucide-rows-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 12h18"></path></g>',
  },
  'lucide-rows-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M21 9H3"></path><path d="M21 15H3"></path></g>',
  },
  'lucide-rows-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M21 7.5H3"></path><path d="M21 12H3"></path><path d="M21 16.5H3"></path></g>',
  },
  'lucide-rss': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 11a9 9 0 0 1 9 9"></path><path d="M4 4a16 16 0 0 1 16 16"></path><circle cx="5" cy="19" r="1"></circle></g>',
  },
  'lucide-ruler-dimension-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15v-3.014"></path><path d="M16 15v-3.014"></path><path d="M20 6H4"></path><path d="M20 8V4"></path><path d="M4 8V4"></path><path d="M8 15v-3.014"></path><rect x="3" y="12" width="18" height="7" rx="1"></rect></g>',
  },
  'lucide-ruler': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"></path><path d="m14.5 12.5 2-2"></path><path d="m11.5 9.5 2-2"></path><path d="m8.5 6.5 2-2"></path><path d="m17.5 15.5 2-2"></path></g>',
  },
  'lucide-russian-ruble': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 11h8a4 4 0 0 0 0-8H9v18"></path><path d="M6 15h8"></path></g>',
  },
  'lucide-sailboat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 18H2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4Z"></path><path d="M21 14 10 2 3 14h18Z"></path><path d="M10 2v16"></path></g>',
  },
  'lucide-salad': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 21h10"></path><path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z"></path><path d="M11.38 12a2.4 2.4 0 0 1-.4-4.77 2.4 2.4 0 0 1 3.2-2.77 2.4 2.4 0 0 1 3.47-.63 2.4 2.4 0 0 1 3.37 3.37 2.4 2.4 0 0 1-1.1 3.7 2.51 2.51 0 0 1 .03 1.1"></path><path d="m13 12 4-4"></path><path d="M10.9 7.25A3.99 3.99 0 0 0 4 10c0 .73.2 1.41.54 2"></path></g>',
  },
  'lucide-sandwich': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2.37 11.223 8.372-6.777a2 2 0 0 1 2.516 0l8.371 6.777"></path><path d="M21 15a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-5.25"></path><path d="M3 15a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h9"></path><path d="m6.67 15 6.13 4.6a2 2 0 0 0 2.8-.4l3.15-4.2"></path><rect width="20" height="4" x="2" y="11" rx="1"></rect></g>',
  },
  'lucide-satellite-dish': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10a7.31 7.31 0 0 0 10 10Z"></path><path d="m9 15 3-3"></path><path d="M17 13a6 6 0 0 0-6-6"></path><path d="M21 13A10 10 0 0 0 11 3"></path></g>',
  },
  'lucide-satellite': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 7 9 3 5 7l4 4"></path><path d="m17 11 4 4-4 4-4-4"></path><path d="m8 12 4 4 6-6-4-4Z"></path><path d="m16 8 3-3"></path><path d="M9 21a6 6 0 0 0-6-6"></path></g>',
  },
  'lucide-saudi-riyal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m20 19.5-5.5 1.2"></path><path d="M14.5 4v11.22a1 1 0 0 0 1.242.97L20 15.2"></path><path d="m2.978 19.351 5.549-1.363A2 2 0 0 0 10 16V2"></path><path d="M20 10 4 13.5"></path></g>',
  },
  'lucide-save-all': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v3a1 1 0 0 0 1 1h5"></path><path d="M18 18v-6a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6"></path><path d="M18 22H4a2 2 0 0 1-2-2V6"></path><path d="M8 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9.172a2 2 0 0 1 1.414.586l2.828 2.828A2 2 0 0 1 22 6.828V16a2 2 0 0 1-2.01 2z"></path></g>',
  },
  'lucide-save-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 13H8a1 1 0 0 0-1 1v7"></path><path d="M14 8h1"></path><path d="M17 21v-4"></path><path d="m2 2 20 20"></path><path d="M20.41 20.41A2 2 0 0 1 19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 .59-1.41"></path><path d="M29.5 11.5s5 5 4 5"></path><path d="M9 3h6.2a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V15"></path></g>',
  },
  'lucide-save': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"></path><path d="M7 3v4a1 1 0 0 0 1 1h7"></path></g>',
  },
  'lucide-scale-3d': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 7v11a1 1 0 0 0 1 1h11"></path><path d="M5.293 18.707 11 13"></path><circle cx="19" cy="19" r="2"></circle><circle cx="5" cy="5" r="2"></circle></g>',
  },
  'lucide-scale': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"></path><path d="M7 21h10"></path><path d="M12 3v18"></path><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"></path></g>',
  },
  'lucide-scaling': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M14 15H9v-5"></path><path d="M16 3h5v5"></path><path d="M21 3 9 15"></path></g>',
  },
  'lucide-scan-barcode': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><path d="M8 7v10"></path><path d="M12 7v10"></path><path d="M17 7v10"></path></g>',
  },
  'lucide-scan-eye': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><circle cx="12" cy="12" r="1"></circle><path d="M18.944 12.33a1 1 0 0 0 0-.66 7.5 7.5 0 0 0-13.888 0 1 1 0 0 0 0 .66 7.5 7.5 0 0 0 13.888 0"></path></g>',
  },
  'lucide-scan-face': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><path d="M9 9h.01"></path><path d="M15 9h.01"></path></g>',
  },
  'lucide-scan-heart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.246 16.657a1 1 0 0 0 1.508 0l3.57-4.101A2.75 2.75 0 1 0 12 9.168a2.75 2.75 0 1 0-4.324 3.388z"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path></g>',
  },
  'lucide-scan-line': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><path d="M7 12h10"></path></g>',
  },
  'lucide-scan-qr-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 12v4a1 1 0 0 1-1 1h-4"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M17 8V7"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M7 17h.01"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><rect x="7" y="7" width="5" height="5" rx="1"></rect></g>',
  },
  'lucide-scan-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><circle cx="12" cy="12" r="3"></circle><path d="m16 16-1.9-1.9"></path></g>',
  },
  'lucide-scan-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path><path d="M7 8h8"></path><path d="M7 12h10"></path><path d="M7 16h6"></path></g>',
  },
  'lucide-scan': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path><path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path></g>',
  },
  'lucide-school': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 22v-4a2 2 0 1 0-4 0v4"></path><path d="m18 10 3.447 1.724a1 1 0 0 1 .553.894V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7.382a1 1 0 0 1 .553-.894L6 10"></path><path d="M18 5v17"></path><path d="m4 6 7.106-3.553a2 2 0 0 1 1.788 0L20 6"></path><path d="M6 5v17"></path><circle cx="12" cy="9" r="2"></circle></g>',
  },
  'lucide-scissors-line-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.42 9.42 8 12"></path><circle cx="4" cy="8" r="2"></circle><path d="m14 6-8.58 8.58"></path><circle cx="4" cy="16" r="2"></circle><path d="M10.8 14.8 14 18"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path></g>',
  },
  'lucide-scissors': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"></circle><path d="M8.12 8.12 12 12"></path><path d="M20 4 8.12 15.88"></path><circle cx="6" cy="18" r="3"></circle><path d="M14.8 14.8 20 20"></path></g>',
  },
  'lucide-screen-share-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3"></path><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="m22 3-5 5"></path><path d="m17 3 5 5"></path></g>',
  },
  'lucide-screen-share': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3"></path><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="m17 8 5-5"></path><path d="M17 3h5v5"></path></g>',
  },
  'lucide-scroll-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"></path><path d="M15 8h-5"></path><path d="M19 17V5a2 2 0 0 0-2-2H4"></path><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"></path></g>',
  },
  'lucide-scroll': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17V5a2 2 0 0 0-2-2H4"></path><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"></path></g>',
  },
  'lucide-search-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m8 11 2 2 4-4"></path><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></g>',
  },
  'lucide-search-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13 13.5 2-2.5-2-2.5"></path><path d="m21 21-4.3-4.3"></path><path d="M9 8.5 7 11l2 2.5"></path><circle cx="11" cy="11" r="8"></circle></g>',
  },
  'lucide-search-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13.5 8.5-5 5"></path><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></g>',
  },
  'lucide-search-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m13.5 8.5-5 5"></path><path d="m8.5 8.5 5 5"></path><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></g>',
  },
  'lucide-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></g>',
  },
  'lucide-section': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 5a4 3 0 0 0-8 0c0 4 8 3 8 7a4 3 0 0 1-8 0"></path><path d="M8 19a4 3 0 0 0 8 0c0-4-8-3-8-7a4 3 0 0 1 8 0"></path></g>',
  },
  'lucide-send-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"></path><path d="M6 12h16"></path></g>',
  },
  'lucide-send-to-back': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="14" y="14" width="8" height="8" rx="2"></rect><rect x="2" y="2" width="8" height="8" rx="2"></rect><path d="M7 14v1a2 2 0 0 0 2 2h1"></path><path d="M14 7h1a2 2 0 0 1 2 2v1"></path></g>',
  },
  'lucide-send': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path><path d="m21.854 2.147-10.94 10.939"></path></g>',
  },
  'lucide-separator-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="12" y2="12"></line><polyline points="8 8 12 4 16 8"></polyline><polyline points="16 16 12 20 8 16"></polyline></g>',
  },
  'lucide-separator-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="3" y2="21"></line><polyline points="8 8 4 12 8 16"></polyline><polyline points="16 16 20 12 16 8"></polyline></g>',
  },
  'lucide-server-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10.852 14.772-.383.923"></path><path d="M13.148 14.772a3 3 0 1 0-2.296-5.544l-.383-.923"></path><path d="m13.148 9.228.383-.923"></path><path d="m13.53 15.696-.382-.924a3 3 0 1 1-2.296-5.544"></path><path d="m14.772 10.852.923-.383"></path><path d="m14.772 13.148.923.383"></path><path d="M4.5 10H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-.5"></path><path d="M4.5 14H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-.5"></path><path d="M6 18h.01"></path><path d="M6 6h.01"></path><path d="m9.228 10.852-.923-.383"></path><path d="m9.228 13.148-.923.383"></path></g>',
  },
  'lucide-server-crash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 10H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"></path><path d="M6 14H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-2"></path><path d="M6 6h.01"></path><path d="M6 18h.01"></path><path d="m13 6-4 6h6l-4 6"></path></g>',
  },
  'lucide-server-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 2h13a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-5"></path><path d="M10 10 2.5 2.5C2 2 2 2.5 2 5v3a2 2 0 0 0 2 2h6z"></path><path d="M22 17v-1a2 2 0 0 0-2-2h-1"></path><path d="M4 14a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h16.5l1-.5.5.5-8-8H4z"></path><path d="M6 18h.01"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-server': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="8" x="2" y="2" rx="2" ry="2"></rect><rect width="20" height="8" x="2" y="14" rx="2" ry="2"></rect><line x1="6" x2="6.01" y1="6" y2="6"></line><line x1="6" x2="6.01" y1="18" y2="18"></line></g>',
  },
  'lucide-settings-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-9"></path><path d="M14 17H5"></path><circle cx="17" cy="17" r="3"></circle><circle cx="7" cy="7" r="3"></circle></g>',
  },
  'lucide-settings': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></g>',
  },
  'lucide-shapes': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z"></path><rect x="3" y="14" width="7" height="7" rx="1"></rect><circle cx="17.5" cy="17.5" r="3.5"></circle></g>',
  },
  'lucide-share-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"></line><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"></line></g>',
  },
  'lucide-share': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" x2="12" y1="2" y2="15"></line></g>',
  },
  'lucide-sheet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><line x1="3" x2="21" y1="9" y2="9"></line><line x1="3" x2="21" y1="15" y2="15"></line><line x1="9" x2="9" y1="9" y2="21"></line><line x1="15" x2="15" y1="9" y2="21"></line></g>',
  },
  'lucide-shell': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 11a2 2 0 1 1-4 0 4 4 0 0 1 8 0 6 6 0 0 1-12 0 8 8 0 0 1 16 0 10 10 0 1 1-20 0 11.93 11.93 0 0 1 2.42-7.22 2 2 0 1 1 3.16 2.44"></path></g>',
  },
  'lucide-shield-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M12 8v4"></path><path d="M12 16h.01"></path></g>',
  },
  'lucide-shield-ban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m4.243 5.21 14.39 12.472"></path></g>',
  },
  'lucide-shield-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m9 12 2 2 4-4"></path></g>',
  },
  'lucide-shield-ellipsis': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M8 12h.01"></path><path d="M12 12h.01"></path><path d="M16 12h.01"></path></g>',
  },
  'lucide-shield-half': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M12 22V2"></path></g>',
  },
  'lucide-shield-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M9 12h6"></path></g>',
  },
  'lucide-shield-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 2 20 20"></path><path d="M5 5a1 1 0 0 0-1 1v7c0 5 3.5 7.5 7.67 8.94a1 1 0 0 0 .67.01c2.35-.82 4.48-1.97 5.9-3.71"></path><path d="M9.309 3.652A12.252 12.252 0 0 0 11.24 2.28a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7a9.784 9.784 0 0 1-.08 1.264"></path></g>',
  },
  'lucide-shield-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M9 12h6"></path><path d="M12 9v6"></path></g>',
  },
  'lucide-shield-question': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></g>',
  },
  'lucide-shield-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="M6.376 18.91a6 6 0 0 1 11.249.003"></path><circle cx="12" cy="11" r="4"></circle></g>',
  },
  'lucide-shield-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path><path d="m14.5 9.5-5 5"></path><path d="m9.5 9.5 5 5"></path></g>',
  },
  'lucide-shield': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></g>',
  },
  'lucide-ship-wheel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8"></circle><path d="M12 2v7.5"></path><path d="m19 5-5.23 5.23"></path><path d="M22 12h-7.5"></path><path d="m19 19-5.23-5.23"></path><path d="M12 14.5V22"></path><path d="M10.23 13.77 5 19"></path><path d="M9.5 12H2"></path><path d="M10.23 10.23 5 5"></path><circle cx="12" cy="12" r="2.5"></circle></g>',
  },
  'lucide-ship': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 10.189V14"></path><path d="M12 2v3"></path><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"></path><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76"></path><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path></g>',
  },
  'lucide-shirt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"></path></g>',
  },
  'lucide-shopping-bag': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></g>',
  },
  'lucide-shopping-basket': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 11-1 9"></path><path d="m19 11-4-7"></path><path d="M2 11h20"></path><path d="m3.5 11 1.6 7.4a2 2 0 0 0 2 1.6h9.8a2 2 0 0 0 2-1.6l1.7-7.4"></path><path d="M4.5 15.5h15"></path><path d="m5 11 4-7"></path><path d="m9 11 1 9"></path></g>',
  },
  'lucide-shopping-cart': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path></g>',
  },
  'lucide-shovel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22v-5l5-5 5 5-5 5z"></path><path d="M9.5 14.5 16 8"></path><path d="m17 2 5 5-.5.5a3.53 3.53 0 0 1-5 0s0 0 0 0a3.53 3.53 0 0 1 0-5L17 2"></path></g>',
  },
  'lucide-shower-head': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4 4 2.5 2.5"></path><path d="M13.5 6.5a4.95 4.95 0 0 0-7 7"></path><path d="M15 5 5 15"></path><path d="M14 17v.01"></path><path d="M10 16v.01"></path><path d="M13 13v.01"></path><path d="M16 10v.01"></path><path d="M11 20v.01"></path><path d="M17 14v.01"></path><path d="M20 11v.01"></path></g>',
  },
  'lucide-shredder': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 22v-5"></path><path d="M14 19v-2"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M18 20v-3"></path><path d="M2 13h20"></path><path d="M20 13V7l-5-5H6a2 2 0 0 0-2 2v9"></path><path d="M6 20v-3"></path></g>',
  },
  'lucide-shrimp': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12h.01"></path><path d="M13 22c.5-.5 1.12-1 2.5-1-1.38 0-2-.5-2.5-1"></path><path d="M14 2a3.28 3.28 0 0 1-3.227 1.798l-6.17-.561A2.387 2.387 0 1 0 4.387 8H15.5a1 1 0 0 1 0 13 1 1 0 0 0 0-5H12a7 7 0 0 1-7-7V8"></path><path d="M14 8a8.5 8.5 0 0 1 0 8"></path><path d="M16 16c2 0 4.5-4 4-6"></path></g>',
  },
  'lucide-shrink': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 15 6 6m-6-6v4.8m0-4.8h4.8"></path><path d="M9 19.8V15m0 0H4.2M9 15l-6 6"></path><path d="M15 4.2V9m0 0h4.8M15 9l6-6"></path><path d="M9 4.2V9m0 0H4.2M9 9 3 3"></path></g>',
  },
  'lucide-shrub': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-7l-2-2"></path><path d="M17 8v.8A6 6 0 0 1 13.8 20H10A6.5 6.5 0 0 1 7 8a5 5 0 0 1 10 0Z"></path><path d="m14 14-2 2"></path></g>',
  },
  'lucide-shuffle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 14 4 4-4 4"></path><path d="m18 2 4 4-4 4"></path><path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"></path><path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"></path><path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"></path></g>',
  },
  'lucide-sigma': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 7V5a1 1 0 0 0-1-1H6.5a.5.5 0 0 0-.4.8l4.5 6a2 2 0 0 1 0 2.4l-4.5 6a.5.5 0 0 0 .4.8H17a1 1 0 0 0 1-1v-2"></path></g>',
  },
  'lucide-signal-high': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path><path d="M7 20v-4"></path><path d="M12 20v-8"></path><path d="M17 20V8"></path></g>',
  },
  'lucide-signal-low': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path><path d="M7 20v-4"></path></g>',
  },
  'lucide-signal-medium': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path><path d="M7 20v-4"></path><path d="M12 20v-8"></path></g>',
  },
  'lucide-signal-zero': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path></g>',
  },
  'lucide-signal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20h.01"></path><path d="M7 20v-4"></path><path d="M12 20v-8"></path><path d="M17 20V8"></path><path d="M22 4v16"></path></g>',
  },
  'lucide-signature': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21 17-2.156-1.868A.5.5 0 0 0 18 15.5v.5a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1c0-2.545-3.991-3.97-8.5-4a1 1 0 0 0 0 5c4.153 0 4.745-11.295 5.708-13.5a2.5 2.5 0 1 1 3.31 3.284"></path><path d="M3 21h18"></path></g>',
  },
  'lucide-signpost-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9H4L2 7l2-2h6"></path><path d="M14 5h6l2 2-2 2h-6"></path><path d="M10 22V4a2 2 0 1 1 4 0v18"></path><path d="M8 22h8"></path></g>',
  },
  'lucide-signpost': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 13v8"></path><path d="M12 3v3"></path><path d="M18 6a2 2 0 0 1 1.387.56l2.307 2.22a1 1 0 0 1 0 1.44l-2.307 2.22A2 2 0 0 1 18 13H6a2 2 0 0 1-1.387-.56l-2.306-2.22a1 1 0 0 1 0-1.44l2.306-2.22A2 2 0 0 1 6 6z"></path></g>',
  },
  'lucide-siren': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 18v-6a5 5 0 1 1 10 0v6"></path><path d="M5 21a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2z"></path><path d="M21 12h1"></path><path d="M18.5 4.5 18 5"></path><path d="M2 12h1"></path><path d="M12 2v1"></path><path d="m4.929 4.929.707.707"></path><path d="M12 12v6"></path></g>',
  },
  'lucide-skip-back': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="19 20 9 12 19 4 19 20"></polygon><line x1="5" x2="5" y1="19" y2="5"></line></g>',
  },
  'lucide-skip-forward': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 4 15 12 5 20 5 4"></polygon><line x1="19" x2="19" y1="5" y2="19"></line></g>',
  },
  'lucide-skull': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12.5 17-.5-1-.5 1h1z"></path><path d="M15 22a1 1 0 0 0 1-1v-1a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20v1a1 1 0 0 0 1 1z"></path><circle cx="15" cy="12" r="1"></circle><circle cx="9" cy="12" r="1"></circle></g>',
  },
  'lucide-slack': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="3" height="8" x="13" y="2" rx="1.5"></rect><path d="M19 8.5V10h1.5A1.5 1.5 0 1 0 19 8.5"></path><rect width="3" height="8" x="8" y="14" rx="1.5"></rect><path d="M5 15.5V14H3.5A1.5 1.5 0 1 0 5 15.5"></path><rect width="8" height="3" x="14" y="13" rx="1.5"></rect><path d="M15.5 19H14v1.5a1.5 1.5 0 1 0 1.5-1.5"></path><rect width="8" height="3" x="2" y="8" rx="1.5"></rect><path d="M8.5 5H10V3.5A1.5 1.5 0 1 0 8.5 5"></path></g>',
  },
  'lucide-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 2 22"></path></g>',
  },
  'lucide-slice': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 16.586V19a1 1 0 0 1-1 1H2L18.37 3.63a1 1 0 1 1 3 3l-9.663 9.663a1 1 0 0 1-1.414 0L8 14"></path></g>',
  },
  'lucide-sliders-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="14" y1="4" y2="4"></line><line x1="10" x2="3" y1="4" y2="4"></line><line x1="21" x2="12" y1="12" y2="12"></line><line x1="8" x2="3" y1="12" y2="12"></line><line x1="21" x2="16" y1="20" y2="20"></line><line x1="12" x2="3" y1="20" y2="20"></line><line x1="14" x2="14" y1="2" y2="6"></line><line x1="8" x2="8" y1="10" y2="14"></line><line x1="16" x2="16" y1="18" y2="22"></line></g>',
  },
  'lucide-sliders-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="4" y1="21" y2="14"></line><line x1="4" x2="4" y1="10" y2="3"></line><line x1="12" x2="12" y1="21" y2="12"></line><line x1="12" x2="12" y1="8" y2="3"></line><line x1="20" x2="20" y1="21" y2="16"></line><line x1="20" x2="20" y1="12" y2="3"></line><line x1="2" x2="6" y1="14" y2="14"></line><line x1="10" x2="14" y1="8" y2="8"></line><line x1="18" x2="22" y1="16" y2="16"></line></g>',
  },
  'lucide-smartphone-charging': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12.667 8 10 12h4l-2.667 4"></path></g>',
  },
  'lucide-smartphone-nfc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="12" x="2" y="6" rx="1"></rect><path d="M13 8.32a7.43 7.43 0 0 1 0 7.36"></path><path d="M16.46 6.21a11.76 11.76 0 0 1 0 11.58"></path><path d="M19.91 4.1a15.91 15.91 0 0 1 .01 15.8"></path></g>',
  },
  'lucide-smartphone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect><path d="M12 18h.01"></path></g>',
  },
  'lucide-smile-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11v1a10 10 0 1 1-9-10"></path><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line><path d="M16 5h6"></path><path d="M19 2v6"></path></g>',
  },
  'lucide-smile': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" x2="9.01" y1="9" y2="9"></line><line x1="15" x2="15.01" y1="9" y2="9"></line></g>',
  },
  'lucide-snail': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 13a6 6 0 1 0 12 0 4 4 0 1 0-8 0 2 2 0 0 0 4 0"></path><circle cx="10" cy="13" r="8"></circle><path d="M2 21h12c4.4 0 8-3.6 8-8V7a2 2 0 1 0-4 0v6"></path><path d="M18 3 19.1 5.2"></path><path d="M22 3 20.9 5.2"></path></g>',
  },
  'lucide-snowflake': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 20-1.25-2.5L6 18"></path><path d="M10 4 8.75 6.5 6 6"></path><path d="m14 20 1.25-2.5L18 18"></path><path d="m14 4 1.25 2.5L18 6"></path><path d="m17 21-3-6h-4"></path><path d="m17 3-3 6 1.5 3"></path><path d="M2 12h6.5L10 9"></path><path d="m20 10-1.5 2 1.5 2"></path><path d="M22 12h-6.5L14 15"></path><path d="m4 10 1.5 2L4 14"></path><path d="m7 21 3-6-1.5-3"></path><path d="m7 3 3 6h4"></path></g>',
  },
  'lucide-soap-dispenser-droplet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 2v4"></path><path d="M14 2H7a2 2 0 0 0-2 2"></path><path d="M19.29 14.76A6.67 6.67 0 0 1 17 11a6.6 6.6 0 0 1-2.29 3.76c-1.15.92-1.71 2.04-1.71 3.19 0 2.22 1.8 4.05 4 4.05s4-1.83 4-4.05c0-1.16-.57-2.26-1.71-3.19"></path><path d="M9.607 21H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h7V7a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3"></path></g>',
  },
  'lucide-sofa': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"></path><path d="M2 16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z"></path><path d="M4 18v2"></path><path d="M20 18v2"></path><path d="M12 4v9"></path></g>',
  },
  'lucide-soup': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z"></path><path d="M7 21h10"></path><path d="M19.5 12 22 6"></path><path d="M16.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.73 1.62"></path><path d="M11.25 3c.27.1.8.53.74 1.36-.05.83-.93 1.2-.98 2.02-.06.78.33 1.24.72 1.62"></path><path d="M6.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.74 1.62"></path></g>',
  },
  'lucide-space': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 17v1c0 .5-.5 1-1 1H3c-.5 0-1-.5-1-1v-1"></path></g>',
  },
  'lucide-spade': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9c-1.5 1.5-3 3.2-3 5.5A5.5 5.5 0 0 0 7.5 20c1.8 0 3-.5 4.5-2 1.5 1.5 2.7 2 4.5 2a5.5 5.5 0 0 0 5.5-5.5c0-2.3-1.5-4-3-5.5l-7-7-7 7Z"></path><path d="M12 18v4"></path></g>',
  },
  'lucide-sparkle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path></g>',
  },
  'lucide-sparkles': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></g>',
  },
  'lucide-speaker': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2"></rect><path d="M12 6h.01"></path><circle cx="12" cy="14" r="4"></circle><path d="M12 14h.01"></path></g>',
  },
  'lucide-speech': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8.8 20v-4.1l1.9.2a2.3 2.3 0 0 0 2.164-2.1V8.3A5.37 5.37 0 0 0 2 8.25c0 2.8.656 3.054 1 4.55a5.77 5.77 0 0 1 .029 2.758L2 20"></path><path d="M19.8 17.8a7.5 7.5 0 0 0 .003-10.603"></path><path d="M17 15a3.5 3.5 0 0 0-.025-4.975"></path></g>',
  },
  'lucide-spell-check-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 16 6-12 6 12"></path><path d="M8 12h8"></path><path d="M4 21c1.1 0 1.1-1 2.3-1s1.1 1 2.3 1c1.1 0 1.1-1 2.3-1 1.1 0 1.1 1 2.3 1 1.1 0 1.1-1 2.3-1 1.1 0 1.1 1 2.3 1 1.1 0 1.1-1 2.3-1"></path></g>',
  },
  'lucide-spell-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m6 16 6-12 6 12"></path><path d="M8 12h8"></path><path d="m16 20 2 2 4-4"></path></g>',
  },
  'lucide-spline-pointer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z"></path><path d="M5 17A12 12 0 0 1 17 5"></path><circle cx="19" cy="5" r="2"></circle><circle cx="5" cy="19" r="2"></circle></g>',
  },
  'lucide-spline': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="19" cy="5" r="2"></circle><circle cx="5" cy="19" r="2"></circle><path d="M5 17A12 12 0 0 1 17 5"></path></g>',
  },
  'lucide-split': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"></path><path d="M8 3H3v5"></path><path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"></path><path d="m15 9 6-6"></path></g>',
  },
  'lucide-spray-can': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h.01"></path><path d="M7 5h.01"></path><path d="M11 7h.01"></path><path d="M3 7h.01"></path><path d="M7 9h.01"></path><path d="M3 11h.01"></path><rect width="4" height="4" x="15" y="5"></rect><path d="m19 9 2 2v10c0 .6-.4 1-1 1h-6c-.6 0-1-.4-1-1V11l2-2"></path><path d="m13 14 8-2"></path><path d="m13 19 8-2"></path></g>',
  },
  'lucide-sprout': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 20h10"></path><path d="M10 20c5.5-2.5.8-6.4 3-10"></path><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z"></path><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z"></path></g>',
  },
  'lucide-square-activity': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M17 12h-2l-2 5-2-10-2 5H7"></path></g>',
  },
  'lucide-square-arrow-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m16 8-8 8"></path><path d="M16 16H8V8"></path></g>',
  },
  'lucide-square-arrow-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m8 8 8 8"></path><path d="M16 8v8H8"></path></g>',
  },
  'lucide-square-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M12 8v8"></path><path d="m8 12 4 4 4-4"></path></g>',
  },
  'lucide-square-arrow-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m12 8-4 4 4 4"></path><path d="M16 12H8"></path></g>',
  },
  'lucide-square-arrow-out-down-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 21h6a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6"></path><path d="m3 21 9-9"></path><path d="M9 21H3v-6"></path></g>',
  },
  'lucide-square-arrow-out-down-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"></path><path d="m21 21-9-9"></path><path d="M21 15v6h-6"></path></g>',
  },
  'lucide-square-arrow-out-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6"></path><path d="m3 3 9 9"></path><path d="M3 9V3h6"></path></g>',
  },
  'lucide-square-arrow-out-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"></path><path d="m21 3-9 9"></path><path d="M15 3h6v6"></path></g>',
  },
  'lucide-square-arrow-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 12h8"></path><path d="m12 16 4-4-4-4"></path></g>',
  },
  'lucide-square-arrow-up-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 16V8h8"></path><path d="M16 16 8 8"></path></g>',
  },
  'lucide-square-arrow-up-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 8h8v8"></path><path d="m8 16 8-8"></path></g>',
  },
  'lucide-square-arrow-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m16 12-4-4-4 4"></path><path d="M12 16V8"></path></g>',
  },
  'lucide-square-asterisk': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M12 8v8"></path><path d="m8.5 14 7-4"></path><path d="m8.5 10 7 4"></path></g>',
  },
  'lucide-square-bottom-dashed-scissors': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2"></path><path d="M10 22H8"></path><path d="M16 22h-2"></path><circle cx="8" cy="8" r="2"></circle><path d="M9.414 9.414 12 12"></path><path d="M14.8 14.8 18 18"></path><circle cx="8" cy="16" r="2"></circle><path d="m18 6-8.586 8.586"></path></g>',
  },
  'lucide-square-chart-gantt': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 8h7"></path><path d="M8 12h6"></path><path d="M11 16h5"></path></g>',
  },
  'lucide-square-check-big': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12.5"></path><path d="m9 11 3 3L22 4"></path></g>',
  },
  'lucide-square-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m9 12 2 2 4-4"></path></g>',
  },
  'lucide-square-chevron-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m16 10-4 4-4-4"></path></g>',
  },
  'lucide-square-chevron-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m14 16-4-4 4-4"></path></g>',
  },
  'lucide-square-chevron-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m10 8 4 4-4 4"></path></g>',
  },
  'lucide-square-chevron-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m8 14 4-4 4 4"></path></g>',
  },
  'lucide-square-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9.5 8 12l2 2.5"></path><path d="m14 9.5 2 2.5-2 2.5"></path><rect width="18" height="18" x="3" y="3" rx="2"></rect></g>',
  },
  'lucide-square-dashed-bottom-code': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 9.5 8 12l2 2.5"></path><path d="M14 21h1"></path><path d="m14 9.5 2 2.5-2 2.5"></path><path d="M5 21a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2"></path><path d="M9 21h1"></path></g>',
  },
  'lucide-square-dashed-bottom': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2"></path><path d="M9 21h1"></path><path d="M14 21h1"></path></g>',
  },
  'lucide-square-dashed-kanban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 7v7"></path><path d="M12 7v4"></path><path d="M16 7v9"></path><path d="M5 3a2 2 0 0 0-2 2"></path><path d="M9 3h1"></path><path d="M14 3h1"></path><path d="M19 3a2 2 0 0 1 2 2"></path><path d="M21 9v1"></path><path d="M21 14v1"></path><path d="M21 19a2 2 0 0 1-2 2"></path><path d="M14 21h1"></path><path d="M9 21h1"></path><path d="M5 21a2 2 0 0 1-2-2"></path><path d="M3 14v1"></path><path d="M3 9v1"></path></g>',
  },
  'lucide-square-dashed-mouse-pointer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z"></path><path d="M5 3a2 2 0 0 0-2 2"></path><path d="M19 3a2 2 0 0 1 2 2"></path><path d="M5 21a2 2 0 0 1-2-2"></path><path d="M9 3h1"></path><path d="M9 21h2"></path><path d="M14 3h1"></path><path d="M3 9v1"></path><path d="M21 9v2"></path><path d="M3 14v1"></path></g>',
  },
  'lucide-square-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3a2 2 0 0 0-2 2"></path><path d="M19 3a2 2 0 0 1 2 2"></path><path d="M21 19a2 2 0 0 1-2 2"></path><path d="M5 21a2 2 0 0 1-2-2"></path><path d="M9 3h1"></path><path d="M9 21h1"></path><path d="M14 3h1"></path><path d="M14 21h1"></path><path d="M3 9v1"></path><path d="M21 9v1"></path><path d="M3 14v1"></path><path d="M21 14v1"></path></g>',
  },
  'lucide-square-divide': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><line x1="8" x2="16" y1="12" y2="12"></line><line x1="12" x2="12" y1="16" y2="16"></line><line x1="12" x2="12" y1="8" y2="8"></line></g>',
  },
  'lucide-square-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><circle cx="12" cy="12" r="1"></circle></g>',
  },
  'lucide-square-equal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 10h10"></path><path d="M7 14h10"></path></g>',
  },
  'lucide-square-function': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="M9 17c2 0 2.8-1 2.8-2.8V10c0-2 1-3.3 3.2-3"></path><path d="M9 11.2h5.7"></path></g>',
  },
  'lucide-square-kanban': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 7v7"></path><path d="M12 7v4"></path><path d="M16 7v9"></path></g>',
  },
  'lucide-square-library': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 7v10"></path><path d="M11 7v10"></path><path d="m15 7 2 10"></path></g>',
  },
  'lucide-square-m': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 16V8l4 4 4-4v8"></path></g>',
  },
  'lucide-square-menu': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 8h10"></path><path d="M7 12h10"></path><path d="M7 16h10"></path></g>',
  },
  'lucide-square-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 12h8"></path></g>',
  },
  'lucide-square-mouse-pointer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z"></path><path d="M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6"></path></g>',
  },
  'lucide-square-parking-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.6 3.6A2 2 0 0 1 5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-.59 1.41"></path><path d="M3 8.7V19a2 2 0 0 0 2 2h10.3"></path><path d="m2 2 20 20"></path><path d="M13 13a3 3 0 1 0 0-6H9v2"></path><path d="M9 17v-2.3"></path></g>',
  },
  'lucide-square-parking': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M9 17V7h4a3 3 0 0 1 0 6H9"></path></g>',
  },
  'lucide-square-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path></g>',
  },
  'lucide-square-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m15 9-6 6"></path><path d="M9 9h.01"></path><path d="M15 15h.01"></path></g>',
  },
  'lucide-square-pi': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M7 7h10"></path><path d="M10 7v10"></path><path d="M16 17a2 2 0 0 1-2-2V7"></path></g>',
  },
  'lucide-square-pilcrow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M12 12H9.5a2.5 2.5 0 0 1 0-5H17"></path><path d="M12 7v10"></path><path d="M16 7v10"></path></g>',
  },
  'lucide-square-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="m9 8 6 4-6 4Z"></path></g>',
  },
  'lucide-square-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M8 12h8"></path><path d="M12 8v8"></path></g>',
  },
  'lucide-square-power': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v4"></path><path d="M7.998 9.003a5 5 0 1 0 8-.005"></path><rect x="3" y="3" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-square-radical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 12h2l2 5 2-10h4"></path><rect x="3" y="3" width="18" height="18" rx="2"></rect></g>',
  },
  'lucide-square-round-corner': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11a8 8 0 0 0-8-8"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path></g>',
  },
  'lucide-square-scissors': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="20" x="2" y="2" rx="2"></rect><circle cx="8" cy="8" r="2"></circle><path d="M9.414 9.414 12 12"></path><path d="M14.8 14.8 18 18"></path><circle cx="8" cy="16" r="2"></circle><path d="m18 6-8.586 8.586"></path></g>',
  },
  'lucide-square-sigma': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M16 8.9V7H8l4 5-4 5h8v-1.9"></path></g>',
  },
  'lucide-square-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><line x1="9" x2="15" y1="15" y2="9"></line></g>',
  },
  'lucide-square-split-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 19H5c-1 0-2-1-2-2V7c0-1 1-2 2-2h3"></path><path d="M16 5h3c1 0 2 1 2 2v10c0 1-1 2-2 2h-3"></path><line x1="12" x2="12" y1="4" y2="20"></line></g>',
  },
  'lucide-square-split-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8V5c0-1 1-2 2-2h10c1 0 2 1 2 2v3"></path><path d="M19 16v3c0 1-1 2-2 2H7c-1 0-2-1-2-2v-3"></path><line x1="4" x2="20" y1="12" y2="12"></line></g>',
  },
  'lucide-square-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><rect x="8" y="8" width="8" height="8" rx="1"></rect></g>',
  },
  'lucide-square-stack': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2"></path><path d="M10 16c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2"></path><rect width="8" height="8" x="14" y="14" rx="2"></rect></g>',
  },
  'lucide-square-terminal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m7 11 2-2-2-2"></path><path d="M11 13h4"></path><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect></g>',
  },
  'lucide-square-user-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 21a6 6 0 0 0-12 0"></path><circle cx="12" cy="11" r="4"></circle><rect width="18" height="18" x="3" y="3" rx="2"></rect></g>',
  },
  'lucide-square-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><circle cx="12" cy="10" r="3"></circle><path d="M7 21v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"></path></g>',
  },
  'lucide-square-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path></g>',
  },
  'lucide-square': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect></g>',
  },
  'lucide-squares-exclude': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12v2a2 2 0 0 1-2 2H9a1 1 0 0 0-1 1v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h0"></path><path d="M4 16a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3a1 1 0 0 1-1 1h-5a2 2 0 0 0-2 2v2"></path></g>',
  },
  'lucide-squares-intersect': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 22a2 2 0 0 1-2-2"></path><path d="M14 2a2 2 0 0 1 2 2"></path><path d="M16 22h-2"></path><path d="M2 10V8"></path><path d="M2 4a2 2 0 0 1 2-2"></path><path d="M20 8a2 2 0 0 1 2 2"></path><path d="M22 14v2"></path><path d="M22 20a2 2 0 0 1-2 2"></path><path d="M4 16a2 2 0 0 1-2-2"></path><path d="M8 10a2 2 0 0 1 2-2h5a1 1 0 0 1 1 1v5a2 2 0 0 1-2 2H9a1 1 0 0 1-1-1z"></path><path d="M8 2h2"></path></g>',
  },
  'lucide-squares-subtract': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 22a2 2 0 0 1-2-2"></path><path d="M16 22h-2"></path><path d="M16 4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-5a2 2 0 0 1 2-2h5a1 1 0 0 0 1-1z"></path><path d="M20 8a2 2 0 0 1 2 2"></path><path d="M22 14v2"></path><path d="M22 20a2 2 0 0 1-2 2"></path></g>',
  },
  'lucide-squares-unite': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3a1 1 0 0 0 1 1h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-3a1 1 0 0 0-1-1z"></path></g>',
  },
  'lucide-squircle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9-9 9-9-1.8-9-9 1.8-9 9-9"></path></g>',
  },
  'lucide-squirrel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.236 22a3 3 0 0 0-2.2-5"></path><path d="M16 20a3 3 0 0 1 3-3h1a2 2 0 0 0 2-2v-2a4 4 0 0 0-4-4V4"></path><path d="M18 13h.01"></path><path d="M18 6a4 4 0 0 0-4 4 7 7 0 0 0-7 7c0-5 4-5 4-10.5a4.5 4.5 0 1 0-9 0 2.5 2.5 0 0 0 5 0C7 10 3 11 3 17c0 2.8 2.2 5 5 5h10"></path></g>',
  },
  'lucide-stamp': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"></path><path d="M19.27 13.73A2.5 2.5 0 0 0 17.5 13h-11A2.5 2.5 0 0 0 4 15.5V17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1.5c0-.66-.26-1.3-.73-1.77Z"></path><path d="M14 13V8.5C14 7 15 7 15 5a3 3 0 0 0-3-3c-1.66 0-3 1-3 3s1 2 1 3.5V13"></path></g>',
  },
  'lucide-star-half': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 18.338a2.1 2.1 0 0 0-.987.244L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16l2.309-4.679A.53.53 0 0 1 12 2"></path></g>',
  },
  'lucide-star-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8.34 8.34 2 9.27l5 4.87L5.82 21 12 17.77 18.18 21l-.59-3.43"></path><path d="M18.42 12.76 22 9.27l-6.91-1L12 2l-1.44 2.91"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-star': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path></g>',
  },
  'lucide-step-back': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="18" y1="20" y2="4"></line><polygon points="14,20 4,12 14,4"></polygon></g>',
  },
  'lucide-step-forward': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="6" y1="4" y2="20"></line><polygon points="10,4 20,12 10,20"></polygon></g>',
  },
  'lucide-stethoscope': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 2v2"></path><path d="M5 2v2"></path><path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1"></path><path d="M8 15a6 6 0 0 0 12 0v-3"></path><circle cx="20" cy="10" r="2"></circle></g>',
  },
  'lucide-sticker': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15.5 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z"></path><path d="M14 3v4a2 2 0 0 0 2 2h4"></path><path d="M8 13h.01"></path><path d="M16 13h.01"></path><path d="M10 16s.8 1 2 1c1.3 0 2-1 2-1"></path></g>',
  },
  'lucide-sticky-note': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z"></path><path d="M15 3v4a2 2 0 0 0 2 2h4"></path></g>',
  },
  'lucide-store': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"></path><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"></path><path d="M2 7h20"></path><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"></path></g>',
  },
  'lucide-stretch-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="6" x="2" y="4" rx="2"></rect><rect width="20" height="6" x="2" y="14" rx="2"></rect></g>',
  },
  'lucide-stretch-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="6" height="20" x="4" y="2" rx="2"></rect><rect width="6" height="20" x="14" y="2" rx="2"></rect></g>',
  },
  'lucide-strikethrough': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4H9a3 3 0 0 0-2.83 4"></path><path d="M14 12a4 4 0 0 1 0 8H6"></path><line x1="4" x2="20" y1="12" y2="12"></line></g>',
  },
  'lucide-subscript': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4 5 8 8"></path><path d="m12 5-8 8"></path><path d="M20 19h-4c0-1.5.44-2 1.5-2.5S20 15.33 20 14c0-.47-.17-.93-.48-1.29a2.11 2.11 0 0 0-2.62-.44c-.42.24-.74.62-.9 1.07"></path></g>',
  },
  'lucide-sun-dim': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 4h.01"></path><path d="M20 12h.01"></path><path d="M12 20h.01"></path><path d="M4 12h.01"></path><path d="M17.657 6.343h.01"></path><path d="M17.657 17.657h.01"></path><path d="M6.343 17.657h.01"></path><path d="M6.343 6.343h.01"></path></g>',
  },
  'lucide-sun-medium': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 3v1"></path><path d="M12 20v1"></path><path d="M3 12h1"></path><path d="M20 12h1"></path><path d="m18.364 5.636-.707.707"></path><path d="m6.343 17.657-.707.707"></path><path d="m5.636 5.636.707.707"></path><path d="m17.657 17.657.707.707"></path></g>',
  },
  'lucide-sun-moon': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8a2.83 2.83 0 0 0 4 4 4 4 0 1 1-4-4"></path><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.9 4.9 1.4 1.4"></path><path d="m17.7 17.7 1.4 1.4"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.3 17.7-1.4 1.4"></path><path d="m19.1 4.9-1.4 1.4"></path></g>',
  },
  'lucide-sun-snow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 21v-1"></path><path d="M10 4V3"></path><path d="M10 9a3 3 0 0 0 0 6"></path><path d="m14 20 1.25-2.5L18 18"></path><path d="m14 4 1.25 2.5L18 6"></path><path d="m17 21-3-6 1.5-3H22"></path><path d="m17 3-3 6 1.5 3"></path><path d="M2 12h1"></path><path d="m20 10-1.5 2 1.5 2"></path><path d="m3.64 18.36.7-.7"></path><path d="m4.34 6.34-.7-.7"></path></g>',
  },
  'lucide-sun': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path></g>',
  },
  'lucide-sunrise': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v8"></path><path d="m4.93 10.93 1.41 1.41"></path><path d="M2 18h2"></path><path d="M20 18h2"></path><path d="m19.07 10.93-1.41 1.41"></path><path d="M22 22H2"></path><path d="m8 6 4-4 4 4"></path><path d="M16 18a4 4 0 0 0-8 0"></path></g>',
  },
  'lucide-sunset': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 10V2"></path><path d="m4.93 10.93 1.41 1.41"></path><path d="M2 18h2"></path><path d="M20 18h2"></path><path d="m19.07 10.93-1.41 1.41"></path><path d="M22 22H2"></path><path d="m16 6-4 4-4-4"></path><path d="M16 18a4 4 0 0 0-8 0"></path></g>',
  },
  'lucide-superscript': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4 19 8-8"></path><path d="m12 19-8-8"></path><path d="M20 12h-4c0-1.5.442-2 1.5-2.5S20 8.334 20 7.002c0-.472-.17-.93-.484-1.29a2.105 2.105 0 0 0-2.617-.436c-.42.239-.738.614-.899 1.06"></path></g>',
  },
  'lucide-swatch-book': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 17a4 4 0 0 1-8 0V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2Z"></path><path d="M16.7 13H19a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H7"></path><path d="M 7 17h.01"></path><path d="m11 8 2.3-2.3a2.4 2.4 0 0 1 3.404.004L18.6 7.6a2.4 2.4 0 0 1 .026 3.434L9.9 19.8"></path></g>',
  },
  'lucide-swiss-franc': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 21V3h8"></path><path d="M6 16h9"></path><path d="M10 9.5h7"></path></g>',
  },
  'lucide-switch-camera': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"></path><path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"></path><circle cx="12" cy="12" r="3"></circle><path d="m18 22-3-3 3-3"></path><path d="m6 2 3 3-3 3"></path></g>',
  },
  'lucide-sword': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"></polyline><line x1="13" x2="19" y1="19" y2="13"></line><line x1="16" x2="20" y1="16" y2="20"></line><line x1="19" x2="21" y1="21" y2="19"></line></g>',
  },
  'lucide-swords': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"></polyline><line x1="13" x2="19" y1="19" y2="13"></line><line x1="16" x2="20" y1="16" y2="20"></line><line x1="19" x2="21" y1="21" y2="19"></line><polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"></polyline><line x1="5" x2="9" y1="14" y2="18"></line><line x1="7" x2="4" y1="17" y2="20"></line><line x1="3" x2="5" y1="19" y2="21"></line></g>',
  },
  'lucide-syringe': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18 2 4 4"></path><path d="m17 7 3-3"></path><path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5"></path><path d="m9 11 4 4"></path><path d="m5 19-3 3"></path><path d="m14 4 6 6"></path></g>',
  },
  'lucide-table-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"></path></g>',
  },
  'lucide-table-cells-merge': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21v-6"></path><path d="M12 9V3"></path><path d="M3 15h18"></path><path d="M3 9h18"></path><rect width="18" height="18" x="3" y="3" rx="2"></rect></g>',
  },
  'lucide-table-cells-split': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V9"></path><path d="M3 15h18"></path><path d="M3 9h18"></path><rect width="18" height="18" x="3" y="3" rx="2"></rect></g>',
  },
  'lucide-table-columns-split': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 14v2"></path><path d="M14 20v2"></path><path d="M14 2v2"></path><path d="M14 8v2"></path><path d="M2 15h8"></path><path d="M2 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H2"></path><path d="M2 9h8"></path><path d="M22 15h-4"></path><path d="M22 3h-2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2"></path><path d="M22 9h-4"></path><path d="M5 3v18"></path></g>',
  },
  'lucide-table-of-contents': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12H3"></path><path d="M16 18H3"></path><path d="M16 6H3"></path><path d="M21 12h.01"></path><path d="M21 18h.01"></path><path d="M21 6h.01"></path></g>',
  },
  'lucide-table-properties': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3v18"></path><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M21 9H3"></path><path d="M21 15H3"></path></g>',
  },
  'lucide-table-rows-split': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 10h2"></path><path d="M15 22v-8"></path><path d="M15 2v4"></path><path d="M2 10h2"></path><path d="M20 10h2"></path><path d="M3 19h18"></path><path d="M3 22v-6a2 2 135 0 1 2-2h14a2 2 45 0 1 2 2v6"></path><path d="M3 2v2a2 2 45 0 0 2 2h14a2 2 135 0 0 2-2V2"></path><path d="M8 10h2"></path><path d="M9 22v-8"></path><path d="M9 2v4"></path></g>',
  },
  'lucide-table': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"></path><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9h18"></path><path d="M3 15h18"></path></g>',
  },
  'lucide-tablet-smartphone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="10" height="14" x="3" y="8" rx="2"></rect><path d="M5 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-2.4"></path><path d="M8 18h.01"></path></g>',
  },
  'lucide-tablet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"></rect><line x1="12" x2="12.01" y1="18" y2="18"></line></g>',
  },
  'lucide-tablets': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="7" r="5"></circle><circle cx="17" cy="17" r="5"></circle><path d="M12 17h10"></path><path d="m3.46 10.54 7.08-7.08"></path></g>',
  },
  'lucide-tag': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle></g>',
  },
  'lucide-tags': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19"></path><path d="M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z"></path><circle cx="6.5" cy="9.5" r=".5" fill="currentColor"></circle></g>',
  },
  'lucide-tally-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16"></path></g>',
  },
  'lucide-tally-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16"></path><path d="M9 4v16"></path></g>',
  },
  'lucide-tally-3': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16"></path><path d="M9 4v16"></path><path d="M14 4v16"></path></g>',
  },
  'lucide-tally-4': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16"></path><path d="M9 4v16"></path><path d="M14 4v16"></path><path d="M19 4v16"></path></g>',
  },
  'lucide-tally-5': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4v16"></path><path d="M9 4v16"></path><path d="M14 4v16"></path><path d="M19 4v16"></path><path d="M22 6 2 18"></path></g>',
  },
  'lucide-tangent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="17" cy="4" r="2"></circle><path d="M15.59 5.41 5.41 15.59"></path><circle cx="4" cy="17" r="2"></circle><path d="M12 22s-4-9-1.5-11.5S22 12 22 12"></path></g>',
  },
  'lucide-target': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-telescope': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10.065 12.493-6.18 1.318a.934.934 0 0 1-1.108-.702l-.537-2.15a1.07 1.07 0 0 1 .691-1.265l13.504-4.44"></path><path d="m13.56 11.747 4.332-.924"></path><path d="m16 21-3.105-6.21"></path><path d="M16.485 5.94a2 2 0 0 1 1.455-2.425l1.09-.272a1 1 0 0 1 1.212.727l1.515 6.06a1 1 0 0 1-.727 1.213l-1.09.272a2 2 0 0 1-2.425-1.455z"></path><path d="m6.158 8.633 1.114 4.456"></path><path d="m8 21 3.105-6.21"></path><circle cx="12" cy="13" r="2"></circle></g>',
  },
  'lucide-tent-tree': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="4" cy="4" r="2"></circle><path d="m14 5 3-3 3 3"></path><path d="m14 10 3-3 3 3"></path><path d="M17 14V2"></path><path d="M17 14H7l-5 8h20Z"></path><path d="M8 14v8"></path><path d="m9 14 5 8"></path></g>',
  },
  'lucide-tent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 21 14 3"></path><path d="M20.5 21 10 3"></path><path d="M15.5 21 12 15l-3.5 6"></path><path d="M2 21h20"></path></g>',
  },
  'lucide-terminal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" x2="20" y1="19" y2="19"></line></g>',
  },
  'lucide-test-tube-diagonal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7 6.82 21.18a2.83 2.83 0 0 1-3.99-.01a2.83 2.83 0 0 1 0-4L17 3"></path><path d="m16 2 6 6"></path><path d="M12 16H4"></path></g>',
  },
  'lucide-test-tube': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5c-1.4 0-2.5-1.1-2.5-2.5V2"></path><path d="M8.5 2h7"></path><path d="M14.5 16h-5"></path></g>',
  },
  'lucide-test-tubes': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 2v17.5A2.5 2.5 0 0 1 6.5 22A2.5 2.5 0 0 1 4 19.5V2"></path><path d="M20 2v17.5a2.5 2.5 0 0 1-2.5 2.5a2.5 2.5 0 0 1-2.5-2.5V2"></path><path d="M3 2h7"></path><path d="M14 2h7"></path><path d="M9 16H4"></path><path d="M20 16h-5"></path></g>',
  },
  'lucide-text-cursor-input': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h-1a2 2 0 0 1-2-2 2 2 0 0 1-2 2H6"></path><path d="M13 8h7a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-7"></path><path d="M5 16H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1"></path><path d="M6 4h1a2 2 0 0 1 2 2 2 2 0 0 1 2-2h1"></path><path d="M9 6v12"></path></g>',
  },
  'lucide-text-cursor': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 22h-1a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4h1"></path><path d="M7 22h1a4 4 0 0 0 4-4v-1"></path><path d="M7 2h1a4 4 0 0 1 4 4v1"></path></g>',
  },
  'lucide-text-quote': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 6H3"></path><path d="M21 12H8"></path><path d="M21 18H8"></path><path d="M3 12v6"></path></g>',
  },
  'lucide-text-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 6H3"></path><path d="M10 12H3"></path><path d="M10 18H3"></path><circle cx="17" cy="15" r="3"></circle><path d="m21 19-1.9-1.9"></path></g>',
  },
  'lucide-text-select': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 21h1"></path><path d="M14 3h1"></path><path d="M19 3a2 2 0 0 1 2 2"></path><path d="M21 14v1"></path><path d="M21 19a2 2 0 0 1-2 2"></path><path d="M21 9v1"></path><path d="M3 14v1"></path><path d="M3 9v1"></path><path d="M5 21a2 2 0 0 1-2-2"></path><path d="M5 3a2 2 0 0 0-2 2"></path><path d="M7 12h10"></path><path d="M7 16h6"></path><path d="M7 8h8"></path><path d="M9 21h1"></path><path d="M9 3h1"></path></g>',
  },
  'lucide-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18H3"></path><path d="M17 6H3"></path><path d="M21 12H3"></path></g>',
  },
  'lucide-theater': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 10s3-3 3-8"></path><path d="M22 10s-3-3-3-8"></path><path d="M10 2c0 4.4-3.6 8-8 8"></path><path d="M14 2c0 4.4 3.6 8 8 8"></path><path d="M2 10s2 2 2 5"></path><path d="M22 10s-2 2-2 5"></path><path d="M8 15h8"></path><path d="M2 22v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1"></path><path d="M14 22v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1"></path></g>',
  },
  'lucide-thermometer-snowflake': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 20-1.25-2.5L6 18"></path><path d="M10 4 8.75 6.5 6 6"></path><path d="M10.585 15H10"></path><path d="M2 12h6.5L10 9"></path><path d="M20 14.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0z"></path><path d="m4 10 1.5 2L4 14"></path><path d="m7 21 3-6-1.5-3"></path><path d="m7 3 3 6h2"></path></g>',
  },
  'lucide-thermometer-sun': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9a4 4 0 0 0-2 7.5"></path><path d="M12 3v2"></path><path d="m6.6 18.4-1.4 1.4"></path><path d="M20 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"></path><path d="M4 13H2"></path><path d="M6.34 7.34 4.93 5.93"></path></g>',
  },
  'lucide-thermometer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"></path></g>',
  },
  'lucide-thumbs-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 14V2"></path><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"></path></g>',
  },
  'lucide-thumbs-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path></g>',
  },
  'lucide-ticket-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="m9 12 2 2 4-4"></path></g>',
  },
  'lucide-ticket-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M9 12h6"></path></g>',
  },
  'lucide-ticket-percent': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M9 9h.01"></path><path d="m15 9-6 6"></path><path d="M15 15h.01"></path></g>',
  },
  'lucide-ticket-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M9 12h6"></path><path d="M12 9v6"></path></g>',
  },
  'lucide-ticket-slash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="m9.5 14.5 5-5"></path></g>',
  },
  'lucide-ticket-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="m9.5 14.5 5-5"></path><path d="m9.5 9.5 5 5"></path></g>',
  },
  'lucide-ticket': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 17v2"></path><path d="M13 11v2"></path></g>',
  },
  'lucide-tickets-plane': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 17h1.227a2 2 0 0 0 1.345-.52L18 12"></path><path d="m12 13.5 3.75.5"></path><path d="m4.5 8 10.58-5.06a1 1 0 0 1 1.342.488L18.5 8"></path><path d="M6 10V8"></path><path d="M6 14v1"></path><path d="M6 19v2"></path><rect x="2" y="8" width="20" height="13" rx="2"></rect></g>',
  },
  'lucide-tickets': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 8 10.58-5.06a1 1 0 0 1 1.342.488L18.5 8"></path><path d="M6 10V8"></path><path d="M6 14v1"></path><path d="M6 19v2"></path><rect x="2" y="8" width="20" height="13" rx="2"></rect></g>',
  },
  'lucide-timer-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2h4"></path><path d="M4.6 11a8 8 0 0 0 1.7 8.7 8 8 0 0 0 8.7 1.7"></path><path d="M7.4 7.4a8 8 0 0 1 10.3 1 8 8 0 0 1 .9 10.2"></path><path d="m2 2 20 20"></path><path d="M12 12v-2"></path></g>',
  },
  'lucide-timer-reset': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2h4"></path><path d="M12 14v-4"></path><path d="M4 13a8 8 0 0 1 8-7 8 8 0 1 1-5.3 14L4 17.6"></path><path d="M9 17H4v5"></path></g>',
  },
  'lucide-timer': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="10" x2="14" y1="2" y2="2"></line><line x1="12" x2="15" y1="14" y2="11"></line><circle cx="12" cy="14" r="8"></circle></g>',
  },
  'lucide-toggle-left': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="12" r="3"></circle><rect width="20" height="14" x="2" y="5" rx="7"></rect></g>',
  },
  'lucide-toggle-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="15" cy="12" r="3"></circle><rect width="20" height="14" x="2" y="5" rx="7"></rect></g>',
  },
  'lucide-toilet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 12h13a1 1 0 0 1 1 1 5 5 0 0 1-5 5h-.598a.5.5 0 0 0-.424.765l1.544 2.47a.5.5 0 0 1-.424.765H5.402a.5.5 0 0 1-.424-.765L7 18"></path><path d="M8 18a5 5 0 0 1-5-5V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8"></path></g>',
  },
  'lucide-tornado': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 4H3"></path><path d="M18 8H6"></path><path d="M19 12H9"></path><path d="M16 16h-6"></path><path d="M11 20H9"></path></g>',
  },
  'lucide-torus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="11" rx="3" ry="2"></ellipse><ellipse cx="12" cy="12.5" rx="10" ry="8.5"></ellipse></g>',
  },
  'lucide-touchpad-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20v-6"></path><path d="M19.656 14H22"></path><path d="M2 14h12"></path><path d="m2 2 20 20"></path><path d="M20 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2"></path><path d="M9.656 4H20a2 2 0 0 1 2 2v10.344"></path></g>',
  },
  'lucide-touchpad': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M2 14h20"></path><path d="M12 20v-6"></path></g>',
  },
  'lucide-tower-control': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18.2 12.27 20 6H4l1.8 6.27a1 1 0 0 0 .95.73h10.5a1 1 0 0 0 .96-.73Z"></path><path d="M8 13v9"></path><path d="M16 22v-9"></path><path d="m9 6 1 7"></path><path d="m15 6-1 7"></path><path d="M12 6V2"></path><path d="M13 2h-2"></path></g>',
  },
  'lucide-toy-brick': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="12" x="3" y="8" rx="1"></rect><path d="M10 8V5c0-.6-.4-1-1-1H6a1 1 0 0 0-1 1v3"></path><path d="M19 8V5c0-.6-.4-1-1-1h-3a1 1 0 0 0-1 1v3"></path></g>',
  },
  'lucide-tractor': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m10 11 11 .9a1 1 0 0 1 .8 1.1l-.665 4.158a1 1 0 0 1-.988.842H20"></path><path d="M16 18h-5"></path><path d="M18 5a1 1 0 0 0-1 1v5.573"></path><path d="M3 4h8.129a1 1 0 0 1 .99.863L13 11.246"></path><path d="M4 11V4"></path><path d="M7 15h.01"></path><path d="M8 10.1V4"></path><circle cx="18" cy="18" r="2"></circle><circle cx="7" cy="15" r="5"></circle></g>',
  },
  'lucide-traffic-cone': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.05 10.966a5 2.5 0 0 1-8.1 0"></path><path d="m16.923 14.049 4.48 2.04a1 1 0 0 1 .001 1.831l-8.574 3.9a2 2 0 0 1-1.66 0l-8.574-3.91a1 1 0 0 1 0-1.83l4.484-2.04"></path><path d="M16.949 14.14a5 2.5 0 1 1-9.9 0L10.063 3.5a2 2 0 0 1 3.874 0z"></path><path d="M9.194 6.57a5 2.5 0 0 0 5.61 0"></path></g>',
  },
  'lucide-train-front-tunnel': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22V12a10 10 0 1 1 20 0v10"></path><path d="M15 6.8v1.4a3 2.8 0 1 1-6 0V6.8"></path><path d="M10 15h.01"></path><path d="M14 15h.01"></path><path d="M10 19a4 4 0 0 1-4-4v-3a6 6 0 1 1 12 0v3a4 4 0 0 1-4 4Z"></path><path d="m9 19-2 3"></path><path d="m15 19 2 3"></path></g>',
  },
  'lucide-train-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3.1V7a4 4 0 0 0 8 0V3.1"></path><path d="m9 15-1-1"></path><path d="m15 15 1-1"></path><path d="M9 19c-2.8 0-5-2.2-5-5v-4a8 8 0 0 1 16 0v4c0 2.8-2.2 5-5 5Z"></path><path d="m8 19-2 3"></path><path d="m16 19 2 3"></path></g>',
  },
  'lucide-train-track': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 17 17 2"></path><path d="m2 14 8 8"></path><path d="m5 11 8 8"></path><path d="m8 8 8 8"></path><path d="m11 5 8 8"></path><path d="m14 2 8 8"></path><path d="M7 22 22 7"></path></g>',
  },
  'lucide-tram-front': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="16" x="4" y="3" rx="2"></rect><path d="M4 11h16"></path><path d="M12 3v8"></path><path d="m8 19-2 3"></path><path d="m18 22-2-3"></path><path d="M8 15h.01"></path><path d="M16 15h.01"></path></g>',
  },
  'lucide-transgender': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16v6"></path><path d="M14 20h-4"></path><path d="M18 2h4v4"></path><path d="m2 2 7.17 7.17"></path><path d="M2 5.355V2h3.357"></path><path d="m22 2-7.17 7.17"></path><path d="M8 5 5 8"></path><circle cx="12" cy="12" r="4"></circle></g>',
  },
  'lucide-trash-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></g>',
  },
  'lucide-trash': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></g>',
  },
  'lucide-tree-deciduous': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 19a4 4 0 0 1-2.24-7.32A3.5 3.5 0 0 1 9 6.03V6a3 3 0 1 1 6 0v.04a3.5 3.5 0 0 1 3.24 5.65A4 4 0 0 1 16 19Z"></path><path d="M12 19v3"></path></g>',
  },
  'lucide-tree-palm': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h2l1-1 1 1h4"></path><path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-3l-1-1-1 1h-3"></path><path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35"></path><path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14"></path></g>',
  },
  'lucide-tree-pine': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"></path><path d="M12 22v-3"></path></g>',
  },
  'lucide-trees': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z"></path><path d="M7 16v6"></path><path d="M13 19v3"></path><path d="M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5"></path></g>',
  },
  'lucide-trello': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect><rect width="3" height="9" x="7" y="7"></rect><rect width="3" height="5" x="14" y="7"></rect></g>',
  },
  'lucide-trending-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline><polyline points="16 17 22 17 22 11"></polyline></g>',
  },
  'lucide-trending-up-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.828 14.828 21 21"></path><path d="M21 16v5h-5"></path><path d="m21 3-9 9-4-4-6 6"></path><path d="M21 8V3h-5"></path></g>',
  },
  'lucide-trending-up': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></g>',
  },
  'lucide-triangle-alert': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></g>',
  },
  'lucide-triangle-dashed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.17 4.193a2 2 0 0 1 3.666.013"></path><path d="M14 21h2"></path><path d="m15.874 7.743 1 1.732"></path><path d="m18.849 12.952 1 1.732"></path><path d="M21.824 18.18a2 2 0 0 1-1.835 2.824"></path><path d="M4.024 21a2 2 0 0 1-1.839-2.839"></path><path d="m5.136 12.952-1 1.732"></path><path d="M8 21h2"></path><path d="m8.102 7.743-1 1.732"></path></g>',
  },
  'lucide-triangle-right': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 18a2 2 0 0 1-2 2H3c-1.1 0-1.3-.6-.4-1.3L20.4 4.3c.9-.7 1.6-.4 1.6.7Z"></path></g>',
  },
  'lucide-triangle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path></g>',
  },
  'lucide-trophy': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></g>',
  },
  'lucide-truck-electric': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 19V7a2 2 0 0 0-2-2H9"></path><path d="M15 19H9"></path><path d="M19 19h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62L18.3 9.38a1 1 0 0 0-.78-.38H14"></path><path d="M2 13v5a1 1 0 0 0 1 1h2"></path><path d="M4 3 2.15 5.15a.495.495 0 0 0 .35.86h2.15a.47.47 0 0 1 .35.86L3 9.02"></path><circle cx="17" cy="19" r="2"></circle><circle cx="7" cy="19" r="2"></circle></g>',
  },
  'lucide-truck': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path><path d="M15 18H9"></path><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path><circle cx="17" cy="18" r="2"></circle><circle cx="7" cy="18" r="2"></circle></g>',
  },
  'lucide-turtle': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m12 10 2 4v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3a8 8 0 1 0-16 0v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3l2-4h4Z"></path><path d="M4.82 7.9 8 10"></path><path d="M15.18 7.9 12 10"></path><path d="M16.93 10H20a2 2 0 0 1 0 4H2"></path></g>',
  },
  'lucide-tv-minimal-play': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 7.75a.75.75 0 0 1 1.142-.638l3.664 2.249a.75.75 0 0 1 0 1.278l-3.664 2.25a.75.75 0 0 1-1.142-.64z"></path><path d="M7 21h10"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect></g>',
  },
  'lucide-tv-minimal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 21h10"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect></g>',
  },
  'lucide-tv': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="15" x="2" y="7" rx="2" ry="2"></rect><polyline points="17 2 12 7 7 2"></polyline></g>',
  },
  'lucide-twitch': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2H3v16h5v4l4-4h5l4-4V2zm-10 9V7m5 4V7"></path></g>',
  },
  'lucide-twitter': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></g>',
  },
  'lucide-type-outline': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 16.5a.5.5 0 0 0 .5.5h.5a2 2 0 0 1 0 4H9a2 2 0 0 1 0-4h.5a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5V8a2 2 0 0 1-4 0V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-4 0v-.5a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5Z"></path></g>',
  },
  'lucide-type': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 7 4 4 20 4 20 7"></polyline><line x1="9" x2="15" y1="20" y2="20"></line><line x1="12" x2="12" y1="4" y2="20"></line></g>',
  },
  'lucide-umbrella-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v1"></path><path d="M15.5 21a1.85 1.85 0 0 1-3.5-1v-8H2a10 10 0 0 1 3.428-6.575"></path><path d="M17.5 12H22A10 10 0 0 0 9.004 3.455"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-umbrella': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12a10.06 10.06 1 0 0-20 0Z"></path><path d="M12 12v8a2 2 0 0 0 4 0"></path><path d="M12 2v1"></path></g>',
  },
  'lucide-underline': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4v6a6 6 0 0 0 12 0V4"></path><line x1="4" x2="20" y1="20" y2="20"></line></g>',
  },
  'lucide-undo-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14 4 9l5-5"></path><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"></path></g>',
  },
  'lucide-undo-dot': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 17a9 9 0 0 0-15-6.7L3 13"></path><path d="M3 7v6h6"></path><circle cx="12" cy="17" r="1"></circle></g>',
  },
  'lucide-undo': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"></path><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path></g>',
  },
  'lucide-unfold-horizontal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 12h6"></path><path d="M8 12H2"></path><path d="M12 2v2"></path><path d="M12 8v2"></path><path d="M12 14v2"></path><path d="M12 20v2"></path><path d="m19 15 3-3-3-3"></path><path d="m5 9-3 3 3 3"></path></g>',
  },
  'lucide-unfold-vertical': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-6"></path><path d="M12 8V2"></path><path d="M4 12H2"></path><path d="M10 12H8"></path><path d="M16 12h-2"></path><path d="M22 12h-2"></path><path d="m15 19-3 3-3-3"></path><path d="m15 5-3-3-3 3"></path></g>',
  },
  'lucide-ungroup': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="6" x="5" y="4" rx="1"></rect><rect width="8" height="6" x="11" y="14" rx="1"></rect></g>',
  },
  'lucide-university': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="10" r="1"></circle><path d="M22 20V8h-4l-6-4-6 4H2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2"></path><path d="M6 17v.01"></path><path d="M6 13v.01"></path><path d="M18 17v.01"></path><path d="M18 13v.01"></path><path d="M14 22v-5a2 2 0 0 0-2-2a2 2 0 0 0-2 2v5"></path></g>',
  },
  'lucide-unlink-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 7h2a5 5 0 0 1 0 10h-2m-6 0H7A5 5 0 0 1 7 7h2"></path></g>',
  },
  'lucide-unlink': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71"></path><path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71"></path><line x1="8" x2="8" y1="2" y2="5"></line><line x1="2" x2="5" y1="8" y2="8"></line><line x1="16" x2="16" y1="19" y2="22"></line><line x1="19" x2="22" y1="16" y2="16"></line></g>',
  },
  'lucide-unplug': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 5 3-3"></path><path d="m2 22 3-3"></path><path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z"></path><path d="M7.5 13.5 10 11"></path><path d="M10.5 16.5 13 14"></path><path d="m12 6 6 6 2.3-2.3a2.4 2.4 0 0 0 0-3.4l-2.6-2.6a2.4 2.4 0 0 0-3.4 0Z"></path></g>',
  },
  'lucide-upload': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" x2="12" y1="3" y2="15"></line></g>',
  },
  'lucide-usb': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="7" r="1"></circle><circle cx="4" cy="20" r="1"></circle><path d="M4.7 19.3 19 5"></path><path d="m21 3-3 1 2 2Z"></path><path d="M9.26 7.68 5 12l2 5"></path><path d="m10 14 5 2 3.5-3.5"></path><path d="m18 12 1-1 1 1-1 1Z"></path></g>',
  },
  'lucide-user-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></g>',
  },
  'lucide-user-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15H6a4 4 0 0 0-4 4v2"></path><path d="m14.305 16.53.923-.382"></path><path d="m15.228 13.852-.923-.383"></path><path d="m16.852 12.228-.383-.923"></path><path d="m16.852 17.772-.383.924"></path><path d="m19.148 12.228.383-.923"></path><path d="m19.53 18.696-.382-.924"></path><path d="m20.772 13.852.924-.383"></path><path d="m20.772 16.148.924.383"></path><circle cx="18" cy="15" r="3"></circle><circle cx="9" cy="7" r="4"></circle></g>',
  },
  'lucide-user-lock': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="7" r="4"></circle><path d="M10.3 15H7a4 4 0 0 0-4 4v2"></path><path d="M15 15.5V14a2 2 0 0 1 4 0v1.5"></path><rect width="8" height="5" x="13" y="16" rx=".899"></rect></g>',
  },
  'lucide-user-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="22" x2="16" y1="11" y2="11"></line></g>',
  },
  'lucide-user-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.5 15H7a4 4 0 0 0-4 4v2"></path><path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path><circle cx="10" cy="7" r="4"></circle></g>',
  },
  'lucide-user-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" x2="19" y1="8" y2="14"></line><line x1="22" x2="16" y1="11" y2="11"></line></g>',
  },
  'lucide-user-round-check': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21a8 8 0 0 1 13.292-6"></path><circle cx="10" cy="8" r="5"></circle><path d="m16 19 2 2 4-4"></path></g>',
  },
  'lucide-user-round-cog': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m14.305 19.53.923-.382"></path><path d="m15.228 16.852-.923-.383"></path><path d="m16.852 15.228-.383-.923"></path><path d="m16.852 20.772-.383.924"></path><path d="m19.148 15.228.383-.923"></path><path d="m19.53 21.696-.382-.924"></path><path d="M2 21a8 8 0 0 1 10.434-7.62"></path><path d="m20.772 16.852.924-.383"></path><path d="m20.772 19.148.924.383"></path><circle cx="10" cy="8" r="5"></circle><circle cx="18" cy="18" r="3"></circle></g>',
  },
  'lucide-user-round-minus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21a8 8 0 0 1 13.292-6"></path><circle cx="10" cy="8" r="5"></circle><path d="M22 19h-6"></path></g>',
  },
  'lucide-user-round-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21a8 8 0 0 1 10.821-7.487"></path><path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path><circle cx="10" cy="8" r="5"></circle></g>',
  },
  'lucide-user-round-plus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21a8 8 0 0 1 13.292-6"></path><circle cx="10" cy="8" r="5"></circle><path d="M19 16v6"></path><path d="M22 19h-6"></path></g>',
  },
  'lucide-user-round-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="8" r="5"></circle><path d="M2 21a8 8 0 0 1 10.434-7.62"></path><circle cx="18" cy="18" r="3"></circle><path d="m22 22-1.9-1.9"></path></g>',
  },
  'lucide-user-round-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 21a8 8 0 0 1 11.873-7"></path><circle cx="10" cy="8" r="5"></circle><path d="m17 17 5 5"></path><path d="m22 17-5 5"></path></g>',
  },
  'lucide-user-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"></circle><path d="M20 21a8 8 0 0 0-16 0"></path></g>',
  },
  'lucide-user-search': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="7" r="4"></circle><path d="M10.3 15H7a4 4 0 0 0-4 4v2"></path><circle cx="17" cy="17" r="3"></circle><path d="m21 21-1.9-1.9"></path></g>',
  },
  'lucide-user-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="17" x2="22" y1="8" y2="13"></line><line x1="22" x2="17" y1="8" y2="13"></line></g>',
  },
  'lucide-user': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></g>',
  },
  'lucide-users-round': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 21a8 8 0 0 0-16 0"></path><circle cx="10" cy="8" r="5"></circle><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"></path></g>',
  },
  'lucide-users': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><path d="M16 3.128a4 4 0 0 1 0 7.744"></path><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><circle cx="9" cy="7" r="4"></circle></g>',
  },
  'lucide-utensils-crossed': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8"></path><path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7"></path><path d="m2.1 21.8 6.4-6.3"></path><path d="m19 5-7 7"></path></g>',
  },
  'lucide-utensils': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"></path><path d="M7 2v20"></path><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"></path></g>',
  },
  'lucide-utility-pole': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20"></path><path d="M2 5h20"></path><path d="M3 3v2"></path><path d="M7 3v2"></path><path d="M17 3v2"></path><path d="M21 3v2"></path><path d="m19 5-7 7-7-7"></path></g>',
  },
  'lucide-variable': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 21s-4-3-4-9 4-9 4-9"></path><path d="M16 3s4 3 4 9-4 9-4 9"></path><line x1="15" x2="9" y1="9" y2="15"></line><line x1="9" x2="15" y1="9" y2="15"></line></g>',
  },
  'lucide-vault': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle><path d="m7.9 7.9 2.7 2.7"></path><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle><path d="m13.4 10.6 2.7-2.7"></path><circle cx="7.5" cy="16.5" r=".5" fill="currentColor"></circle><path d="m7.9 16.1 2.7-2.7"></path><circle cx="16.5" cy="16.5" r=".5" fill="currentColor"></circle><path d="m13.4 13.4 2.7 2.7"></path><circle cx="12" cy="12" r="2"></circle></g>',
  },
  'lucide-vegan': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8q6 0 6-6-6 0-6 6"></path><path d="M17.41 3.59a10 10 0 1 0 3 3"></path><path d="M2 2a26.6 26.6 0 0 1 10 20c.9-6.82 1.5-9.5 4-14"></path></g>',
  },
  'lucide-venetian-mask': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11c-1.5 0-2.5.5-3 2"></path><path d="M4 6a2 2 0 0 0-2 2v4a5 5 0 0 0 5 5 8 8 0 0 1 5 2 8 8 0 0 1 5-2 5 5 0 0 0 5-5V8a2 2 0 0 0-2-2h-3a8 8 0 0 0-5 2 8 8 0 0 0-5-2z"></path><path d="M6 11c1.5 0 2.5.5 3 2"></path></g>',
  },
  'lucide-venus-and-mars': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 20h4"></path><path d="M12 16v6"></path><path d="M17 2h4v4"></path><path d="m21 2-5.46 5.46"></path><circle cx="12" cy="11" r="5"></circle></g>',
  },
  'lucide-venus': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15v7"></path><path d="M9 19h6"></path><circle cx="12" cy="9" r="6"></circle></g>',
  },
  'lucide-vibrate-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 8 2 2-2 2 2 2-2 2"></path><path d="m22 8-2 2 2 2-2 2 2 2"></path><path d="M8 8v10c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2"></path><path d="M16 10.34V6c0-.55-.45-1-1-1h-4.34"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-vibrate': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 8 2 2-2 2 2 2-2 2"></path><path d="m22 8-2 2 2 2-2 2 2 2"></path><rect width="8" height="14" x="8" y="5" rx="1"></rect></g>',
  },
  'lucide-video-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196"></path><path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-video': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"></path><rect x="2" y="6" width="14" height="12" rx="2"></rect></g>',
  },
  'lucide-videotape': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="M2 8h20"></path><circle cx="8" cy="14" r="2"></circle><path d="M8 12h8"></path><circle cx="16" cy="14" r="2"></circle></g>',
  },
  'lucide-view': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 17v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2"></path><path d="M21 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2"></path><circle cx="12" cy="12" r="1"></circle><path d="M18.944 12.33a1 1 0 0 0 0-.66 7.5 7.5 0 0 0-13.888 0 1 1 0 0 0 0 .66 7.5 7.5 0 0 0 13.888 0"></path></g>',
  },
  'lucide-voicemail': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="12" r="4"></circle><circle cx="18" cy="12" r="4"></circle><line x1="6" x2="18" y1="16" y2="16"></line></g>',
  },
  'lucide-volleyball': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.1 7.1a16.55 16.55 0 0 1 10.9 4"></path><path d="M12 12a12.6 12.6 0 0 1-8.7 5"></path><path d="M16.8 13.6a16.55 16.55 0 0 1-9 7.5"></path><path d="M20.7 17a12.8 12.8 0 0 0-8.7-5 13.3 13.3 0 0 1 0-10"></path><path d="M6.3 3.8a16.55 16.55 0 0 0 1.9 11.5"></path><circle cx="12" cy="12" r="10"></circle></g>',
  },
  'lucide-volume-1': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"></path><path d="M16 9a5 5 0 0 1 0 6"></path></g>',
  },
  'lucide-volume-2': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"></path><path d="M16 9a5 5 0 0 1 0 6"></path><path d="M19.364 18.364a9 9 0 0 0 0-12.728"></path></g>',
  },
  'lucide-volume-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16 9a5 5 0 0 1 .95 2.293"></path><path d="M19.364 5.636a9 9 0 0 1 1.889 9.96"></path><path d="m2 2 20 20"></path><path d="m7 7-.587.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298V11"></path><path d="M9.828 4.172A.686.686 0 0 1 11 4.657v.686"></path></g>',
  },
  'lucide-volume-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"></path><line x1="22" x2="16" y1="9" y2="15"></line><line x1="16" x2="22" y1="9" y2="15"></line></g>',
  },
  'lucide-volume': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"></path></g>',
  },
  'lucide-vote': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 12 2 2 4-4"></path><path d="M5 7c0-1.1.9-2 2-2h10a2 2 0 0 1 2 2v12H5V7Z"></path><path d="M22 19H2"></path></g>',
  },
  'lucide-wallet-cards': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"></rect><path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2"></path><path d="M3 11h3c.8 0 1.6.3 2.1.9l1.1.9c1.6 1.6 4.1 1.6 5.7 0l1.1-.9c.5-.5 1.3-.9 2.1-.9H21"></path></g>',
  },
  'lucide-wallet-minimal': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 14h.01"></path><path d="M7 7h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14"></path></g>',
  },
  'lucide-wallet': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path></g>',
  },
  'lucide-wallpaper': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="9" r="2"></circle><path d="m9 17 6.1-6.1a2 2 0 0 1 2.81.01L22 15V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2"></path><path d="M8 21h8"></path><path d="M12 17v4"></path></g>',
  },
  'lucide-wand-sparkles': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"></path><path d="m14 7 3 3"></path><path d="M5 6v4"></path><path d="M19 14v4"></path><path d="M10 2v2"></path><path d="M7 8H3"></path><path d="M21 16h-4"></path><path d="M11 3H9"></path></g>',
  },
  'lucide-wand': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4V2"></path><path d="M15 16v-2"></path><path d="M8 9h2"></path><path d="M20 9h2"></path><path d="M17.8 11.8 19 13"></path><path d="M15 9h.01"></path><path d="M17.8 6.2 19 5"></path><path d="m3 21 9-9"></path><path d="M12.2 6.2 11 5"></path></g>',
  },
  'lucide-warehouse': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"></path><path d="M6 18h12"></path><path d="M6 14h12"></path><rect width="12" height="12" x="6" y="10"></rect></g>',
  },
  'lucide-washing-machine': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h3"></path><path d="M17 6h.01"></path><rect width="18" height="20" x="3" y="2" rx="2"></rect><circle cx="12" cy="13" r="5"></circle><path d="M12 18a2.5 2.5 0 0 0 0-5 2.5 2.5 0 0 1 0-5"></path></g>',
  },
  'lucide-watch': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="6"></circle><polyline points="12 10 12 12 13 13"></polyline><path d="m16.13 7.66-.81-4.05a2 2 0 0 0-2-1.61h-2.68a2 2 0 0 0-2 1.61l-.78 4.05"></path><path d="m7.88 16.36.8 4a2 2 0 0 0 2 1.61h2.72a2 2 0 0 0 2-1.61l.81-4.05"></path></g>',
  },
  'lucide-waves-ladder': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M19 5a2 2 0 0 0-2 2v11"></path><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M7 13h10"></path><path d="M7 9h10"></path><path d="M9 5a2 2 0 0 0-2 2v11"></path></g>',
  },
  'lucide-waves': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path></g>',
  },
  'lucide-waypoints': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4.5" r="2.5"></circle><path d="m10.2 6.3-3.9 3.9"></path><circle cx="4.5" cy="12" r="2.5"></circle><path d="M7 12h10"></path><circle cx="19.5" cy="12" r="2.5"></circle><path d="m13.8 17.7 3.9-3.9"></path><circle cx="12" cy="19.5" r="2.5"></circle></g>',
  },
  'lucide-webcam': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="10" r="8"></circle><circle cx="12" cy="10" r="3"></circle><path d="M7 22h10"></path><path d="M12 22v-4"></path></g>',
  },
  'lucide-webhook-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 17h-5c-1.09-.02-1.94.92-2.5 1.9A3 3 0 1 1 2.57 15"></path><path d="M9 3.4a4 4 0 0 1 6.52.66"></path><path d="m6 17 3.1-5.8a2.5 2.5 0 0 0 .057-2.05"></path><path d="M20.3 20.3a4 4 0 0 1-2.3.7"></path><path d="M18.6 13a4 4 0 0 1 3.357 3.414"></path><path d="m12 6 .6 1"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-webhook': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"></path><path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06"></path><path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"></path></g>',
  },
  'lucide-weight': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="3"></circle><path d="M6.5 8a2 2 0 0 0-1.905 1.46L2.1 18.5A2 2 0 0 0 4 21h16a2 2 0 0 0 1.925-2.54L19.4 9.5A2 2 0 0 0 17.48 8Z"></path></g>',
  },
  'lucide-wheat-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2 22 10-10"></path><path d="m16 8-1.17 1.17"></path><path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"></path><path d="m8 8-.53.53a3.5 3.5 0 0 0 0 4.94L9 15l1.53-1.53c.55-.55.88-1.25.98-1.97"></path><path d="M10.91 5.26c.15-.26.34-.51.56-.73L13 3l1.53 1.53a3.5 3.5 0 0 1 .28 4.62"></path><path d="M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z"></path><path d="M11.47 17.47 13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"></path><path d="m16 16-.53.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.49 3.49 0 0 1 1.97-.98"></path><path d="M18.74 13.09c.26-.15.51-.34.73-.56L21 11l-1.53-1.53a3.5 3.5 0 0 0-4.62-.28"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-wheat': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 22 16 8"></path><path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"></path><path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"></path><path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z"></path><path d="M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z"></path><path d="M11.47 17.47 13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"></path><path d="M15.47 13.47 17 15l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"></path><path d="M19.47 9.47 21 11l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L13 11l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z"></path></g>',
  },
  'lucide-whole-word': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="12" r="3"></circle><path d="M10 9v6"></path><circle cx="17" cy="12" r="3"></circle><path d="M14 7v8"></path><path d="M22 17v1c0 .5-.5 1-1 1H3c-.5 0-1-.5-1-1v-1"></path></g>',
  },
  'lucide-wifi-high': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"></path><path d="M5 12.859a10 10 0 0 1 14 0"></path><path d="M8.5 16.429a5 5 0 0 1 7 0"></path></g>',
  },
  'lucide-wifi-low': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"></path><path d="M8.5 16.429a5 5 0 0 1 7 0"></path></g>',
  },
  'lucide-wifi-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"></path><path d="M8.5 16.429a5 5 0 0 1 7 0"></path><path d="M5 12.859a10 10 0 0 1 5.17-2.69"></path><path d="M19 12.859a10 10 0 0 0-2.007-1.523"></path><path d="M2 8.82a15 15 0 0 1 4.177-2.643"></path><path d="M22 8.82a15 15 0 0 0-11.288-3.764"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-wifi-pen': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8.82a15 15 0 0 1 20 0"></path><path d="M21.378 16.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path><path d="M5 12.859a10 10 0 0 1 10.5-2.222"></path><path d="M8.5 16.429a5 5 0 0 1 3-1.406"></path></g>',
  },
  'lucide-wifi-zero': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"></path></g>',
  },
  'lucide-wifi': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"></path><path d="M2 8.82a15 15 0 0 1 20 0"></path><path d="M5 12.859a10 10 0 0 1 14 0"></path><path d="M8.5 16.429a5 5 0 0 1 7 0"></path></g>',
  },
  'lucide-wind-arrow-down': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2v8"></path><path d="M12.8 21.6A2 2 0 1 0 14 18H2"></path><path d="M17.5 10a2.5 2.5 0 1 1 2 4H2"></path><path d="m6 6 4 4 4-4"></path></g>',
  },
  'lucide-wind': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12.8 19.6A2 2 0 1 0 14 16H2"></path><path d="M17.5 8a2.5 2.5 0 1 1 2 4H2"></path><path d="M9.8 4.4A2 2 0 1 1 11 8H2"></path></g>',
  },
  'lucide-wine-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 22h8"></path><path d="M7 10h3m7 0h-1.343"></path><path d="M12 15v7"></path><path d="M7.307 7.307A12.33 12.33 0 0 0 7 10a5 5 0 0 0 7.391 4.391M8.638 2.981C8.75 2.668 8.872 2.34 9 2h6c1.5 4 2 6 2 8 0 .407-.05.809-.145 1.198"></path><line x1="2" x2="22" y1="2" y2="22"></line></g>',
  },
  'lucide-wine': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 22h8"></path><path d="M7 10h10"></path><path d="M12 15v7"></path><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z"></path></g>',
  },
  'lucide-workflow': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="8" x="3" y="3" rx="2"></rect><path d="M7 11v4a2 2 0 0 0 2 2h4"></path><rect width="8" height="8" x="13" y="13" rx="2"></rect></g>',
  },
  'lucide-worm': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m19 12-1.5 3"></path><path d="M19.63 18.81 22 20"></path><path d="M6.47 8.23a1.68 1.68 0 0 1 2.44 1.93l-.64 2.08a6.76 6.76 0 0 0 10.16 7.67l.42-.27a1 1 0 1 0-2.73-4.21l-.42.27a1.76 1.76 0 0 1-2.63-1.99l.64-2.08A6.66 6.66 0 0 0 3.94 3.9l-.7.4a1 1 0 1 0 2.55 4.34z"></path></g>',
  },
  'lucide-wrap-text': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="6" y2="6"></line><path d="M3 12h15a3 3 0 1 1 0 6h-4"></path><polyline points="16 16 14 18 16 20"></polyline><line x1="3" x2="10" y1="18" y2="18"></line></g>',
  },
  'lucide-wrench': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></g>',
  },
  'lucide-x': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></g>',
  },
  'lucide-youtube': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"></path><path d="m10 15 5-3-5-3z"></path></g>',
  },
  'lucide-zap-off': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.513 4.856 13.12 2.17a.5.5 0 0 1 .86.46l-1.377 4.317"></path><path d="M15.656 10H20a1 1 0 0 1 .78 1.63l-1.72 1.773"></path><path d="M16.273 16.273 10.88 21.83a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14H4a1 1 0 0 1-.78-1.63l4.507-4.643"></path><path d="m2 2 20 20"></path></g>',
  },
  'lucide-zap': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path></g>',
  },
  'lucide-zoom-in': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" x2="16.65" y1="21" y2="16.65"></line><line x1="11" x2="11" y1="8" y2="14"></line><line x1="8" x2="14" y1="11" y2="11"></line></g>',
  },
  'lucide-zoom-out': {
    viewBox: '0 0 24 24',
    fill: 'none',
    content:
      '<g stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" x2="16.65" y1="21" y2="16.65"></line><line x1="8" x2="14" y1="11" y2="11"></line></g>',
  },
} satisfies Record<string, XuwoIconData>;
